import React, { useState, useEffect } from 'react';
import CustomIntelligencePanel from './CustomIntelligencePanel';
import IntelligenceChart from './IntelligenceChart';
import ViewConfiguration from './ViewConfiguration';
import LLMTop10Panel from './LLMTop10';
import CdgSqlPanel from './CdgSqlPanel';
import BatchStatusPanel from './BatchStatusPanel';
import DatabaseMetadataBanner from './DatabaseMetadataBanner';
import DemoDataWarning from './DemoDataWarning';
import InsightsPanel, { hasStructuredInsights } from './InsightsPanel';
import AnalyticsPanel from './AnalyticsPanel';
import AnalyticsFieldset from './AnalyticsFieldset';
import UI, { getProductTypeLabel } from '../config/uiLabels';
import { getDbStatus, resolveDbConfig } from '../config/productDatabaseMap';
import { getQuerySource, getSourceTooltip, getSourceColor } from '../config/sqlIntelligenceSources';
import useConcurrentData from '../hooks/useConcurrentData';
import useLLMPreferences from '../hooks/useLLMPreferences';

/**
 * ClientApplication — The live client application experience.
 *
 * RAG AI (CEO Directives) runs silently as guardrails — never shown to users.
 * Only LLM (Operations BI) and Comm-AI (MCP/A2A) are visible.
 *
 * LLM operations menu is built dynamically from the client's product_name,
 * matching the use cases defined in LLM config. Each query returns realistic
 * business data from the client's operational database.
 */
const ClientApplication = ({ clientConfig, onBack, activeTab: externalTab, setActiveTab: externalSetActiveTab }) => {
  // Use external tab state from sidebar if provided, otherwise internal
  const [internalTab, setInternalTab] = useState('overview');
  const activeTab = externalTab || internalTab;
  const setActiveTab = externalSetActiveTab || setInternalTab;
  const [genaiQuery, setGenaiQuery] = useState('');
  const [genaiResult, setGenaiResult] = useState(null);
  const [agenticAction, setAgenticAction] = useState('');

  // Daily & Analytics group state (user-configurable button labels)
  // Product-type-aware defaults — no restaurant BUC for non-restaurant products
  const _pt = (clientConfig?.product_type || '').toLowerCase();
  const _dailyDefaults = _pt.includes('realestate') || _pt.includes('realty')
    ? { l1: 'Active Listings', q1: 'Show all active listings with prices and status', l2: 'New Offers', q2: 'What offers were received this week?', l3: 'Showings', q3: 'What showings are scheduled today?' }
    : _pt.includes('procurement') || _pt.includes('procureguard')
    ? { l1: 'Active Contracts', q1: 'Show all active contracts by department', l2: 'Pending Tenders', q2: 'What tenders are currently open for bidding?', l3: 'Compliance', q3: 'Show compliance check status for this month' }
    : _pt.includes('bank') || _pt.includes('financial')
    ? { l1: 'Balance Sheet', q1: 'Show balance sheet summary', l2: 'Loan Portfolio', q2: 'What is our current loan-to-deposit ratio?', l3: 'Risk Metrics', q3: 'Show risk-weighted assets by category' }
    : _pt.includes('gym') || _pt.includes('fitness')
    ? { l1: 'Memberships', q1: 'Show active membership count and revenue', l2: 'Class Schedule', q2: 'What classes are scheduled today?', l3: 'Renewals', q3: 'Which memberships are expiring this month?' }
    : _pt.includes('grocery') || _pt.includes('supermarket')
    ? { l1: 'Daily Sales', q1: 'What are today\'s total sales across all departments?', l2: 'Low Stock', q2: 'Which products are below reorder level?', l3: 'Top Products', q3: 'Show top 10 selling products this week' }
    : _pt.includes('it-consult')
    ? { l1: 'Utilization', q1: 'What is our current resource utilization rate by department?', l2: 'Active Projects', q2: 'Show all active projects with status and budget utilization', l3: 'Bench', q3: 'How many consultants are currently on bench?' }
    : _pt.includes('healthcare') || _pt.includes('clinical')
    ? { l1: 'Wait Times', q1: 'What is our average patient wait time this week?', l2: 'Same-Day Access', q2: 'What percentage of appointments are same-day access?', l3: 'Claims', q3: 'Show claim denial rate this month' }
    : _pt.includes('insurance')
    ? { l1: 'Premium Revenue', q1: 'Total premium revenue across all offices this quarter?', l2: 'Claims', q2: 'What is our loss ratio by office?', l3: 'Renewals', q3: 'Which office has the highest policy renewal rate?' }
    : _pt.includes('hvac')
    ? { l1: 'Service Calls', q1: 'What is our average service call revenue this month?', l2: 'Parts', q2: 'How many parts are below reorder point?', l3: 'Contracts', q3: 'How many active maintenance contracts do we have?' }
    : _pt.includes('plumbing')
    ? { l1: 'Network Revenue', q1: 'Total network revenue this month?', l2: 'Job Volume', q2: 'Franchise vs retail subscriber job volume?', l3: 'Ratings', q3: 'Location with best customer rating?' }
    : _pt.includes('medical') || _pt.includes('wellness')
    ? { l1: 'Patient Retention', q1: 'What is our patient retention rate by service?', l2: 'Room Utilization', q2: 'What is our treatment room utilization this week?', l3: 'Revenue', q3: 'What is our revenue by treatment type this month?' }
    : _pt.includes('logistics')
    ? { l1: 'Route Efficiency', q1: 'What is our route fuel efficiency by corridor?', l2: 'Delivery SLA', q2: 'What is our delivery SLA performance this month?', l3: 'Drivers', q3: 'Show driver performance metrics and safety scores' }
    : _pt.includes('lawn')
    ? { l1: 'Revenue', q1: 'What is our revenue breakdown by service type?', l2: 'Crew Schedule', q2: 'Which crew has the most jobs scheduled this week?', l3: 'Equipment', q3: 'Which equipment needs maintenance?' }
    : _pt.includes('valet') || _pt.includes('auto')
    ? { l1: 'Shift Optimization', q1: 'What is our valet shift optimization this week?', l2: 'EV Charging', q2: 'What is our EV charging station utilization and revenue?', l3: 'Retrieval', q3: 'What is our vehicle retrieval time performance?' }
    : _pt.includes('industrialwater') || _pt.includes('industrial-water') || _pt.includes('pfas')
    ? { l1: 'PFAS Compliance', q1: 'What are our current PFAS detection levels vs. EPA MCL limits?', l2: 'Membrane Health', q2: 'What is the current membrane performance and replacement status?', l3: 'SCADA Alerts', q3: 'Show any SCADA sensor anomalies or threshold breaches today' }
    : _pt.includes('biopharma') || _pt.includes('bio-pharma')
    ? { l1: 'Pipeline Status', q1: 'What is the current drug pipeline stage and regulatory milestone status?', l2: 'Safety Signals', q2: 'Are there any open adverse event reports requiring escalation?', l3: 'Compliance', q3: 'Show manufacturing compliance gaps for upcoming FDA review' }
    : _pt.includes('china') || _pt.includes('geopulse') || _pt.includes('strategic-intel')
    ? { l1: 'Event Severity', q1: 'What are the highest severity geopolitical events in the last 7 days?', l2: 'Corporate Risk', q2: 'Which S&P 500 companies have the highest China revenue exposure?', l3: 'Tariff Impact', q3: 'What tariff deadlines are approaching in the next 30 days?' }
    : _pt.includes('restaurant') || _pt.includes('hospitality')
    ? { l1: 'Reservations', q1: 'What are today\'s reservations and table availability?', l2: 'Today\'s Sales', q2: 'What were today\'s total sales so far?', l3: 'Staff Schedule', q3: 'Who is scheduled to work today?' }
    : { l1: 'Key Metrics', q1: 'What are our key performance metrics today?', l2: 'Revenue', q2: 'What is our revenue this month?', l3: 'Operations', q3: 'Show operational status summary' };

  // Auto-invalidate stale localStorage when product-type defaults change (version key)
  const _dailyVersion = `v4_${_pt.split('.')[0] || 'generic'}`;
  const _storedVersion = localStorage.getItem(`pnp_daily_ver_${clientConfig?.client_id}`);
  if (_storedVersion !== _dailyVersion) {
    // Clear stale Daily/Analytics button labels for this client
    ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_daily_${k}_${clientConfig?.client_id}`));
    ['lbl', 'q', 'lbl2', 'q2', 'lbl3', 'q3'].forEach(k => localStorage.removeItem(`pnp_analytics_${k}_${clientConfig?.client_id}`));
    localStorage.setItem(`pnp_daily_ver_${clientConfig?.client_id}`, _dailyVersion);
  }

  // Button 1 = user's primary choice, Button 2 = auto-filled from top of remaining menu
  const [dailyBtn1Label, setDailyBtn1Label] = useState(() => localStorage.getItem(`pnp_daily_lbl_${clientConfig?.client_id}`) || _dailyDefaults.l1);
  const [dailyBtn1Query, setDailyBtn1Query] = useState(() => localStorage.getItem(`pnp_daily_q_${clientConfig?.client_id}`) || _dailyDefaults.q1);
  const [dailyBtn2Label, setDailyBtn2Label] = useState(() => localStorage.getItem(`pnp_daily_lbl2_${clientConfig?.client_id}`) || _dailyDefaults.l2);
  const [dailyBtn2Query, setDailyBtn2Query] = useState(() => localStorage.getItem(`pnp_daily_q2_${clientConfig?.client_id}`) || _dailyDefaults.q2);
  const [dailyBtn3Label, setDailyBtn3Label] = useState(() => localStorage.getItem(`pnp_daily_lbl3_${clientConfig?.client_id}`) || _dailyDefaults.l3);
  const [dailyBtn3Query, setDailyBtn3Query] = useState(() => localStorage.getItem(`pnp_daily_q3_${clientConfig?.client_id}`) || _dailyDefaults.q3);
  const [showDailyMenu, setShowDailyMenu] = useState(false);
  // Configurable button counts (2 or 3) — persisted per client
  const [dailyBtnCount, setDailyBtnCount] = useState(() => {
    const saved = localStorage.getItem(`pnp_daily_btncount_${clientConfig?.client_id}`);
    return saved ? parseInt(saved, 10) : 3;
  });
  const [showDailyConfig, setShowDailyConfig] = useState(false);
  // User-added custom items for Daily/Analytics (persisted in localStorage)
  const [customDailyItems, setCustomDailyItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`pnp_daily_custom_${clientConfig?.client_id}`) || '[]'); } catch { return []; }
  });
  const [customAnalyticsItems, setCustomAnalyticsItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem(`pnp_analytics_custom_${clientConfig?.client_id}`) || '[]'); } catch { return []; }
  });
  // Analytics button state (legacy hardcoded path — used when USE_DYNAMIC_ANALYTICS=false)
  const [analyticsBtnLabel, setAnalyticsBtnLabel] = useState(() => localStorage.getItem(`pnp_analytics_lbl_${clientConfig?.client_id}`) || '');
  const [analyticsBtnQuery, setAnalyticsBtnQuery] = useState(() => localStorage.getItem(`pnp_analytics_q_${clientConfig?.client_id}`) || '');
  const [analyticsBtn2Label, setAnalyticsBtn2Label] = useState(() => localStorage.getItem(`pnp_analytics_lbl2_${clientConfig?.client_id}`) || '');
  const [analyticsBtn2Query, setAnalyticsBtn2Query] = useState(() => localStorage.getItem(`pnp_analytics_q2_${clientConfig?.client_id}`) || '');
  const [analyticsBtn3Label, setAnalyticsBtn3Label] = useState(() => localStorage.getItem(`pnp_analytics_lbl3_${clientConfig?.client_id}`) || '');
  const [analyticsBtn3Query, setAnalyticsBtn3Query] = useState(() => localStorage.getItem(`pnp_analytics_q3_${clientConfig?.client_id}`) || '');
  const [analyticsBtnCount, setAnalyticsBtnCount] = useState(() => {
    const saved = localStorage.getItem(`pnp_analytics_btncount_${clientConfig?.client_id}`);
    return saved ? parseInt(saved, 10) : 3;
  });
  const [showAnalyticsMenu, setShowAnalyticsMenu] = useState(false);
  const [showAnalyticsConfig, setShowAnalyticsConfig] = useState(false);
  const [agenticResult, setAgenticResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const clientId = clientConfig?.client_id || 'unknown';
  const companyName = clientConfig?.company_name || 'Client';
  const product = (clientConfig?.product_name || clientConfig?.company_name || '').toLowerCase();
  const tenantType = (clientConfig?.tenant_type || 'standalone').toLowerCase();

  // Concurrent data engine — SWR caching + background revalidation
  const dataEngine = useConcurrentData(clientId);

  // Refinement & error state
  const [showRefinement, setShowRefinement] = useState(false);
  const [refinementInput, setRefinementInput] = useState('');
  const [systemError, setSystemError] = useState(null);
  const [cdgSqlState, setCdgSqlState] = useState(null);

  // License banner state
  const [licenseStatus, setLicenseStatus] = useState(null);
  const [showLicenseBanner, setShowLicenseBanner] = useState(false); // Hidden by default — only shown when backend confirms payment issue

  // Determine if this client has a real database connected (prod vs demo)
  // Uses centralized product-type-to-database mapping
  // Resolve db_config from product type map if not explicitly provided
  const resolvedDbConfig = clientConfig?.db_config || resolveDbConfig(null, clientConfig?.product_type);
  const dbStatus = getDbStatus(resolvedDbConfig, clientConfig?.product_type);
  const hasDb = dbStatus.label !== 'demo';
  const dotColor = dbStatus.color;
  const dotLabel = dbStatus.label;

  // Custom Intelligence state
  const llmPrefs = useLLMPreferences(clientId);
  const [customQuestions, setCustomQuestionsLocal] = useState([]);
  const [sharedQuestions, setSharedQuestions] = useState([]);
  // LLM refresh key — increments on provider change to force re-render
  const [llmRefreshKey, setLlmRefreshKey] = useState(0);

  // Sync custom questions with persistence
  const setCustomQuestions = (questions) => {
    setCustomQuestionsLocal(questions);
    llmPrefs.setCustomQuestions(questions);
  };

  // ── License Banner Logic ──
  // Rules:
  //   1. Backend returns show_banner: false → hide banner completely
  //   2. Backend returns show_banner: true with banner_severity → show appropriate banner
  //   3. ACTIVE status → no banner (subscription is current)
  //   4. PAST_DUE (1-10 days overdue) → yellow warning with due date
  //   5. OVERDUE (11-13 days overdue) → red alert
  //   6. SUSPENDED (14+ days overdue) → locked mode, red banner
  const getLicenseInfo = () => {
    if (licenseStatus && licenseStatus.status) {
      // Only show banner if backend explicitly says to
      if (licenseStatus.show_banner === true) {
        return licenseStatus;
      }
      // ACTIVE or any status with show_banner=false → no banner
      return null;
    }

    // No backend response yet — no banner (don't guess)
    return null;
  };

  // Fetch license from backend on mount
  // eslint-disable-next-line
  React.useEffect(() => {
    const fetchLicense = async () => {
      try {
        const res = await fetch(`/v1/clients/${clientId}/license`);
        if (res.ok) {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await res.json();
            if (data.status && data.status !== 'unknown') {
              setLicenseStatus(data);
              // Only show banner if backend explicitly says to
              setShowLicenseBanner(data.show_banner === true);
            }
          }
        }
      } catch { /* use computed fallback — banner stays hidden */ }
    };
    fetchLicense();
  }, [clientId]);

  // Sync dataEngine result → genaiResult for rendering
  // eslint-disable-next-line
  React.useEffect(() => {
    if (dataEngine.result) setGenaiResult(dataEngine.result);
  }, [dataEngine.result]);

  // ── 3-Tier Intelligence Response for waste/food cost queries ──
  const getWasteIntelligenceResponse = (query, company) => {
    const distilledSql = "SELECT ii.item_name, bi.quantity_on_hand, ii.reorder_level\nFROM inventory_items ii\nJOIN branch_inventory bi ON bi.inventory_item_id = ii.inventory_item_id\nWHERE bi.quantity_on_hand < ii.reorder_level\nORDER BY bi.quantity_on_hand;";
    const erSql1 = "SELECT ii.item_name, ii.category,\n  SUM(it.quantity) AS total_waste_qty,\n  SUM(it.quantity * ii.unit_cost) AS waste_cost\nFROM inventory_transactions it\nJOIN inventory_items ii ON ii.inventory_item_id = it.inventory_item_id\nWHERE it.transaction_type = 'wastage'\nGROUP BY ii.item_name, ii.category\nORDER BY waste_cost DESC;";
    const erSql2 = "SELECT b.branch_name,\n  SUM(it.quantity) AS waste_qty,\n  SUM(it.quantity * ii.unit_cost) AS waste_cost\nFROM inventory_transactions it\nJOIN inventory_items ii ON ii.inventory_item_id = it.inventory_item_id\nJOIN branches b ON b.branch_id = it.branch_id\nWHERE it.transaction_type = 'wastage'\nGROUP BY b.branch_name\nORDER BY waste_cost DESC;";
    const erSql3 = "SELECT mi.item_name, mi.price,\n  SUM(ri.quantity_needed * ii.unit_cost) AS ingredient_cost,\n  mi.price - SUM(ri.quantity_needed * ii.unit_cost) AS margin\nFROM menu_items mi\nJOIN recipes r ON r.menu_item_id = mi.item_id\nJOIN recipe_ingredients ri ON ri.recipe_id = r.recipe_id\nJOIN inventory_items ii ON ii.inventory_item_id = ri.inventory_item_id\nGROUP BY mi.item_id, mi.item_name, mi.price\nORDER BY margin DESC;";
    return {
      answer: [
        `Query: "${query}"`,
        '',
        `🔍 3-Tier Intelligence Resolution for ${company}:`,
        '',
        '⚡ Distilled SQL Intelligence',
        '  Low Stock Items:',
        `  ${distilledSql}`,
        '',
        '🧠 ER Intelligence',
        '  1. Food Waste by Item (from inventory_transactions):',
        `  ${erSql1}`,
        '',
        '  2. Waste by Prep Station / Branch:',
        `  ${erSql2}`,
        '',
        '  3. Menu Item Profitability (recipe cost vs price):',
        `  ${erSql3}`,
        '',
        '🤖 LLM',
        '  ⚠️ LLM suggested: SELECT prep_station, SUM(waste_cost) FROM food_waste_data',
        '  — but table food_waste_data does NOT exist in this schema.',
        '  Use ER Intelligence queries above instead.',
        '',
        '💡 Copy any SQL above and paste into the query box with [Instruction: SQL: ...] to execute.',
      ].join('\n'),
      tables_queried: ['inventory_transactions', 'inventory_items', 'branches', 'recipes', 'recipe_ingredients'],
      rows_scanned: 342,
      sql: erSql1,
      recommended_sql: [
        { label: '⚡ Distilled: Low Stock Items', sql: distilledSql },
        { label: '🧠 ER: Food Waste by Item', sql: erSql1 },
        { label: '🧠 ER: Waste by Prep Station', sql: erSql2 },
        { label: '🧠 ER: Menu Profitability', sql: erSql3 },
      ],
    };
  };

  // ── LLM Demo Responses (from Onboarding Test Scenarios) ──
  const getDemoResponse = (query) => {
    const q = query.toLowerCase();
    // Restaurant / Hospitality
    if (q.includes('revenue') && q.includes('order')) return { answer: 'Revenue Breakdown by Order Type (March 2026):\n\n• Dine-in: $42,800 (38%) — 1,240 orders\n• Takeaway: $31,200 (28%) — 980 orders\n• Delivery: $22,400 (20%) — 720 orders\n• Catering: $15,600 (14%) — 48 events\n\nTotal Revenue: $112,000\nAvg ticket: $38.40 (dine-in), $31.80 (takeaway), $31.10 (delivery)', tables_queried: ['orders', 'order_items', 'payments'], rows_scanned: 2988, sql: "SELECT order_type,\n  COUNT(*) AS order_count,\n  SUM(total_amount) AS revenue\nFROM orders\nJOIN order_items USING (order_id)\nJOIN payments USING (order_id)\nWHERE order_date >= '2026-03-01'\nGROUP BY order_type\nORDER BY revenue DESC;" };
    if (q.includes('inventory') && (q.includes('below') || q.includes('reorder'))) return { answer: 'Inventory Items Below Reorder Point:\n\n🔴 Critical (qty < 25% of reorder):\n• Chicken Breast — 12 lbs (reorder: 50 lbs)\n• Basmati Rice — 8 lbs (reorder: 40 lbs)\n• Cooking Oil — 2 gal (reorder: 10 gal)\n\n🟡 Warning (qty < 50% of reorder):\n• Tomatoes — 15 lbs (reorder: 35 lbs)\n• Onions — 18 lbs (reorder: 40 lbs)\n• Napkins — 200 (reorder: 500)\n• To-go Containers — 80 (reorder: 200)\n\n7 items need restocking. Estimated cost: $847', tables_queried: ['inventory_items', 'branch_inventory'], rows_scanned: 156 };
    if (q.includes('inventory') && (q.includes('detail') || q.includes('profit') || q.includes('value') || q.includes('status') || q.includes('stock') || q.includes('level'))) return { answer: 'Inventory Details — Profitability Analysis:\n\n📊 Total Inventory Value: $24,680 (across 156 items)\n\n💰 Top 5 by Value:\n• Premium Olive Oil — 24 bottles × $18.50 = $444 (margin: 62%)\n• Aged Parmesan — 15 lbs × $28.00 = $420 (margin: 58%)\n• Wagyu Beef — 8 lbs × $45.00 = $360 (margin: 45%)\n• Saffron — 200g × $1.20/g = $240 (margin: 72%)\n• Truffle Oil — 12 bottles × $16.00 = $192 (margin: 68%)\n\n⚠️ Low Margin Items (< 20%):\n• House Wine — margin: 18%\n• Bottled Water — margin: 15%\n• Paper Goods — margin: 12%\n\n📈 Recommendations:\n• Increase order frequency for high-margin perishables\n• Negotiate bulk pricing on low-margin staples\n• Review par levels for slow-moving items', tables_queried: ['inventory_items', 'branch_inventory', 'recipes'], rows_scanned: 156, sql: "SELECT ii.item_name, ii.unit, ii.unit_cost, bi.quantity_on_hand, bi.reorder_level,\n  ROUND((bi.quantity_on_hand * ii.unit_cost), 2) AS inventory_value\nFROM inventory_items ii\nJOIN branch_inventory bi ON bi.inventory_item_id = ii.inventory_item_id\nORDER BY inventory_value DESC\nLIMIT 100;" };
    if (q.includes('order') && (q.includes('volume') || q.includes('channel'))) return { answer: 'Order Volume by Channel (This Week):\n\n📱 Mobile App: 312 orders (34%) — avg $33.20\n🌐 Website: 198 orders (22%) — avg $41.50\n🚶 Walk-in: 245 orders (27%) — avg $38.80\n📞 Phone: 89 orders (10%) — avg $29.40\n🛵 Third-party (DoorDash/UberEats): 68 orders (7%) — avg $27.60\n\nTotal: 912 orders this week\nPeak hours: 11:30am-1:30pm, 5:30pm-8:00pm', tables_queried: ['orders', 'order_items'], rows_scanned: 912 };
    if (q.includes('staff') && (q.includes('rating') || q.includes('performance'))) return { answer: 'Staff Performance — Customer Ratings (March 2026):\n\n⭐ Top Performers:\n1. Maria Santos — 4.9/5.0 (142 reviews) — Server\n2. James Park — 4.8/5.0 (128 reviews) — Server\n3. Chef Ravi — 4.8/5.0 (89 reviews) — Kitchen\n4. Lisa Chen — 4.7/5.0 (96 reviews) — Host\n5. Tom Wilson — 4.6/5.0 (74 reviews) — Server\n\n📊 Team Average: 4.5/5.0\nTotal reviews this month: 847\nResponse rate: 23% of customers leave reviews', tables_queried: ['staff', 'reviews', 'orders'], rows_scanned: 847 };
    if (q.includes('reservation')) return { answer: 'Reservations — Today:\n\n📅 Total: 34 reservations\n• Lunch (11am-2pm): 12 reservations, 38 guests\n• Dinner (5pm-9pm): 22 reservations, 72 guests\n\n🪑 Table utilization: 78% (lunch), 94% (dinner)\n⚠️ 2 large parties (8+ guests) at 7:00pm and 7:30pm\n\nNo-show rate this month: 6.2%\nWaitlist: 4 parties currently waiting (avg 18 min)', tables_queried: ['reservations', 'dining_tables'], rows_scanned: 34 };
    if (q.includes('waste') || q.includes('spoilage') || (q.includes('food') && q.includes('cost') && q.includes('prep'))) return getWasteIntelligenceResponse(query, companyName);
    if (q.includes('food cost') || (q.includes('cost') && q.includes('percentage') && q.includes('category'))) return getWasteIntelligenceResponse(query, companyName);
    // Lawn & Landscape
    if (q.includes('revenue') && q.includes('service')) return { answer: 'Revenue by Service Type (March 2026):\n\n🌿 Lawn Mowing: $28,400 (52%) — 186 jobs\n🌳 Landscaping: $12,600 (23%) — 34 projects\n🍂 Seasonal Cleanup: $8,200 (15%) — 62 jobs\n🧪 Fertilization: $4,800 (9%) — 48 treatments\n\nTotal: $54,000\nAvg job value: $163.60\nRepeat customer rate: 87%', tables_queried: ['service_jobs', 'invoices'], rows_scanned: 330 };
    if (q.includes('crew') && (q.includes('schedule') || q.includes('most jobs'))) return { answer: 'Crew Schedule — This Week:\n\n👥 Crew B (Lead: Marcus) — 24 jobs ⭐ Busiest\n👥 Crew A (Lead: Carlos) — 21 jobs\n👥 Crew C (Lead: Devon) — 18 jobs\n👥 Crew D (Lead: Sarah) — 15 jobs\n\nTotal: 78 jobs scheduled Mon-Fri\nEquipment assigned: 4 mowers, 4 trimmers, 2 blowers per crew\nZone coverage: North Charlotte (A), South (B), East (C), West (D)', tables_queried: ['service_jobs', 'crews'], rows_scanned: 78 };
    if (q.includes('active customer') || q.includes('customer base')) return { answer: 'Active Customer Base (March 2026):\n\n🏠 Residential: 142 accounts\n🏢 Commercial: 18 accounts\n\nTotal: 160 active customers\n\nNew this month: 8 residential, 2 commercial\nChurn: 3 (1.9% monthly churn rate)\nAvg customer lifetime: 2.4 years\nTop zip codes: 28277 (42), 28270 (28), 28226 (22)', tables_queried: ['customers', 'service_jobs'], rows_scanned: 160 };
    if (q.includes('equipment') && q.includes('maintenance')) return { answer: 'Equipment Maintenance Status:\n\n🔴 Overdue:\n• Mower #M-07 — Last service: 62 days ago (max: 30 days)\n• Trimmer #T-03 — Last service: 45 days ago (max: 30 days)\n\n🟡 Due Soon (next 7 days):\n• Mower #M-02 — Due in 3 days\n• Blower #B-01 — Due in 5 days\n\n✅ Current: 12 of 16 units\n\nTotal fleet: 8 mowers, 4 trimmers, 4 blowers\nMaintenance budget used: $2,400 of $4,000 (60%)', tables_queried: ['equipment'], rows_scanned: 16 };
    if (q.includes('seasonal') && q.includes('forecast')) return { answer: 'Seasonal Demand Forecast — Q2 2026:\n\n📈 April: $62,000 (+15% vs March) — Spring cleanup surge\n📈 May: $71,000 (+31%) — Peak mowing + landscaping season\n📈 June: $68,000 (+26%) — Sustained high demand\n\nRecommendations:\n• Hire 2 seasonal crew members by April 1\n• Pre-order fertilizer (bulk discount deadline: March 28)\n• Schedule equipment maintenance before April rush', tables_queried: ['service_jobs', 'seasonal_plans'], rows_scanned: 480 };
    // HVAC
    if (q.includes('average') && q.includes('service call')) return { answer: 'Average Service Call Revenue (March 2026):\n\n💰 Overall Average: $387/call\n\nBy Type:\n• Repair: $520/call (142 calls)\n• Maintenance: $180/call (98 calls)\n• Installation: $1,240/call (24 calls)\n• Emergency: $680/call (31 calls)\n\nTotal revenue: $114,210\nTotal calls: 295\nAvg calls/day: 9.5', tables_queried: ['service_calls', 'invoices'], rows_scanned: 295 };
    if (q.includes('technician') && q.includes('rating')) return { answer: 'Technician Performance — Customer Ratings:\n\n⭐ Rankings:\n1. Mike Torres — 4.9/5.0 (67 calls this quarter)\n2. Sarah Kim — 4.8/5.0 (54 calls)\n3. James Lee — 4.7/5.0 (61 calls)\n4. David Chen — 4.6/5.0 (48 calls)\n5. Rachel Green — 4.5/5.0 (42 calls)\n\nTeam average: 4.7/5.0\nCertifications: All EPA 608 certified\nAvg response time: 2.4 hours', tables_queried: ['technicians', 'service_calls', 'reviews'], rows_scanned: 272 };
    if (q.includes('parts') && q.includes('reorder')) return { answer: 'Parts Below Reorder Point:\n\n🔴 12 parts need restocking:\n• Compressors (4): Carrier 24ACC636 — qty: 1, reorder: 3\n• Capacitors (3): 45/5 MFD — qty: 2, reorder: 8\n• Contactors (2): Honeywell DP1030A — qty: 0, reorder: 4\n• Filter Sets (3): 20x25x4 MERV-11 — qty: 5, reorder: 20\n\nEstimated restock cost: $4,280\nSupplier: HVAC Parts Direct (2-day delivery)', tables_queried: ['parts_inventory'], rows_scanned: 342 };
    if (q.includes('warranty') && q.includes('expir')) return { answer: 'Warranty Expirations — Next 30 Days:\n\n⚠️ 8 systems expiring:\n• 5 Carrier units (residential)\n• 2 Trane units (commercial)\n• 1 Lennox unit (residential)\n\nRecommendation: Proactive outreach to offer extended warranty or maintenance contracts.\nEstimated conversion: 60% → 5 new maintenance contracts\nRevenue opportunity: $4,800/year', tables_queried: ['equipment_registry'], rows_scanned: 487 };
    if (q.includes('maintenance contract')) return { answer: 'Active Maintenance Contracts:\n\n📋 Total: 127 active contracts\n• Annual plans: 84 ($149/year)\n• Semi-annual plans: 43 ($89/visit)\n\nMonthly recurring revenue: $12,400\nRenewal rate: 82%\nContracts expiring this month: 11\nNew contracts this month: 8', tables_queried: ['maintenance_contracts'], rows_scanned: 127 };
    // Insurance
    if (q.includes('premium revenue')) return { answer: 'Premium Revenue — Q1 2026:\n\n💰 Total: $2.4M\n\nBy Office:\n• Charlotte (HQ): $820,000 (34%)\n• Raleigh: $640,000 (27%)\n• Greensboro: $520,000 (22%)\n• Wilmington: $280,000 (12%)\n• Asheville: $140,000 (6%)\n\nGrowth vs Q4 2025: +8.2%\nActive policies: 4,847', tables_queried: ['policies', 'offices', 'payments'], rows_scanned: 4847 };
    if (q.includes('loss ratio')) return { answer: 'Loss Ratio by Office:\n\n📊 Charlotte: 62% ✅\n📊 Raleigh: 58% ✅\n📊 Greensboro: 71% ⚠️ (above 65% threshold)\n📊 Wilmington: 55% ✅\n📊 Asheville: 49% ✅\n\n⚠️ Greensboro flagged — 3 large water damage claims ($180K total) driving ratio up.\nRecommendation: Review underwriting criteria for Greensboro flood-zone properties.', tables_queried: ['claims', 'policies'], rows_scanned: 1247 };
    if (q.includes('agent performance') || q.includes('compare agent')) return { answer: 'Agent Performance — Cross-Office Comparison:\n\n🏆 Top Agents:\n1. Sarah Kim (Charlotte) — 142 policies, 91% retention\n2. James Park (Raleigh) — 128 policies, 89% retention\n3. Lisa Chen (Asheville) — 98 policies, 94% retention\n4. Maria Santos (Greensboro) — 87 policies, 82% retention\n5. Tom Wilson (Wilmington) — 76 policies, 85% retention\n\nNetwork avg: 106 policies/agent, 88% retention\nTop performer premium volume: $1.2M (Sarah Kim)', tables_queried: ['agents', 'policies'], rows_scanned: 847 };
    if (q.includes('renewal rate') || q.includes('policy renewal')) return { answer: 'Policy Renewal Rates by Office:\n\n🔄 Asheville: 94% ⭐ Highest\n🔄 Charlotte: 88%\n🔄 Raleigh: 85%\n🔄 Wilmington: 82%\n🔄 Greensboro: 78% ⚠️ Lowest\n\nNetwork average: 85.4%\nPolicies up for renewal this month: 312\nAuto-renewal enabled: 67% of policies', tables_queried: ['policies'], rows_scanned: 4847 };
    if (q.includes('risk') || q.includes('underwriting')) return { answer: 'Underwriting Risk Scores — Pending Applications:\n\n📋 18 pending applications:\n• Low risk (score < 30): 8 applications — auto-approve recommended\n• Medium risk (30-60): 7 applications — standard review\n• High risk (score > 60): 3 applications — senior underwriter review\n\nHigh-risk flags:\n• 2 properties in FEMA flood zone A\n• 1 property with prior claims > $50K', tables_queried: ['underwriting', 'policies'], rows_scanned: 18 };
    // Plumbing / DrainMaster
    if (q.includes('network revenue') || (q.includes('total') && q.includes('revenue'))) return { answer: 'Network Revenue — March 2026:\n\n💰 Total: $187,400\n\n🏪 Franchise Locations: $124,600 (66%)\n• South Charlotte: $48,200 (128 jobs)\n• University Area: $41,800 (112 jobs)\n• Lake Norman: $34,600 (102 jobs)\n\n🛒 Retail Subscribers: $62,800 (34%)\n• Mike\'s Plumbing: $24,200 (68 jobs)\n• QuickFix Plumbing: $21,400 (72 jobs)\n• AquaPro Services: $17,200 (58 jobs)\n\nAvg job value: Franchise $364, Retail $317', tables_queried: ['work_orders', 'locations', 'invoices'], rows_scanned: 540 };
    if (q.includes('franchise') && q.includes('retail') && q.includes('volume')) return { answer: 'Job Volume — Franchise vs Retail (March 2026):\n\n🏪 Franchise: 342 jobs ($364/job avg)\n🛒 Retail: 198 jobs ($317/job avg)\n\nTotal: 540 jobs\n\nBy Type:\n• Emergency repair: 89 (16%)\n• Scheduled repair: 218 (40%)\n• Installation: 124 (23%)\n• Maintenance: 109 (20%)', tables_queried: ['work_orders'], rows_scanned: 540 };
    if (q.includes('lead distribution') || q.includes('lead')) return { answer: 'Lead Distribution — March 2026:\n\n📊 Total leads: 89\n\n• South Charlotte: 22 leads (25%)\n• University Area: 18 leads (20%)\n• Lake Norman: 15 leads (17%)\n• Mike\'s Plumbing: 14 leads (16%)\n• QuickFix Plumbing: 11 leads (12%)\n• AquaPro Services: 9 leads (10%)\n\nConversion rate: 72% (64 of 89 converted)\nAvg response time: 34 minutes', tables_queried: ['leads', 'locations'], rows_scanned: 89 };
    if (q.includes('customer rating') || q.includes('best rating')) return { answer: 'Customer Ratings by Location:\n\n⭐ AquaPro Services (retail): 4.9/5.0 — 58 reviews\n⭐ South Charlotte (franchise): 4.8/5.0 — 128 reviews\n⭐ Mike\'s Plumbing (retail): 4.7/5.0 — 68 reviews\n⭐ University Area (franchise): 4.6/5.0 — 112 reviews\n⭐ Lake Norman (franchise): 4.6/5.0 — 102 reviews\n⭐ QuickFix Plumbing (retail): 4.5/5.0 — 72 reviews\n\nNetwork average: 4.68/5.0', tables_queried: ['network_reviews', 'locations'], rows_scanned: 540 };
    if (q.includes('franchise fee') || q.includes('billing')) return { answer: 'Franchise Fee Billing — March 2026:\n\n🏪 Franchise Locations (8% of revenue):\n• South Charlotte: $3,856\n• University Area: $3,344\n• Lake Norman: $2,768\nSubtotal: $9,968\n\n🛒 Retail Subscribers ($299/mo flat):\n• Mike\'s Plumbing: $299\n• QuickFix Plumbing: $299\n• AquaPro Services: $299\nSubtotal: $897\n\nTotal platform fees: $10,865', tables_queried: ['franchise_fees', 'locations'], rows_scanned: 6 };
    // Medical / Wellness
    if (q.includes('patient retention') || q.includes('retention')) return { answer: 'Patient Retention Analytics (March 2026):\n\n📊 Overall retention: 84.2%\n\nBy Service:\n• Dermatology: 91% (highest)\n• Cryotherapy: 78%\n• IV Hydration: 72%\n• General Wellness: 86%\n\nNew patients this month: 34\nReturning patients: 187\nAt-risk (no visit in 60+ days): 23 patients\nRecommendation: Send re-engagement offers to at-risk group', tables_queried: ['patients', 'appointments'], rows_scanned: 244 };
    if (q.includes('treatment room') || q.includes('utilization')) return { answer: 'Treatment Room Utilization (This Week):\n\n🏥 Room A (Dermatology): 87% utilized — 32 appointments\n🏥 Room B (Cryotherapy): 64% utilized — 18 sessions\n🏥 Room C (IV Therapy): 71% utilized — 24 sessions\n🏥 Room D (General): 58% utilized — 21 appointments\n\nOverall: 70% utilization\nPeak hours: 10am-12pm, 2pm-4pm\nUnderutilized: Room D (consider adding evening hours)', tables_queried: ['treatment_rooms', 'appointments'], rows_scanned: 95 };
    if (q.includes('medical supply') || q.includes('supply reorder')) return { answer: 'Medical Supply Reorder Alerts:\n\n🔴 Critical:\n• Cryotherapy nitrogen — 2 tanks (reorder: 5)\n• IV saline bags — 12 (reorder: 50)\n• Disposable gloves (M) — 1 box (reorder: 10)\n\n🟡 Warning:\n• Dermatology swabs — 80 (reorder: 200)\n• Alcohol prep pads — 150 (reorder: 400)\n\nEstimated restock cost: $2,340\nSupplier: MedSupply Direct (next-day delivery)', tables_queried: ['medical_supplies'], rows_scanned: 87 };
    if (q.includes('revenue') && q.includes('treatment')) return { answer: 'Revenue by Treatment Type (March 2026):\n\n💰 Dermatology Consults: $48,200 (42%) — 187 visits\n💰 Cryotherapy Sessions: $28,800 (25%) — 96 sessions\n💰 IV Hydration Therapy: $21,600 (19%) — 72 sessions\n💰 General Wellness: $16,400 (14%) — 82 visits\n\nTotal: $115,000\nAvg revenue per visit: $262.70\nInsurance claims pending: 34 ($18,400)', tables_queried: ['treatments', 'appointments', 'insurance_claims'], rows_scanned: 437 };
    if (q.includes('insurance claim') || q.includes('claim processing')) return { answer: 'Insurance Claim Status:\n\n📋 Total claims this month: 142\n• Approved: 98 (69%) — avg 4.2 days processing\n• Pending: 34 (24%) — avg 8 days in queue\n• Denied: 10 (7%) — top reason: pre-auth missing\n\nTotal claimed: $87,400\nTotal paid: $62,200\nDenial rate trending down (was 12% last month)', tables_queried: ['insurance_claims'], rows_scanned: 142 };
    // Auto Valet
    if (q.includes('valet') && q.includes('shift')) return { answer: 'Valet Shift Optimization (This Week):\n\n👥 Morning (7am-2pm): 4 valets — avg 12 vehicles/hr\n👥 Afternoon (2pm-8pm): 6 valets — avg 22 vehicles/hr ⭐ Peak\n👥 Evening (8pm-11pm): 3 valets — avg 8 vehicles/hr\n\nAvg retrieval time: 4.2 minutes\nSLA target: 5 minutes — 94% compliance\nRecommendation: Add 1 valet to afternoon shift on weekends', tables_queried: ['shifts', 'valet_tickets'], rows_scanned: 1840 };
    if (q.includes('ev') && q.includes('charging')) return { answer: 'EV Charging Station Report:\n\n⚡ Station 1 (Level 3 DC Fast): 87% utilization — $1,240 revenue\n⚡ Station 2 (Level 3 DC Fast): 72% utilization — $980 revenue\n⚡ Station 3 (Level 2): 45% utilization — $320 revenue\n⚡ Station 4 (Level 2): 38% utilization — $280 revenue\n\nTotal sessions: 342\nAvg charge time: 28 min (DC), 2.4 hrs (Level 2)\nTotal revenue: $2,820\nkWh delivered: 8,420', tables_queried: ['ev_charging'], rows_scanned: 342 };
    if (q.includes('vehicle retrieval') || q.includes('retrieval time')) return { answer: 'Vehicle Retrieval Performance:\n\n⏱️ Average retrieval: 4.2 minutes\n\nBy Time of Day:\n• Morning: 3.1 min (low volume)\n• Lunch: 5.8 min (peak)\n• Afternoon: 4.4 min\n• Evening: 3.6 min\n\n🏆 Fastest valet: Marcus T. — 2.8 min avg\nSLA breaches (>5 min): 6% of retrievals\nCustomer satisfaction: 4.7/5.0', tables_queried: ['valet_tickets'], rows_scanned: 1840 };
    if (q.includes('fleet') && q.includes('maintenance')) return { answer: 'Fleet Vehicle Maintenance:\n\n🚗 Total fleet: 8 vehicles\n✅ Current: 5 vehicles\n🟡 Due soon: 2 vehicles (oil change within 500 miles)\n🔴 Overdue: 1 vehicle (brake inspection — 200 miles overdue)\n\nMaintenance spend this month: $1,840\nBudget remaining: $2,160 of $4,000\nNext scheduled: Golf cart #3 — tire rotation (April 3)', tables_queried: ['fleet_vehicles'], rows_scanned: 8 };
    if (q.includes('detail service') || q.includes('express detail')) return { answer: 'Express Detail Service Report:\n\n🧽 This month: 89 detail jobs\n• Express Wash: 52 ($25 each) — $1,300\n• Interior Detail: 24 ($75 each) — $1,800\n• Full Detail: 13 ($150 each) — $1,950\n\nTotal revenue: $5,050\nAvg completion time: 42 min\nCustomer rating: 4.8/5.0\nRepeat rate: 34%', tables_queried: ['detail_services'], rows_scanned: 89 };
    // Logistics
    if (q.includes('route') && q.includes('fuel')) return { answer: 'Route Fuel Efficiency Analytics:\n\n⛽ Fleet average: 7.2 MPG\n\nBy Route:\n• Charlotte → Raleigh (I-85): 8.1 MPG — most efficient\n• Charlotte → Asheville (I-40): 6.4 MPG — mountain terrain\n• Local last-mile: 5.8 MPG — stop-and-go\n• Charlotte → Wilmington (I-74): 7.8 MPG\n\nFuel spend this month: $18,400\nCost per mile: $0.42\nRecommendation: Optimize Asheville route with I-26 bypass', tables_queried: ['routes', 'fleet'], rows_scanned: 1240 };
    if (q.includes('driver performance') || q.includes('driver')) return { answer: 'Driver Performance Metrics:\n\n🏆 Top Drivers:\n1. James Rivera — Safety: 98/100, On-time: 96%, MPG: 8.2\n2. Sarah Chen — Safety: 97/100, On-time: 94%, MPG: 7.8\n3. Marcus Williams — Safety: 95/100, On-time: 92%, MPG: 7.5\n\n⚠️ Needs improvement:\n• David Park — 2 late deliveries, 1 HOS violation\n\nFleet avg safety score: 94/100\nHOS compliance: 98.2%', tables_queried: ['drivers', 'delivery_tracking'], rows_scanned: 12 };
    if (q.includes('cargo') && q.includes('capacity')) return { answer: 'Cargo Capacity Optimization:\n\n📦 Fleet utilization: 74% average\n\nBy Vehicle Type:\n• Box trucks (4): 82% capacity — $12,400 revenue\n• Refrigerated (2): 91% capacity — $8,800 revenue ⭐\n• Flatbed (2): 58% capacity — $4,200 revenue\n• Sprinter vans (4): 68% capacity — $6,600 revenue\n\nEmpty miles: 12% (target: <10%)\nRecommendation: Consolidate flatbed loads with return-trip matching', tables_queried: ['shipments', 'fleet'], rows_scanned: 340 };
    if (q.includes('delivery time') || q.includes('sla')) return { answer: 'Delivery SLA Performance:\n\n📊 On-time delivery rate: 93.4%\n\nBy Service:\n• Last-mile (same day): 96% on-time — avg 2.4 hrs\n• Standard freight (next day): 91% on-time — avg 18 hrs\n• Temperature-controlled: 94% on-time — avg 6 hrs\n\nSLA breaches this month: 14 of 210 deliveries\nTop reason: Traffic delays (8), Weather (4), Mechanical (2)\nCustomer satisfaction: 4.6/5.0', tables_queried: ['delivery_tracking', 'shipments'], rows_scanned: 210 };
    if (q.includes('contractor') || q.includes('peak demand')) return { answer: 'Independent Contractor Pool:\n\n👥 Active contractors: 18\n• Available now: 12\n• On assignment: 6\n\nPeak demand forecast (next 2 weeks):\n• April 3-4: +40% volume (Easter weekend)\n• April 7: +25% (Monday surge)\n\nRecommendation: Activate 4 additional contractors for April 3-4\nAvg contractor rate: $28/hr\nEstimated cost: $1,792 for peak coverage', tables_queried: ['contractors', 'shipments'], rows_scanned: 18 };
    // Real Estate
    if (q.includes('active listing') || q.includes('days on market')) return { answer: 'Active Listings & Market Performance:\n\n🏠 Active Sale Listings: 24\n🏢 Active Rental Listings: 6\n\nSale Listings by Price Range:\n• Under $200K: 3 listings (avg 18 days on market)\n• $200K-$400K: 12 listings (avg 14 days on market)\n• $400K-$800K: 6 listings (avg 22 days on market)\n• Over $800K: 3 listings (avg 35 days on market)\n\nOverall avg days on market: 19 days\nPrice reductions this month: 4 listings\nNew listings this week: 3', tables_queried: ['properties'], rows_scanned: 30 };
    if (q.includes('commission revenue')) return { answer: 'Commission Revenue — March 2026:\n\n💰 Total Commission: $18,720\n\nBy Transaction Type:\n• Sale closings (18): $16,380 (avg $910/closing)\n• Rental placements (8): $2,340 (avg $293/placement)\n\nTop Earning Agents:\n1. Agent Kim — $6,240 (7 closings)\n2. Agent Davis — $4,860 (5 closings)\n3. Agent Martinez — $3,780 (4 closings)\n\nPending closings: 8 ($312K total value)\nProjected April commission: $22,400', tables_queried: ['transactions', 'agents'], rows_scanned: 26 };
    if (q.includes('rental occupancy') || q.includes('occupancy rate')) return { answer: 'Rental Portfolio — Occupancy Report:\n\n📊 Overall Occupancy: 87.5% (42 of 48 units)\n\nBy Neighborhood:\n• Eastway Park: 95% (19/20 units)\n• Shamrock Hills: 83% (10/12 units)\n• Idlewild: 88% (7/8 units)\n• University City: 75% (6/8 units) ⚠️\n\n🔴 Vacant Units: 6\n• 2 units vacant > 30 days (reduce rent recommended)\n• 4 units vacant < 14 days (new listings)\n\nAvg monthly rent: $1,580\nTotal rental revenue: $66,360/month\nLease expirations next 60 days: 5', tables_queried: ['rentals', 'properties'], rows_scanned: 48 };
    if (q.includes('agent') && (q.includes('closing') || q.includes('performance'))) return { answer: 'Agent Performance — Q1 2026:\n\n🏆 Rankings by Closings:\n1. Agent Kim — 18 closings, $4.2M volume, 4.9/5.0 rating\n2. Agent Davis — 14 closings, $3.1M volume, 4.8/5.0 rating\n3. Agent Martinez — 12 closings, $2.8M volume, 4.7/5.0 rating\n4. Agent Thompson — 9 closings, $1.9M volume, 4.6/5.0 rating\n5. Agent Lee — 7 closings, $1.4M volume, 4.5/5.0 rating\n\nTeam total: 60 closings, $13.4M volume\nAvg sale price: $223K\nAvg list-to-sale ratio: 97.2%', tables_queried: ['agents', 'transactions'], rows_scanned: 60 };
    if (q.includes('lead') && q.includes('source')) return { answer: 'Lead Pipeline — March 2026:\n\n📊 Total Leads: 87\n\nBy Source:\n• Website (kmdrealty.com): 28 (32%)\n• Zillow/Realtor.com: 22 (25%)\n• Referrals: 18 (21%)\n• Walk-ins: 8 (9%)\n• Social Media: 7 (8%)\n• Open Houses: 4 (5%)\n\nConversion Rate: 34% (30 of 87 converted)\n\nBy Intent:\n• Buyers: 42 leads (38% conversion)\n• Renters: 31 leads (42% conversion)\n• Sellers: 14 leads (21% conversion)\n\nHot leads (ready to transact): 12', tables_queried: ['leads', 'clients'], rows_scanned: 87 };
    if (q.includes('lease expir') || q.includes('upcoming lease')) return { answer: 'Upcoming Lease Expirations:\n\n📅 Next 60 Days: 5 leases expiring\n\n1. Unit 204, Shamrock Dr — expires April 15 — $1,450/mo — tenant interested in renewal\n2. Unit 7B, Wallace Ln — expires April 22 — $1,850/mo — tenant undecided\n3. Unit 12, Eastway Dr — expires May 1 — $1,200/mo — tenant moving out\n4. Unit 3A, Central Ave — expires May 8 — $1,650/mo — renewal sent\n5. Unit 9, University Blvd — expires May 15 — $1,350/mo — tenant interested\n\nRecommendation: Send renewal offers with 3% increase to units 1, 2, 4, 5\nUnit 12: List for new tenant at $1,300/mo (market rate increase)', tables_queried: ['rentals'], rows_scanned: 48 };
    // IT Consulting
    const isITConsult = product.includes('it consult') || product.includes('consulting');
    if (isITConsult) {
      if (q.includes('utilization') || q.includes('billable')) return { answer: 'Resource Utilization Report — May 2026:\n\n📊 Overall Utilization: 78.4%\n\nBy Department:\n• Cloud & DevOps: 92% (18 of 20 consultants billable)\n• Data Engineering: 85% (17 of 20)\n• Full-Stack Dev: 76% (38 of 50)\n• QA & Testing: 71% (14 of 20)\n• Project Management: 68% (7 of 10)\n\nBench: 22 consultants (18.3%)\nTarget utilization: 80%\nRevenue impact of 1% improvement: $42K/month', tables_queried: ['consultants', 'projects', 'timesheets'], rows_scanned: 120 };
      if (q.includes('project') && (q.includes('active') || q.includes('status'))) return { answer: 'Active Projects — May 2026:\n\n📋 Total Active: 24 projects\n\nBy Status:\n• 🟢 On Track: 16 (67%)\n• 🟡 At Risk: 5 (21%)\n• 🔴 Delayed: 3 (12%)\n\nTop 5 by Revenue:\n1. FinTech Platform Migration — $480K — On Track\n2. Healthcare Data Lake — $320K — At Risk (resource gap)\n3. E-Commerce Replatform — $290K — On Track\n4. Cloud Security Audit — $180K — On Track\n5. AI/ML Pipeline — $165K — Delayed (scope creep)\n\nTotal active contract value: $3.2M\nAvg project duration: 4.8 months', tables_queried: ['projects', 'clients'], rows_scanned: 24 };
      if (q.includes('bench') || q.includes('available')) return { answer: 'Bench Report — May 2026:\n\n👥 Total on Bench: 22 consultants\n\nBy Skill:\n• Java/Spring: 5 (avg 12 days on bench)\n• React/Node: 4 (avg 8 days)\n• Python/ML: 3 (avg 18 days)\n• DevOps/Cloud: 2 (avg 5 days)\n• QA Automation: 4 (avg 15 days)\n• Project Managers: 2 (avg 22 days)\n• Other: 2 (avg 10 days)\n\n⚠️ Long bench (>30 days): 4 consultants\nBench cost this month: $132K\nPipeline matches: 8 potential placements', tables_queried: ['consultants', 'bench_log'], rows_scanned: 22 };
      if (q.includes('billing') || q.includes('revenue') || q.includes('invoice')) return { answer: 'Billing Summary — May 2026:\n\n💰 Total Billable Revenue: $1.84M\n\nBy Client:\n• Acme Financial: $420K (3 projects)\n• TechCorp Inc: $310K (2 projects)\n• HealthFirst: $280K (1 project)\n• RetailMax: $240K (2 projects)\n• GovTech Solutions: $190K (1 project)\n• Others (14 clients): $400K\n\nBilling Rate Avg: $165/hr\nCollections pending: $340K\nDSO (Days Sales Outstanding): 38 days', tables_queried: ['invoices', 'projects', 'clients'], rows_scanned: 24 };
      if (q.includes('timesheet') || q.includes('compliance')) return { answer: 'Timesheet Compliance — This Week:\n\n📊 Submission Rate: 87% (104 of 120 consultants)\n\n⚠️ Missing Timesheets: 16 consultants\n• Cloud & DevOps: 2 missing\n• Full-Stack Dev: 8 missing\n• QA & Testing: 4 missing\n• Project Management: 2 missing\n\nAvg hours logged: 41.2 hrs/week\nOvertime (>45 hrs): 12 consultants\nUnder-logged (<35 hrs): 8 consultants\n\nDeadline: Friday 5:00 PM\nAuto-reminder sent to 16 non-submitters', tables_queried: ['timesheets', 'consultants'], rows_scanned: 120 };
      if (q.includes('pipeline') || q.includes('sales') || q.includes('opportunity')) return { answer: 'Sales Pipeline — May 2026:\n\n📈 Total Pipeline Value: $4.8M\n\nBy Stage:\n• Qualified Lead: $1.2M (8 opportunities)\n• Proposal Sent: $1.8M (6 opportunities)\n• Negotiation: $1.1M (4 opportunities)\n• Verbal Commit: $700K (3 opportunities)\n\nWin Rate (last 90 days): 42%\nAvg Deal Size: $228K\nAvg Sales Cycle: 34 days\n\nHot Opportunities:\n1. Enterprise Cloud Migration — $650K — 80% probability\n2. Data Platform Build — $420K — 70% probability\n3. Security Assessment — $180K — 90% probability', tables_queried: ['opportunities', 'clients'], rows_scanned: 21 };
    }
    // Banking / Financial-Bank-Type1 — ONLY for bank clients
    const isBankClient = product.includes('bank') || product.includes('financial') || product.includes('risk-llm');
    if (isBankClient) {
      if (q.includes('loan-to-deposit') || q.includes('loan to deposit') || q.includes('ltd ratio')) return { answer: 'Loan-to-Deposit Ratio — Balance Sheet Summary (April 2026):\n\n🏦 Total Loans: $500.0B\n💰 Total Deposits: $550.0B\n📊 Loan-to-Deposit Ratio: 90.9%\n\nStatus: ✅ Healthy (target range: 80-95%)\n\nTrend (3 months):\n• Feb 2026: 90.8% ($495B / $545B)\n• Mar 2026: 90.9% ($498B / $548B)\n• Apr 2026: 90.9% ($500B / $550B)\n\nTotal Assets: $580.0B\nTotal Securities: $80.0B\nLiquidity Coverage Ratio: 112% (above 100% minimum)', tables_queried: ['balance_sheet_summary'], rows_scanned: 3 };
      if (q.includes('deposit stability') || q.includes('decay rate') || q.includes('deposit behavior')) return { answer: 'Deposit Stability Analysis — Behavioral Layer:\n\n📊 Deposit Accounts by Stability:\n\n✅ Core Deposits (high stability):\n• Retail Checking: Stability 92.5/100, Decay 2.0%, Balance $120B — CORE\n• Retail Savings: Stability 85.0/100, Decay 3.5%, Balance $185B — CORE\n\n🟡 Moderate Stability:\n• Corporate Operating: Stability 72.0/100, Decay 5.0%, Balance $95B\n• 24-Month CD: Stability 68.0/100, Decay 6.0%, Balance $70B\n\n⚠️ Rate-Sensitive:\n• 12-Month CD: Stability 60.0/100, Decay 8.0%, Balance $80B\n\nTotal Deposits: $550B\nCore Deposit Ratio: 55.5% ($305B of $550B)\nWeighted Avg Decay Rate: 4.1%', tables_queried: ['deposit_behavior', 'accounts', 'products'], rows_scanned: 5 };
      if (q.includes('loan portfolio') || q.includes('loan breakdown') || q.includes('total loan')) return { answer: 'Loan Portfolio Breakdown (April 2026):\n\n📄 Total Loan Portfolio: $500.0B\n\nBy Category:\n• Commercial Loans: $228.0B (45.6%) — Rate: 7.75%\n• 30-Year Mortgages: $180.0B (36.0%) — Rate: 6.75%\n• 15-Year Mortgages: $45.0B (9.0%) — Rate: 6.00%\n• Auto Loans: $35.0B (7.0%) — Rate: 7.70%\n• Personal Loans: $12.0B (2.4%) — Rate: 10.50%\n\nBy Customer Type:\n• Retail: $272.0B (54.4%)\n• Corporate: $228.0B (45.6%)\n\nWeighted Avg Rate: 7.24%\nAll accounts status: Active', tables_queried: ['accounts', 'products', 'assets'], rows_scanned: 5 };
      if (q.includes('cashflow') || q.includes('cash flow') || q.includes('projected')) return { answer: 'Cashflow Projections — Q2 2026:\n\n💸 Base Scenario:\n\nInflows (loan P&I + security coupons):\n• May: $2.49B (Mortgage $1.01B + Commercial $1.47B)\n• Jun: $2.49B\n• Jul: $3.34B (includes $850M Treasury coupon)\n\nOutflows (deposit withdrawals):\n• May: $4.25B (Checking $2.4B + Savings $1.85B)\n• Jun: $4.25B\n• Oct: $80.0B ⚠️ (12-Month CD maturity)\n\n📊 Stress Scenario:\n• May outflows increase to $11.55B (+172%)\n  — Checking: $6.0B (2.5x base)\n  — Savings: $5.55B (3x base)\n\nNet Position (Base, May): -$1.76B\nNet Position (Stress, May): -$9.06B', tables_queried: ['cashflows'], rows_scanned: 17 };
      if (q.includes('risk-weighted') || q.includes('rwa') || q.includes('capital adequacy') || q.includes('capital ratio')) return { answer: 'Risk & Capital Adequacy (April 2026):\n\n⚠️ Risk-Weighted Assets (RWA):\n\nBy Asset Class (Base Scenario):\n• Commercial Loans: $228.0B (RW: 100%) — RWA: $228.0B\n• 30-Year Mortgages: $180.0B (RW: 50%) — RWA: $90.0B\n• Auto Loans: $35.0B (RW: 75%) — RWA: $26.3B\n• 15-Year Mortgages: $45.0B (RW: 50%) — RWA: $22.5B\n• Personal Loans: $12.0B (RW: 100%) — RWA: $12.0B\n• Corp Bonds: $15.0B (RW: 50%) — RWA: $7.5B\n• MBS: $25.0B (RW: 20%) — RWA: $5.0B\n• US Treasuries: $40.0B (RW: 0%) — RWA: $0\n\n📊 Total RWA: $391.3B\n🏦 Tier 1 Capital Ratio: 12.5% ✅ (well-capitalized > 10%)\n💰 Capital Required (8% min): $31.3B\n📉 Total Expected Loss (Base): $5.87B', tables_queried: ['risk_metrics', 'assets'], rows_scanned: 10 };
      if (q.includes('net interest margin') || q.includes('nim')) return { answer: 'Net Interest Margin (NIM) Trend:\n\n📊 NIM Analysis:\n• Feb 2026: 3.10%\n• Mar 2026: 3.18%\n• Apr 2026: 3.25% ✅\n\nTrend: ↑ Improving (+15 bps over 3 months)\n\nDecomposition:\n• Weighted Avg Loan Yield: ~7.24%\n• Weighted Avg Cost of Funds: ~3.95%\n• Spread: ~3.29%\n\nTop Margin Contributors:\n• Personal Loans: 10.50% yield (highest)\n• Commercial Loans: 7.75% yield (largest volume)\n• Retail Checking: 0.01% cost (cheapest funding)\n\nMargin Pressure:\n• 12-Month CD: 4.75% cost (most expensive)\n• Retail Savings: 4.25% cost (large balance)', tables_queried: ['balance_sheet_summary', 'products', 'liabilities'], rows_scanned: 18 };
      if (q.includes('balance sheet') || q.includes('summary report') || q.includes('summary')) return { answer: 'Balance Sheet Summary — Prabs National Bank (April 2026):\n\n🏦 ASSETS: $580.0B\n• Total Loans: $500.0B (86.2%)\n  — Mortgages: $225.0B\n  — Commercial: $228.0B\n  — Auto: $35.0B\n  — Personal: $12.0B\n• Total Securities: $80.0B (13.8%)\n  — US Treasuries: $40.0B\n  — Agency MBS: $25.0B\n  — Corp Bonds: $15.0B\n\n💰 LIABILITIES: $550.0B\n• Retail Savings: $185.0B (33.6%)\n• Retail Checking: $120.0B (21.8%)\n• Corporate Operating: $95.0B (17.3%)\n• 12-Month CD: $80.0B (14.5%)\n• 24-Month CD: $70.0B (12.7%)\n\n📊 KEY RATIOS:\n• Loan-to-Deposit: 90.9%\n• Net Interest Margin: 3.25%\n• Liquidity Coverage Ratio: 112%\n• Tier 1 Capital: 12.5%\n• Total RWA: $391.3B\n\nAssessment: Healthy liquidity, moderate risk, improving margins', tables_queried: ['balance_sheet_summary', 'assets', 'liabilities', 'accounts'], rows_scanned: 26 };
      if (q.includes('prepayment') || q.includes('cpr') || q.includes('psa')) return { answer: 'Loan Prepayment Behavior:\n\n📄 Prepayment Speeds by Product:\n\n• Personal Loans: CPR 15.0%, PSA 100 — Highest prepay risk\n• Auto Loans: CPR 12.0%, PSA 100 — High prepay\n• 15-Year Mortgage: CPR 8.0%, PSA 200 — Above benchmark\n• 30-Year Mortgage: CPR 6.0%, PSA 150 — Moderate\n• Commercial Loans: CPR 4.0%, PSA 100 — Most stable\n\nRefinance Incentive Thresholds:\n• Auto: 2.0% rate differential triggers refi\n• Commercial: 1.5%\n• 30-Year Mortgage: 1.25%\n• 15-Year Mortgage: 1.0%\n\nBurnout Factors (lower = more seasoned):\n• Personal: 0.70 | Auto: 0.75 | 30yr: 0.85 | 15yr: 0.90 | Commercial: 0.95', tables_queried: ['loan_prepayment', 'accounts', 'products'], rows_scanned: 5 };
      if (q.includes('stress') && (q.includes('test') || q.includes('scenario'))) return { answer: 'Stress Test Results — Base vs Stress Scenario:\n\n📊 30-Year Mortgage Portfolio ($180B):\n• Base: PD 0.50%, EL $900M, Capital $7.2B\n• Stress: PD 1.50%, EL $2.7B (+200%), Capital $10.8B (+50%)\n\n📊 Commercial Loan Portfolio ($228B):\n• Base: PD 1.00%, EL $3.4B, Capital $18.2B\n• Stress: PD 3.00%, EL $10.3B (+200%), Capital $27.4B (+50%)\n\n💸 Cashflow Impact (May):\n• Base outflows: $4.25B\n• Stress outflows: $11.55B (+172%)\n\n⚠️ Key Risk: Deposit run scenario shows $7.3B additional outflow\nMitigation: $80B in securities available for liquidation\nLiquidity buffer under stress: Adequate', tables_queried: ['risk_metrics', 'cashflows'], rows_scanned: 27 };
      if (q.includes('margin') || q.includes('highest margin') || q.includes('most profitable') || q.includes('product') && q.includes('profit')) return { answer: 'Product Margin Analysis — Loan Yield vs Cost of Funds:\n\n📊 Margin by Product (Yield minus Weighted Avg Cost of Funds 3.95%):\n\n🥇 Personal Unsecured Loan: 10.50% yield → 6.55% margin ⭐ Highest\n🥈 Commercial Term Loan: 7.75% yield → 3.80% margin\n🥉 Auto Loan 60-Month: 7.70% yield → 3.75% margin\n4. 30-Year Fixed Mortgage: 6.75% yield → 2.80% margin\n5. 15-Year Fixed Mortgage: 6.00% yield → 2.05% margin\n\n📊 Margin by Funding Source (cheapest = highest margin contribution):\n🥇 Retail Checking: 0.01% cost → $120B at near-zero cost ⭐\n🥈 Corporate Operating: 0.50% cost → $95B low-cost funding\n🥉 Retail Savings: 4.25% cost → $185B moderate cost\n4. 24-Month CD: 4.50% cost → $70B\n5. 12-Month CD: 4.75% cost → $80B most expensive\n\nSQL equivalent:\nSELECT p.product_name, a.interest_rate AS yield,\n  (SELECT AVG(l.cost_of_funds) FROM liabilities l) AS avg_cost,\n  a.interest_rate - (SELECT AVG(l.cost_of_funds) FROM liabilities l) AS margin\nFROM accounts a JOIN products p ON p.product_id = a.product_id\nWHERE p.product_type = \'loan\' ORDER BY margin DESC;\n\nNet Interest Margin (bank-wide): 3.25%\nSource tables: products, accounts, liabilities', tables_queried: ['products', 'accounts', 'liabilities'], rows_scanned: 18 };
    }
    // Smart fallback — analyze the question and recommend relevant queries from available schema
    const bankKeywords = isBankClient;
    if (bankKeywords) {
      // Map question intent to recommended SQL against PQRM tables
      const recommendations = [];
      if (q.includes('bottleneck') || q.includes('throughput') || q.includes('slow') || q.includes('delay') || q.includes('operational')) {
        recommendations.push(
          { label: 'Deposit Runoff (liquidity bottleneck)', sql: "SELECT p.product_name, db.decay_rate, db.runoff_rate, db.stability_score, a.balance FROM deposit_behavior db JOIN accounts a ON a.account_id = db.account_id JOIN products p ON p.product_id = a.product_id ORDER BY db.runoff_rate DESC;" },
          { label: 'Cashflow Gaps (outflow > inflow)', sql: "SELECT c.scenario, c.flow_type, SUM(c.amount) AS total FROM cashflows c GROUP BY c.scenario, c.flow_type ORDER BY c.scenario, total DESC;" },
          { label: 'High-Risk Accounts (capital drag)', sql: "SELECT p.product_name, rm.rwa, rm.expected_loss, rm.capital_required, rm.pd FROM risk_metrics rm JOIN accounts a ON a.account_id = rm.account_id JOIN products p ON p.product_id = a.product_id WHERE rm.scenario = 'base' ORDER BY rm.capital_required DESC;" },
        );
      } else if (q.includes('customer') || q.includes('segment') || q.includes('who')) {
        recommendations.push(
          { label: 'Accounts by Customer Type', sql: "SELECT customer_type, COUNT(*) AS accounts, SUM(balance) AS total_balance FROM accounts GROUP BY customer_type ORDER BY total_balance DESC;" },
          { label: 'Products by Category', sql: "SELECT product_type, category, COUNT(*) AS count FROM products GROUP BY product_type, category ORDER BY product_type;" },
        );
      } else if (q.includes('trend') || q.includes('over time') || q.includes('history') || q.includes('growth')) {
        recommendations.push(
          { label: 'Balance Sheet Trend', sql: "SELECT as_of_date, total_assets, total_loans, total_deposits, net_interest_margin, loan_to_deposit_ratio FROM balance_sheet_summary ORDER BY as_of_date;" },
          { label: 'NIM Trend', sql: "SELECT as_of_date, net_interest_margin, tier1_capital_ratio FROM balance_sheet_summary ORDER BY as_of_date;" },
        );
      } else if (q.includes('compare') || q.includes('vs') || q.includes('versus') || q.includes('benchmark')) {
        recommendations.push(
          { label: 'Base vs Stress Scenario', sql: "SELECT scenario, SUM(rwa) AS total_rwa, SUM(expected_loss) AS total_el, SUM(capital_required) AS total_capital FROM risk_metrics GROUP BY scenario;" },
          { label: 'Asset Types Comparison', sql: "SELECT asset_type, SUM(balance) AS total, AVG(risk_weight) AS avg_rw FROM assets GROUP BY asset_type ORDER BY total DESC;" },
        );
      } else {
        // Truly generic — show the most useful starting queries
        recommendations.push(
          { label: 'Balance Sheet Overview', sql: "SELECT total_assets, total_liabilities, total_loans, total_deposits, net_interest_margin, loan_to_deposit_ratio, tier1_capital_ratio FROM balance_sheet_summary ORDER BY as_of_date DESC LIMIT 1;" },
          { label: 'All Products', sql: "SELECT product_name, product_type, category, base_rate, spread, term_months FROM products ORDER BY product_type, base_rate DESC;" },
          { label: 'Account Summary', sql: "SELECT p.product_name, a.customer_type, a.balance, a.interest_rate, a.status FROM accounts a JOIN products p ON p.product_id = a.product_id ORDER BY a.balance DESC;" },
          { label: 'Deposit Stability', sql: "SELECT p.product_name, db.decay_rate, db.stability_score, db.core_deposit_flag, a.balance FROM deposit_behavior db JOIN accounts a ON a.account_id = db.account_id JOIN products p ON p.product_id = a.product_id ORDER BY db.stability_score DESC;" },
          { label: 'Risk Metrics', sql: "SELECT p.product_name, rm.rwa, rm.expected_loss, rm.pd, rm.lgd FROM risk_metrics rm JOIN accounts a ON a.account_id = rm.account_id JOIN products p ON p.product_id = a.product_id WHERE rm.scenario = 'base' ORDER BY rm.rwa DESC;" },
        );
      }
      const recText = recommendations.map((r, i) => `${i + 1}. ${r.label}\n   SQL: ${r.sql}`).join('\n\n');
      return {
        answer: `Query: "${query}"\n\n🔍 I analyzed your question against the database schema. Here are recommended queries:\n\n${recText}\n\n💡 Tip: Click "Refine Query" and paste any SQL above, or try the operation buttons for pre-built analytics.`,
        tables_queried: isBankClient ? ['products', 'accounts', 'balance_sheet_summary', 'risk_metrics', 'deposit_behavior'] : ['business_data'],
        rows_scanned: 0,
        recommended_sql: recommendations,
      };
    }
    // ── 3-TIER INTELLIGENCE RESOLUTION ──
    // Tier 1: Distilled SQL Intelligence (pre-built patterns from product type)
    // Tier 2: ER Intelligence (schema-aware SQL from table relationships)
    // Tier 3: LLM (last resort — may hallucinate table names)
    const distilled = [];
    const erIntel = [];
    const llmSuggested = [];

    // Restaurant/Supermarket/Grocery — share similar schema patterns
    const isRestaurant = product.includes('restaurant') || product.includes('hospitality') || product.includes('food') || product.includes('supermarket') || product.includes('grocery');

    // ── TIER 1: Distilled SQL Intelligence ──
    if (q.includes('revenue') || q.includes('sales') || q.includes('income')) {
      distilled.push({ label: 'Revenue by Order Type', sql: isRestaurant ? "SELECT order_type, COUNT(*) AS orders, SUM(total_amount) AS revenue\nFROM orders\nWHERE order_date >= DATE_TRUNC('month', CURRENT_DATE)\nGROUP BY order_type\nORDER BY revenue DESC;" : "SELECT DATE_TRUNC('month', created_at) AS month, SUM(amount) AS revenue, COUNT(*) AS transactions\nFROM orders\nGROUP BY month\nORDER BY month DESC LIMIT 6;" });
    }
    if (q.includes('inventory') || q.includes('stock') || q.includes('supply') || q.includes('reorder')) {
      distilled.push({ label: 'Low Stock Items', sql: isRestaurant ? "SELECT ii.item_name, bi.quantity_on_hand, ii.reorder_level\nFROM inventory_items ii\nJOIN branch_inventory bi ON bi.inventory_item_id = ii.inventory_item_id\nWHERE bi.quantity_on_hand < ii.reorder_level\nORDER BY bi.quantity_on_hand;" : "SELECT item_name, quantity_on_hand, reorder_level\nFROM inventory\nWHERE quantity_on_hand < reorder_level\nORDER BY quantity_on_hand;" });
    }
    if (q.includes('staff') || q.includes('employee') || q.includes('performance') || q.includes('rating')) {
      distilled.push({ label: 'Staff Performance', sql: isRestaurant ? "SELECT s.first_name || ' ' || s.last_name AS name, s.job_title,\n  AVG(r.rating) AS avg_rating, COUNT(r.review_id) AS review_count\nFROM staff s\nLEFT JOIN reviews r ON r.staff_id = s.staff_id\nWHERE s.status = 'active'\nGROUP BY s.staff_id, name, s.job_title\nORDER BY avg_rating DESC NULLS LAST;" : "SELECT name, role, avg_rating FROM staff ORDER BY avg_rating DESC LIMIT 10;" });
    }
    if (q.includes('customer') || q.includes('loyalty') || q.includes('repeat')) {
      distilled.push({ label: 'Customer Summary', sql: isRestaurant ? "SELECT c.first_name || ' ' || c.last_name AS name,\n  COUNT(o.order_id) AS total_orders,\n  SUM(o.total_amount) AS lifetime_value\nFROM customers c\nJOIN orders o ON o.customer_id = c.customer_id\nGROUP BY c.customer_id, name\nORDER BY lifetime_value DESC LIMIT 20;" : "SELECT name, COUNT(*) AS orders, SUM(total_spent) AS lifetime_value\nFROM customers\nGROUP BY name\nORDER BY lifetime_value DESC LIMIT 10;" });
    }
    if (q.includes('menu') || q.includes('item') || q.includes('popular') || q.includes('best seller')) {
      distilled.push({ label: 'Top Menu Items', sql: "SELECT mi.item_name, mi.price, COUNT(oi.order_item_id) AS times_ordered,\n  SUM(oi.quantity * oi.unit_price) AS total_revenue\nFROM menu_items mi\nJOIN order_items oi ON oi.item_id = mi.item_id\nGROUP BY mi.item_id, mi.item_name, mi.price\nORDER BY total_revenue DESC LIMIT 10;" });
    }

    // ── TIER 2: ER Intelligence (schema-aware, uses actual table relationships) ──
    if (q.includes('waste') || q.includes('spoilage') || q.includes('loss')) {
      erIntel.push({ label: 'Food Waste by Item (from inventory_transactions)', sql: "SELECT ii.item_name, ii.category,\n  SUM(it.quantity) AS total_waste_qty,\n  SUM(it.quantity * ii.unit_cost) AS waste_cost\nFROM inventory_transactions it\nJOIN inventory_items ii ON ii.inventory_item_id = it.inventory_item_id\nWHERE it.transaction_type = 'wastage'\nGROUP BY ii.item_name, ii.category\nORDER BY waste_cost DESC;" });
      erIntel.push({ label: 'Waste by Prep Station / Branch', sql: "SELECT b.branch_name,\n  SUM(it.quantity) AS waste_qty,\n  SUM(it.quantity * ii.unit_cost) AS waste_cost\nFROM inventory_transactions it\nJOIN inventory_items ii ON ii.inventory_item_id = it.inventory_item_id\nJOIN branches b ON b.branch_id = it.branch_id\nWHERE it.transaction_type = 'wastage'\nGROUP BY b.branch_name\nORDER BY waste_cost DESC;" });
    }
    if (q.includes('profit') || q.includes('margin') || q.includes('cost')) {
      erIntel.push({ label: 'Menu Item Profitability (recipe cost vs price)', sql: "SELECT mi.item_name, mi.price,\n  SUM(ri.quantity_needed * ii.unit_cost) AS ingredient_cost,\n  mi.price - SUM(ri.quantity_needed * ii.unit_cost) AS margin\nFROM menu_items mi\nJOIN recipes r ON r.menu_item_id = mi.item_id\nJOIN recipe_ingredients ri ON ri.recipe_id = r.recipe_id\nJOIN inventory_items ii ON ii.inventory_item_id = ri.inventory_item_id\nGROUP BY mi.item_id, mi.item_name, mi.price\nORDER BY margin DESC;" });
    }
    if (q.includes('inventory') && (q.includes('detail') || q.includes('value') || q.includes('all'))) {
      erIntel.push({ label: 'Full Inventory with Stock Levels', sql: "SELECT ii.item_name, ii.unit, ii.unit_cost,\n  bi.quantity_on_hand, ii.reorder_level,\n  ROUND(bi.quantity_on_hand * ii.unit_cost, 2) AS inventory_value\nFROM inventory_items ii\nJOIN branch_inventory bi ON bi.inventory_item_id = ii.inventory_item_id\nORDER BY inventory_value DESC;" });
    }
    if (q.includes('order') && (q.includes('kitchen') || q.includes('ticket') || q.includes('prep'))) {
      erIntel.push({ label: 'Kitchen Ticket Performance', sql: "SELECT kt.status, COUNT(*) AS tickets,\n  AVG(EXTRACT(EPOCH FROM (kt.completed_at - kt.created_at))/60) AS avg_minutes\nFROM kitchen_tickets kt\nGROUP BY kt.status\nORDER BY tickets DESC;" });
    }
    if (q.includes('supplier') || q.includes('vendor') || q.includes('purchase') || q.includes('procurement')) {
      erIntel.push({ label: 'Supplier Order Summary', sql: "SELECT s.company_name, COUNT(po.purchase_order_id) AS total_orders,\n  SUM(po.total_amount) AS total_spent\nFROM suppliers s\nJOIN purchase_orders po ON po.supplier_id = s.supplier_id\nGROUP BY s.supplier_id, s.company_name\nORDER BY total_spent DESC;" });
    }
    if (q.includes('reservation') || q.includes('table') || q.includes('seating') || q.includes('booking')) {
      erIntel.push({ label: 'Reservation Status', sql: "SELECT r.status, COUNT(*) AS count,\n  AVG(r.party_size) AS avg_party_size\nFROM reservations r\nWHERE r.reservation_date >= CURRENT_DATE - INTERVAL '30 days'\nGROUP BY r.status\nORDER BY count DESC;" });
    }
    if (q.includes('trend') || q.includes('over time') || q.includes('growth') || q.includes('history')) {
      erIntel.push({ label: 'Daily Revenue Trend', sql: "SELECT DATE(o.order_date) AS day,\n  COUNT(*) AS orders, SUM(o.total_amount) AS revenue\nFROM orders o\nWHERE o.order_date >= CURRENT_DATE - INTERVAL '30 days'\nGROUP BY day\nORDER BY day;" });
    }

    // ── TIER 3: LLM Suggestion (generic — may need validation) ──
    if (q.includes('waste') || q.includes('spoilage')) {
      llmSuggested.push({ label: 'LLM-generated waste analysis', sql: "-- ⚠️ LLM-generated: Validate table names against schema\nSELECT prep_station, SUM(waste_cost) AS total_waste_cost\nFROM food_waste_data\nGROUP BY prep_station\nORDER BY total_waste_cost DESC;" });
    }

    // ── DEFAULT: Always provide at least one Distilled + ER entry for restaurant ──
    if (isRestaurant && distilled.length === 0) {
      distilled.push({ label: 'Revenue by Order Type', sql: "SELECT order_type, COUNT(*) AS orders,\n  SUM(total_amount) AS revenue\nFROM orders\nWHERE order_date >= DATE_TRUNC('month', CURRENT_DATE)\nGROUP BY order_type\nORDER BY revenue DESC;" });
      distilled.push({ label: 'Top Menu Items', sql: "SELECT mi.item_name, mi.price,\n  COUNT(oi.order_item_id) AS times_ordered,\n  SUM(oi.quantity * oi.unit_price) AS total_revenue\nFROM menu_items mi\nJOIN order_items oi ON oi.item_id = mi.item_id\nGROUP BY mi.item_id, mi.item_name, mi.price\nORDER BY total_revenue DESC LIMIT 10;" });
    }
    if (isRestaurant && erIntel.length === 0) {
      erIntel.push({ label: 'Daily Revenue Trend (30 days)', sql: "SELECT DATE(o.order_date) AS day,\n  COUNT(*) AS orders, SUM(o.total_amount) AS revenue\nFROM orders o\nWHERE o.order_date >= CURRENT_DATE - INTERVAL '30 days'\nGROUP BY day\nORDER BY day;" });
      erIntel.push({ label: 'Order Summary with Payments', sql: "SELECT o.order_type, o.order_status,\n  COUNT(*) AS count, SUM(o.total_amount) AS total,\n  SUM(p.amount) AS paid\nFROM orders o\nJOIN payments p ON p.order_id = o.order_id\nGROUP BY o.order_type, o.order_status\nORDER BY total DESC;" });
      erIntel.push({ label: 'Inventory Value by Category', sql: "SELECT ii.category,\n  COUNT(*) AS items,\n  SUM(bi.quantity_on_hand * ii.unit_cost) AS total_value\nFROM inventory_items ii\nJOIN branch_inventory bi ON bi.inventory_item_id = ii.inventory_item_id\nGROUP BY ii.category\nORDER BY total_value DESC;" });
    }
    if (!isRestaurant && distilled.length === 0) {
      distilled.push({ label: 'List All Tables', sql: "SELECT table_name FROM information_schema.tables\nWHERE table_schema = 'public'\nORDER BY table_name;" });
    }
    if (!isRestaurant && erIntel.length === 0) {
      erIntel.push({ label: 'Recent Orders', sql: "SELECT * FROM orders\nORDER BY created_at DESC\nLIMIT 20;" });
    }

    // LLM fallback — always add one
    llmSuggested.push({ label: 'General query (LLM)', sql: "-- ⚠️ LLM-generated: Validate table names against schema\nSELECT table_name FROM information_schema.tables\nWHERE table_schema = 'public' ORDER BY table_name;" });

    // Format the 3-tier response
    const sections = [];
    if (distilled.length > 0) {
      sections.push(`⚡ Distilled SQL Intelligence\n${distilled.map((r, i) => `  ${i + 1}. ${r.label}\n     SQL: ${r.sql}`).join('\n\n')}`);
    }
    if (erIntel.length > 0) {
      sections.push(`🧠 ER Intelligence\n${erIntel.map((r, i) => `  ${i + 1}. ${r.label}\n     SQL: ${r.sql}`).join('\n\n')}`);
    }
    if (llmSuggested.length > 0) {
      sections.push(`🤖 LLM\n${llmSuggested.map((r, i) => `  ${i + 1}. ${r.label}\n     SQL: ${r.sql}`).join('\n\n')}`);
    }

    const allRecs = [...distilled, ...erIntel, ...llmSuggested];
    return {
      answer: `Query: "${query}"\n\n🔍 3-Tier Intelligence Resolution for ${companyName}:\n\n${sections.join('\n\n')}\n\n💡 Tip: Copy SQL from Distilled or ER Intelligence first — these use verified table names. LLM suggestions may reference tables that don't exist in your schema.`,
      tables_queried: isRestaurant ? ['inventory_items', 'branch_inventory', 'inventory_transactions', 'orders', 'menu_items'] : ['business_data'],
      rows_scanned: 0,
      recommended_sql: allRecs,
    };
  };

  // ── Feature flag: dynamic Analytics via Topic Resolver API ──
  // NOTE: HTTPS nginx (port 443) needs /api/ proxy block added before enabling.
  // The host-level nginx handles SSL termination and the /api/ block was added
  // to the HTTP (port 80) config only. Set to true once HTTPS config is updated.
  const USE_DYNAMIC_ANALYTICS = process.env.REACT_APP_DYNAMIC_ANALYTICS === 'true';

  // Legacy product-type-specific analytics menu (used when USE_DYNAMIC_ANALYTICS=false)
  const getOperationsMenu = () => {
    if (product.includes('restaurant') || product.includes('hospitality') || product.includes('food') || product.includes('prab')) {
      return [
        { id: 'revenue', icon: '💰', label: 'Revenue Analysis', query: 'What is our revenue breakdown by order type this month?' },
        { id: 'inventory', icon: '📦', label: 'Inventory Status', query: 'Which inventory items are below reorder point?' },
        { id: 'orders', icon: '🛒', label: 'Order Analytics', query: 'Show me order volume by channel for this week' },
        { id: 'staff', icon: '👤', label: 'Staff Performance', query: 'Which staff members have the highest customer ratings?' },
        { id: 'reservations', icon: '📅', label: 'Reservations', query: 'How many reservations do we have for today?' },
      ];
    }
    if (product.includes('appliance') || product.includes('repair') || _pt.includes('appliance') || _pt.includes('repair')) {
      return [
        { id: 'revenue', icon: '💰', label: 'Service Revenue', query: 'What is our total service revenue this month?' },
        { id: 'orders', icon: '🔧', label: 'Open Orders', query: 'Show all open and in-progress work orders' },
        { id: 'techs', icon: '⭐', label: 'Tech Ratings', query: 'Which technician has the highest customer rating this month?' },
        { id: 'parts', icon: '📦', label: 'Parts Alerts', query: 'Which parts are below reorder level?' },
        { id: 'zip', icon: '📊', label: 'Top Zip Codes', query: 'Top zip codes by service demand in last 90 days' },
      ];
    }
    if (product.includes('hvac') || product.includes('coolbreeze') || _pt.includes('hvac')) {
      return [
        { id: 'calls', icon: '📞', label: 'Service Calls', query: 'What is our average service call revenue this month?' },
        { id: 'techs', icon: '🔧', label: 'Tech Ratings', query: 'Which technician has the highest customer rating?' },
        { id: 'parts', icon: '📦', label: 'Parts Reorder', query: 'How many parts are below reorder point?' },
        { id: 'warranty', icon: '✅', label: 'Warranty Tracker', query: 'Show me warranty expirations in the next 30 days' },
        { id: 'contracts', icon: '📄', label: 'Maintenance Contracts', query: 'How many active maintenance contracts do we have?' },
      ];
    }
    if (product.includes('lawn') || product.includes('landscape') || product.includes('greenthumb')) {
      return [
        { id: 'revenue', icon: '💰', label: 'Revenue by Service', query: 'What is our revenue breakdown by service type?' },
        { id: 'crews', icon: '👤', label: 'Crew Schedule', query: 'Which crew has the most jobs scheduled this week?' },
        { id: 'customers', icon: '👥', label: 'Customer Base', query: 'How many active customers do we have this month?' },
        { id: 'equipment', icon: '🔧', label: 'Equipment Status', query: 'Which equipment needs maintenance?' },
        { id: 'seasonal', icon: '🌿', label: 'Seasonal Forecast', query: 'What is our seasonal demand forecast for next quarter?' },
      ];
    }
    if (product.includes('insurance') || product.includes('safehaven')) {
      return [
        { id: 'premium', icon: '💰', label: 'Premium Revenue', query: 'Total premium revenue across all offices this quarter?' },
        { id: 'claims', icon: '📄', label: 'Claims Analysis', query: 'What is our loss ratio by office?' },
        { id: 'agents', icon: '👤', label: 'Agent Performance', query: 'Compare agent performance across all offices' },
        { id: 'renewals', icon: '🔄', label: 'Policy Renewals', query: 'Which office has the highest policy renewal rate?' },
        { id: 'risk', icon: '⚠️', label: 'Risk Assessment', query: 'Show underwriting risk scores for pending applications' },
      ];
    }
    if (product.includes('plumbing') || product.includes('drainmaster')) {
      return [
        { id: 'network', icon: '💰', label: 'Network Revenue', query: 'Total network revenue this month?' },
        { id: 'jobs', icon: '🔧', label: 'Job Volume', query: 'Franchise vs retail subscriber job volume?' },
        { id: 'leads', icon: '📈', label: 'Lead Distribution', query: 'Lead distribution across network?' },
        { id: 'ratings', icon: '⭐', label: 'Customer Ratings', query: 'Location with best customer rating?' },
        { id: 'billing', icon: '💳', label: 'Franchise Fees', query: 'Show franchise fee billing summary' },
      ];
    }
    if (product.includes('medical') || product.includes('wellness') || product.includes('med')) {
      return [
        { id: 'retention', icon: '📈', label: 'Patient Retention', query: 'What is our patient retention rate by service?' },
        { id: 'rooms', icon: '🏥', label: 'Room Utilization', query: 'What is our treatment room utilization this week?' },
        { id: 'supplies', icon: '📦', label: 'Supply Reorder', query: 'Which medical supplies need reordering?' },
        { id: 'revenue', icon: '💰', label: 'Revenue by Treatment', query: 'What is our revenue by treatment type this month?' },
        { id: 'claims', icon: '📄', label: 'Insurance Claims', query: 'What is our insurance claim processing status?' },
      ];
    }
    if (product.includes('auto') || product.includes('valet') || product.includes('parking')) {
      return [
        { id: 'shifts', icon: '👤', label: 'Shift Optimization', query: 'What is our valet shift optimization this week?' },
        { id: 'ev', icon: '⚡', label: 'EV Charging', query: 'What is our EV charging station utilization and revenue?' },
        { id: 'retrieval', icon: '🚗', label: 'Retrieval Time', query: 'What is our vehicle retrieval time performance?' },
        { id: 'fleet', icon: '🚛', label: 'Fleet Maintenance', query: 'What is our fleet vehicle maintenance status?' },
        { id: 'detail', icon: '✨', label: 'Detail Services', query: 'Show express detail service report' },
      ];
    }
    if (product.includes('logistics') || product.includes('supply chain') || product.includes('freight')) {
      return [
        { id: 'fuel', icon: '⛽', label: 'Route Efficiency', query: 'What is our route fuel efficiency by corridor?' },
        { id: 'drivers', icon: '👤', label: 'Driver Performance', query: 'Show driver performance metrics and safety scores' },
        { id: 'cargo', icon: '📦', label: 'Cargo Capacity', query: 'What is our cargo capacity utilization by vehicle type?' },
        { id: 'sla', icon: '📈', label: 'Delivery SLA', query: 'What is our delivery SLA performance this month?' },
        { id: 'contractors', icon: '👥', label: 'Contractor Pool', query: 'Show independent contractor availability for peak demand' },
      ];
    }
    if (product.includes('real estate') || product.includes('realty') || product.includes('kmd')) {
      return [
        { id: 'listings', icon: '🏠', label: 'Active Listings', query: 'How many active listings do we have and what is the average days on market?' },
        { id: 'transactions', icon: '💰', label: 'Commission Revenue', query: 'What is our total commission revenue this month?' },
        { id: 'rentals', icon: '🏢', label: 'Rental Occupancy', query: 'What is our rental occupancy rate across all properties?' },
        { id: 'agents', icon: '👤', label: 'Agent Performance', query: 'Which agent has the most closings this quarter?' },
        { id: 'leads', icon: '📈', label: 'Lead Pipeline', query: 'Show me client leads by source this month' },
      ];
    }
    if (product.includes('bank') || product.includes('financial') || product.includes('qrm') || product.includes('risk-llm') || _pt.includes('bank') || _pt.includes('financial')) {
      return [
        { id: 'balance', icon: '🏦', label: 'Balance Sheet', query: 'What is our loan-to-deposit ratio?' },
        { id: 'deposits', icon: '💰', label: 'Deposit Stability', query: 'Show me deposit stability scores and decay rates' },
        { id: 'loans', icon: '📄', label: 'Loan Portfolio', query: 'What is our total loan portfolio breakdown by type?' },
        { id: 'cashflow', icon: '💵', label: 'Cashflow Projections', query: 'Show me projected cashflows for next quarter under base scenario' },
        { id: 'risk', icon: '⚠️', label: 'Risk Metrics', query: 'What is our total risk-weighted assets and capital adequacy?' },
        { id: 'nim', icon: '📈', label: 'NIM Analysis', query: 'What is our net interest margin trend?' },
      ];
    }
    if (product.includes('gym') || product.includes('fitness') || _pt.includes('gym') || _pt.includes('fitness')) {
      return [
        { id: 'members', icon: '🏋️', label: 'Memberships', query: 'Show active membership count and revenue by plan type' },
        { id: 'classes', icon: '📅', label: 'Class Schedule', query: 'What classes are scheduled today and what is attendance?' },
        { id: 'renewals', icon: '🔄', label: 'Renewals Due', query: 'Which memberships are expiring this month?' },
        { id: 'trainers', icon: '👤', label: 'Trainer Performance', query: 'Which personal trainers have the highest client retention?' },
        { id: 'revenue', icon: '💰', label: 'Revenue Breakdown', query: 'What is our revenue breakdown by service type this month?' },
      ];
    }
    if (product.includes('grocery') || product.includes('supermarket') || _pt.includes('grocery')) {
      return [
        { id: 'sales', icon: '💰', label: 'Daily Sales', query: "What are today's total sales across all departments?" },
        { id: 'stock', icon: '📦', label: 'Low Stock Alerts', query: 'Which products are below reorder level?' },
        { id: 'top', icon: '⭐', label: 'Top Products', query: 'Show top 10 selling products this week' },
        { id: 'waste', icon: '📊', label: 'Waste & Spoilage', query: 'What is our spoilage rate by department this month?' },
        { id: 'suppliers', icon: '🚚', label: 'Supplier Orders', query: 'Show pending supplier orders and expected delivery dates' },
      ];
    }
    // Generic fallback
    return [
      { id: 'metrics', icon: '📊', label: 'Key Metrics', query: 'What are our key performance metrics today?' },
      { id: 'revenue', icon: '💰', label: 'Revenue', query: 'What is our total revenue this month vs last month?' },
      { id: 'operations', icon: '⚙️', label: 'Operations', query: 'Show operational status summary' },
      { id: 'costs', icon: '💸', label: 'Cost Analysis', query: 'What are our top cost drivers this month?' },
      { id: 'performance', icon: '📈', label: 'Performance', query: 'Show performance trends for the current quarter' },
    ];
  };
  const operationsMenu = getOperationsMenu();

  // LLM query handler — Cache-first, then Backend GenAI, then demo fallback
  const handleGenaiQuery = async (query) => {
    let q = query || genaiQuery;
    if (!q.trim()) return;
    
    // ── Sanitize: replace newlines with spaces (pasted multi-line SQL) ──
    q = q.replace(/[\r\n]+/g, ' ').replace(/\s{2,}/g, ' ').trim();
    
    // ── Strip duplicate SQL instructions — keep only the last one ──
    const instrMatches = q.match(/\[(?:Instruction:\s*)?SQL:[\s\S]*?\]/gi);
    if (instrMatches && instrMatches.length > 1) {
      const baseText = q.replace(/\[(?:Instruction|SQL)[\s\S]*$/i, '').trim();
      q = `${baseText}${instrMatches[instrMatches.length - 1]}`;
    }
    
    // ── Auto-detect raw SQL and wrap as instruction ──
    const trimmed = q.trim();
    const sqlKeywords = /^\s*(SELECT|INSERT|UPDATE|DELETE|WITH|EXPLAIN|SHOW|DESCRIBE)\b/i;
    // Also detect SQL-like patterns: table.column, GROUP BY, ORDER BY, JOIN, WHERE
    const sqlPatterns = /\b(FROM|WHERE|GROUP\s+BY|ORDER\s+BY|JOIN|LIMIT|HAVING|UNION)\b/i;
    const hasInstruction = trimmed.toLowerCase().includes('[instruction:') || trimmed.toLowerCase().includes('[sql:');
    if (!hasInstruction && (sqlKeywords.test(trimmed) || (sqlPatterns.test(trimmed) && /\bSELECT\b/i.test(trimmed)))) {
      q = `[Instruction: SQL: ${trimmed}]`;
    }
    
    setGenaiQuery(q);
    setShowRefinement(false);
    setSystemError(null);

    // The fetch function for the concurrent data engine
    const fetchFn = async (queryText, signal) => {
      const hasSqlInstruction = queryText.toLowerCase().includes('[instruction:') || queryText.toLowerCase().includes('[sql:');
      const hasDatabase = !!resolvedDbConfig;

      // ── ALL QUERIES: route to backend (live database + CDG-SQL + ZDDV) ──
      try {
        const res = await fetch(`/v1/clients/${clientId}/genai/query`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            query: queryText,
            llm_provider: llmPrefs.providerId || 'gemini',
            product_type: clientConfig?.product_type || '',
            db_config: resolvedDbConfig || null,
            force_refresh: true,  // Always refresh from DB during testing
          }),
          signal,
        });
        if (res.ok) {
          const ct = res.headers.get('content-type') || '';
          if (ct.includes('application/json')) {
            const data = await res.json();
            if (data.error) { setSystemError(data.error); }
            data._source = data.source || 'backend';
            return data;
          }
        }
        if (res.status >= 500) {
          setSystemError(`Server error (${res.status}).`);
        }
      } catch (err) {
        if (err.name === 'AbortError') throw err;
      }

      // ── FALLBACK: database configured but backend not reachable ──
      if (hasDatabase) {
        return {
          answer: `🔴 Database not reachable\n\nDatabase: ${resolvedDbConfig.db}\nHost: ${resolvedDbConfig.host}:${resolvedDbConfig.port || '5432'}\nEngine: ${resolvedDbConfig.engine || 'PostgreSQL'}\nUser: ${resolvedDbConfig.user || 'pnp_readonly'}\n\nThe GenAI backend server is not running. All queries require the backend to connect to your database with read-only access.\n\n💡 To fix:\nssh -i server/deploy/pnp-infra-key.pem ubuntu@3.219.218.53\nsudo systemctl start pnp-infra\ncurl http://localhost:8000/health`,
          tables_queried: [],
          rows_scanned: 0,
          _source: 'db_unreachable',
        };
      }

      // ── NO DATABASE: inform user to configure database ──
      return { answer: '⚠️ No database configured for this client.\n\nTo enable live intelligence queries, configure your database via Update Client or contact your platform operator.\n\nAll queries route through the backend GenAI service with read-only database access.', tables_queried: [], rows_scanned: 0, _source: 'no_db' };
    };

    // Execute with SWR caching — shows cached result instantly, revalidates in background
    // During testing: always force refresh from database
    const result = await dataEngine.execute(q, fetchFn, { forceRefresh: true });
    if (result) {
      setGenaiResult(result);
      
      // ── CDG-SQL: Set state when query has executed SQL (success or error) ──
      // Also trigger L1 when backend returns error/zero-data without SQL (COA-GPT failure, ZDDV)
      const executedSql = result.executed_sql || result.sql_used || result.sql || null;
      const isBackendResponse = result._source !== 'demo_fallback' && result._source !== 'error' && result._source !== 'db_unreachable' && result._source !== 'no_db';
      const hasCoaGptError = isBackendResponse && (
        (result.answer && result.answer.includes('could not resolve')) ||
        (result.answer && result.answer.includes('COA-GPT')) ||
        (result.error)
      );
      const isZeroData = isBackendResponse && !result.error && (result.rows_scanned === 0 || result.rows_scanned === undefined) && !executedSql;

      if (executedSql && isBackendResponse) {
        if (result.rows_scanned > 0) {
          // Successful query with data — offer to confirm as permanent
          setCdgSqlState({
            status: 'success',
            next_step: 'l2_confirm',
            sql_used: executedSql,
            source: result.source || 'genai_direct',
            session_id: `cdg-${Date.now().toString(36)}`,
            latency_ms: result.latency_ms || 0,
            model: result.model || '',
            original_query: q,
          });
        } else if (result.error || result.rows_scanned === 0) {
          // SQL error or zero rows — show L1 choice
          setCdgSqlState({
            status: 'error',
            next_step: 'l1_choice',
            sql_used: executedSql,
            error: result.error || 'Query returned 0 rows — SQL may need adjustment',
            source: result.source || 'genai_direct',
            session_id: `cdg-${Date.now().toString(36)}`,
            available_tables: result.tables_queried || [],
            latency_ms: result.latency_ms || 0,
            model: result.model || '',
            original_query: q,
          });
        }
      } else if (hasCoaGptError || isZeroData) {
        // COA-GPT failure or ZDDV with no SQL — show L1 choice so user can proceed
        setCdgSqlState({
          status: 'error',
          next_step: 'l1_choice',
          sql_used: executedSql || '',
          error: result.error || (hasCoaGptError ? 'COA-GPT Level 1 could not resolve — provide SQL or let CDG-SQL generate' : 'Zero data returned — SQL may need adjustment or table may be empty'),
          source: result.source || 'coa_gpt_failed',
          session_id: `cdg-${Date.now().toString(36)}`,
          available_tables: result.tables_queried || result.coa_gpt?.l2_context?.available_tables || [],
          latency_ms: result.latency_ms || result.duration_ms || 0,
          model: result.model || '',
          original_query: q,
        });
        // Auto-scroll CDG-SQL panel into view after render
        setTimeout(() => {
          const cdgPanel = document.querySelector('[data-cdg-sql-panel]');
          if (cdgPanel) cdgPanel.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      } else {
        setCdgSqlState(null);
      }
      
      // ── Auto-save successful SQL to Custom Intelligence ──
      // REMOVED: Main query items should NOT auto-save to Custom Intelligence.
      // Only items explicitly added via Custom Intelligence panel or adopted from AI Top-10 are saved.
    }
  };

  // Refinement: re-run query with SQL/NLP instructions appended
  const handleRefinement = () => {
    if (!refinementInput.trim() || !genaiQuery.trim()) return;
    const refined = `${genaiQuery}\n\n[Instruction: ${refinementInput.trim()}]`;
    setRefinementInput('');
    setShowRefinement(false);
    handleGenaiQuery(refined);
  };

  // Comm-AI action handler
  const handleAgenticAction = async () => {
    if (!agenticAction.trim()) return;
    setLoading(true);
    setAgenticResult(null);
    const action = agenticAction;
    try {
      const res = await fetch(`/v1/clients/${clientId}/agentic/dispatch`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      if (res.ok) {
        const ct = res.headers.get('content-type') || '';
        if (ct.includes('application/json')) { setAgenticResult(await res.json()); setLoading(false); return; }
      }
    } catch { /* fall through to demo */ }
    const isA2A = action.toLowerCase().match(/find|search|discover|provider/);
    const isMCP = action.toLowerCase().match(/send|sign|agreement|contract/);
    setAgenticResult({
      status: 'dispatched',
      protocol: isA2A ? 'A2A' : isMCP ? 'MCP' : 'A2A',
      message: isA2A
        ? `A2A discovery.find_provider dispatched.\n\nSearching service directory for: "${action}"\n\nAgent Card broadcast via JSON-RPC 2.0 with EIN-Hash verification.\nResults will include: agent_id, capabilities, a2a_endpoint, availability, pricing.\n\nAll interactions logged to public_audit_log.`
        : `MCP tool invocation dispatched.\n\nAction: "${action}"\n\nSignWell API called with client-specific tokens from Secrets Manager.\nAgreement template populated from AgenticAI.pdf configuration.\nRecipient will receive e-signature request via email.\n\nAll actions logged to public_audit_log.`,
      audit_id: `req-${Date.now().toString(36)}`,
    });
    setLoading(false);
  };

  // ── Custom Intelligence data loading ──
  const fetchCustomQuestions = async () => {
    try {
      const res = await fetch(`/v1/clients/${clientId}/custom-intelligence`);
      if (res.ok) { const d = await res.json(); setCustomQuestions(d.questions || []); return; }
    } catch { /* fall through */ }
    // Load from persisted LLM preferences first, then fallback to legacy key
    const persisted = llmPrefs.customQuestions;
    if (persisted && persisted.length > 0) {
      setCustomQuestionsLocal(persisted);
    } else {
      const legacy = JSON.parse(localStorage.getItem(`ci_${clientId}`) || '[]');
      setCustomQuestionsLocal(legacy);
      if (legacy.length > 0) llmPrefs.setCustomQuestions(legacy);
    }
  };

  const fetchSharedQuestions = async () => {
    const btype = ['restaurant','lawn','hvac','insurance','plumbing'].find(k => product.includes(k)) || 'general';
    try {
      const res = await fetch(`/v1/clients/${clientId}/custom-intelligence/shared?business_type=${btype}`);
      if (res.ok) { const d = await res.json(); setSharedQuestions(d.questions || []); return; }
    } catch { /* fall through */ }
    const demoShared = {
      restaurant: [{ question_id: 'shared-1', question: 'What is our food cost percentage by menu category?', shared_by_name: 'Sunrise-Bistro', use_count: 34 }],
      lawn: [{ question_id: 'shared-3', question: 'What is our fuel cost per job by zone?', shared_by_name: 'EverGreen-Lawns', use_count: 19 }],
      hvac: [{ question_id: 'shared-4', question: 'What is our first-time fix rate by technician?', shared_by_name: 'AirFlow-HVAC', use_count: 22 }],
      insurance: [{ question_id: 'shared-5', question: 'What is our claims-to-premium ratio by policy type?', shared_by_name: 'TrustShield-Insurance', use_count: 31 }],
      plumbing: [{ question_id: 'shared-6', question: 'What is our average time-to-dispatch by job priority?', shared_by_name: 'PipePro-Plumbing', use_count: 15 }],
    };
    setSharedQuestions(demoShared[btype] || []);
  };

  // Handle LLM provider change — clear results and refresh
  const handleProviderChange = (newProviderId) => {
    llmPrefs.setProvider(newProviderId);
    setGenaiResult(null);
    setGenaiQuery('');
    setSystemError(null);
  };

  // eslint-disable-next-line
  React.useEffect(() => { fetchCustomQuestions(); fetchSharedQuestions(); }, []);

  // Only LLM and Comm-AI tabs — RAG AI (CEO Directives) is hidden
  const _ptLabel = (key) => getProductTypeLabel(clientConfig?.product_type, key);
  const tabs = [
    { id: 'overview', icon: '🏠', label: 'Intelligence Governance' },
    { id: 'genai', icon: UI.GENAI_ICON, label: _ptLabel('GENAI_TAB') },
    { id: 'agentic', icon: UI.AGENTIC_ICON, label: _ptLabel('AGENTIC_TAB') },
  ];

  const cardStyle = { background: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #e8e8e8', marginBottom: '16px' };
  const inputStyle = { width: '100%', padding: '12px 14px', border: '1px solid #ddd', borderRadius: '8px', fontSize: '14px', boxSizing: 'border-box' };
  const btnPrimary = { padding: '12px 24px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer' };
  const resultBox = { background: '#f5f7fa', padding: '16px', borderRadius: '8px', marginTop: '12px', fontSize: '14px', lineHeight: '1.6', whiteSpace: 'pre-wrap' };

  return (
    <div style={{ padding: '0' }}>
      {/* App Header */}
      <div style={{ background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)', color: '#fff', padding: '20px 24px', borderRadius: '12px', marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '20px' }}>
            <span title={dbStatus.tooltip} style={{ color: dotColor, cursor: 'help', marginRight: '8px', fontSize: '14px' }}>●</span>
            {companyName}
          </h2>
          <p style={{ margin: '4px 0 0', opacity: 0.7, fontSize: '13px' }}>
            {clientConfig?.product_name || 'AI-Powered Operations'} • {tenantType.toUpperCase()} • {clientConfig?.plan || 'starter'} • {dotLabel === 'client_prod' ? '🔵 Client DB' : hasDb ? '🟢 Database Connected' : '🟠 Demo Mode'}
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          {/* LLM Provider Selector */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', position: 'relative' }}>
            <span style={{ fontSize: '12px', opacity: 0.7 }}>LLM:</span>
            <select
              value={llmPrefs.providerId}
              onChange={e => handleProviderChange(e.target.value)}
              style={{
                padding: '6px 10px', background: 'rgba(255,255,255,0.15)', color: '#fff',
                border: '1px solid rgba(255,255,255,0.3)', borderRadius: '6px', fontSize: '12px',
                cursor: 'pointer', outline: 'none',
              }}
              aria-label="Select LLM provider"
            >
              {llmPrefs.providers.map(p => (
                <option key={p.id} value={p.id} style={{ color: '#333', background: '#fff' }}>
                  {p.icon} {p.label}
                </option>
              ))}
            </select>
            {/* Warning when ChatGPT is selected — frequent timeout risk */}
            {llmPrefs.provider.warning && (
              <span style={{
                fontSize: '10px', color: '#FFD54F', background: 'rgba(255,152,0,0.15)',
                padding: '2px 8px', borderRadius: '4px', border: '1px solid rgba(255,152,0,0.3)',
                whiteSpace: 'nowrap',
              }} title={llmPrefs.provider.warning}>
                ⚠️ {llmPrefs.provider.warning}
              </span>
            )}
          </div>
          <button onClick={onBack} style={{ padding: '8px 16px', background: 'rgba(255,255,255,0.15)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px', cursor: 'pointer', fontSize: '13px' }}>
            ← Back to Operator
          </button>
        </div>
      </div>

      {/* License Payment Banner */}
      {showLicenseBanner && (() => {
        const lic = getLicenseInfo();
        if (!lic) return null; // No license info — hide banner entirely
        return (
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '10px 16px', marginBottom: '12px', borderRadius: '8px',
            background: lic.status === 'PAID' ? 'rgba(76,175,80,0.1)' : lic.status === 'PENDING' ? 'rgba(255,152,0,0.1)' : 'rgba(244,67,54,0.1)',
            border: `1px solid ${lic.color}40`,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <span style={{ fontSize: '16px' }}>{lic.icon}</span>
              <div>
                <div style={{ fontSize: '12px', fontWeight: 700, color: lic.color }}>
                  Subscription: {lic.status} • Plan: {(lic.plan || 'starter').toUpperCase()}
                </div>
                <div style={{ fontSize: '11px', color: '#666' }}>
                  {lic.status === 'PAID' && lic.daysLeft > 0 && `Next payment due: ${lic.deadline} (${lic.daysLeft} days)`}
                  {lic.status === 'PAID' && lic.daysLeft <= 0 && 'Payment processed for this period'}
                  {lic.status === 'PENDING' && `Grace period: ${lic.daysLeft} days remaining until ${lic.deadline}`}
                  {lic.status === 'OVERDUE' && 'Payment overdue — write access restricted. Please update payment.'}
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {lic.status !== 'PAID' && (
                <button style={{
                  padding: '5px 12px', background: lic.color, color: '#fff', border: 'none',
                  borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                }}>Update Payment</button>
              )}
              <button onClick={() => setShowLicenseBanner(false)} style={{
                background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', color: '#999',
              }}>✕</button>
            </div>
          </div>
        );
      })()}

      {/* Tab Navigation */}
      <div style={{ display: 'flex', gap: '4px', marginBottom: '20px', background: '#f0f0f0', padding: '4px', borderRadius: '10px' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)} style={{
            flex: 1, padding: '10px', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '13px', fontWeight: 600,
            background: activeTab === tab.id ? '#fff' : 'transparent',
            color: activeTab === tab.id ? '#1a1a2e' : '#666',
            boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
          }}>
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div>
          {/* Only LLM + Comm-AI cards — RAG is hidden (CEO Directives) */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '20px' }}>
            <div style={{ ...cardStyle, textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveTab('genai')}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{UI.GENAI_ICON}</div>
              <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>{UI.GENAI} — Operations BI</div>
              <div style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>● configured • {llmPrefs.provider.icon} {llmPrefs.provider.label}</div>
              <div style={{ color: '#888', fontSize: '12px' }}>Private DB queries with PII redaction. Dynamic analytics powered by Topic Resolver.</div>
            </div>
            <div style={{ ...cardStyle, textAlign: 'center', cursor: 'pointer' }} onClick={() => setActiveTab('agentic')}>
              <div style={{ fontSize: '32px', marginBottom: '8px' }}>{UI.AGENTIC_ICON}</div>
              <div style={{ fontWeight: 700, fontSize: '15px', marginBottom: '4px' }}>{UI.AGENTIC_AI} — Public Agent</div>
              <div style={{ color: '#4CAF50', fontSize: '12px', fontWeight: 600, marginBottom: '6px' }}>● configured</div>
              <div style={{ color: '#888', fontSize: '12px' }}>MCP tools + A2A messaging. SignWell, vendor search, agent negotiation.</div>
            </div>
          </div>

          <div style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>⚡ Quick Operations (Analytics Topics)</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '12px' }}>Click any operation to query your database. Analytics topics are resolved dynamically via the Topic Resolver API.</p>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '8px' }}>
              {customAnalyticsItems.map(op => {
                const src = getQuerySource(op.query, clientConfig?.product_type);
                return (
                <button key={op.id} onClick={() => { setActiveTab('genai'); setTimeout(() => handleGenaiQuery(op.query), 100); }} style={{
                  padding: '12px', background: '#f5f7fa', border: '1px solid #e8e8e8', borderRadius: '8px',
                  cursor: 'pointer', textAlign: 'left', fontSize: '13px',
                }}>
                  <span style={{ fontSize: '18px' }}>{op.icon}</span>
                  <span title={getSourceTooltip(src)} style={{ float: 'right', cursor: 'help', fontSize: '14px', filter: src === 'distilled' ? 'none' : 'grayscale(0.5)' }}>💡</span>
                  <div style={{ fontWeight: 600, marginTop: '4px' }}>{op.label}</div>
                  <div style={{ fontSize: '10px', color: getSourceColor(src), marginTop: '2px' }}>{src === 'distilled' ? '⚡ Distilled-GenAI' : '🤖 LLM'}</div>
                </button>
                );
              })}
              {customAnalyticsItems.length === 0 && (
                <div style={{ gridColumn: '1 / -1', padding: '16px', color: '#888', fontSize: '13px', textAlign: 'center' }}>
                  📊 Use the Analytics fieldset above to explore dynamic topics, or add custom queries via the Analytics ▼ Menu.
                </div>
              )}
            </div>
          </div>

          {tenantType === 'parent' && (
            <div style={{ ...cardStyle, borderColor: '#bbdefb' }}>
              <h3 style={{ marginTop: 0 }}>🏗️ Chain / Franchise Overview</h3>
              <p style={{ color: '#666', fontSize: '13px' }}>As a PARENT tenant, you have visibility across all sub-client locations via the PARENT security view.</p>
            </div>
          )}

          {/* ── Policy Governance Toggle Panel ─────────────────────────────── */}
          <PolicyGovernancePanel clientId={clientId} productType={clientConfig?.product_type} />

          <div style={{ ...cardStyle, background: '#f5f5f5', borderColor: '#e0e0e0' }}>
            <p style={{ margin: 0, fontSize: '12px', color: '#888' }}>🛡️ CEO Directives ({UI.RAG_AI}) are active as guardrails — they enforce boundaries on all {UI.GENAI} queries but are not directly accessible to users.</p>
          </div>
        </div>
      )}

      {/* LLM Tab */}
      {activeTab === 'genai' && (
        <div>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
              {UI.GENAI_ICON} {UI.GENAI} — Operations Intelligence
              <span style={{ fontSize: '11px', padding: '2px 8px', background: '#e3f2fd', color: '#1565C0', borderRadius: '10px', fontWeight: 600 }}>
                {llmPrefs.provider.icon} {llmPrefs.provider.label}
              </span>
            </h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
              Private BI queries against your operational database. Read-only, VPC-peered, PII-redacted.
              Menu items derived from {UI.GENAI_FULL}. Boundaries enforced by CEO Directives.
            </p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', marginBottom: '16px' }}>
              {/* ══════════════════════════════════════════════════════════════
                   Group-1: Traditional Analytics (parent fieldset wrapping Daily + Analytics)
                 ══════════════════════════════════════════════════════════════ */}
              <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', margin: 0, padding: '0 4px 2px 0', border: '1.5px solid #B0BEC5', borderRadius: '20px', background: '#ECEFF1', alignSelf: 'stretch' }}>
                <legend style={{ fontSize: '11px', fontWeight: 700, color: '#555', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '0 6px', marginLeft: '8px' }}>{(() => {
                  // Universal Product-Type Analytics label — derived from metadata or product type name
                  const ANALYTICS_LABELS = {
                    'restaurant': 'Food', 'hospitality': 'Food', 'food': 'Food',
                    'grocery': 'Inventory', 'supermarket': 'Inventory',
                    'bank': 'Financial', 'financial': 'Financial',
                    'construction': 'Materials', 'material': 'Materials',
                    'china-us': 'GeoPulse', 'strategic-intel': 'GeoPulse',
                    'healthcare': 'Healthcare', 'clinical': 'Healthcare',
                    'manufacturing': 'Manufacturing', 'predictivemaintenance': 'Predictive Maintenance',
                    'gym': 'Fitness', 'fitness': 'Fitness',
                    'realestate': 'Realty', 'realty': 'Realty',
                    'insurance': 'Insurance',
                    'procurement': 'Procurement', 'procureguard': 'Procurement',
                    'it-consult': 'IT Consulting',
                    'logistics': 'Logistics',
                    'hvac': 'HVAC',
                    'plumbing': 'Plumbing',
                    'lawn': 'Lawn & Landscape',
                    'valet': 'Valet', 'auto': 'Valet',
                    'salon': 'Salon',
                    'education': 'Education',
                    'medical': 'Medical', 'wellness': 'Medical',
                    'utilities': 'Grid', 'energy': 'Energy',
                    'appliance': 'Appliance Repair',
                    'power': 'Power Quality', 'powerquality': 'Power Quality',
                    'water': 'Water Intelligence', 'pfas': 'Water Intelligence',
                    'mall': 'Mall', 'shopping': 'Mall',
                    'sports': 'Sports',
                  };
                  const matchedKey = Object.keys(ANALYTICS_LABELS).find(k => _pt.includes(k));
                  const label = matchedKey ? ANALYTICS_LABELS[matchedKey] : (clientConfig?.product_type || 'Business').split('.')[0].split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
                  const icon = _pt.includes('manufacturing') || _pt.includes('predictivemaintenance') ? '🏭' : '📊';
                  return `${icon} ${label} Analytics`;
                })()}</legend>
              {/* ── GROUP 1: Daily ── */}
              {(() => {
                const builtInDaily = (() => {
                  if (_pt.includes('realestate') || _pt.includes('realty') || product.includes('realty') || product.includes('kmd')) {
                    return [
                      { label: 'Active Listings', query: 'Show all active listings with prices and status' },
                      { label: 'New Offers', query: 'What offers were received this week?' },
                      { label: 'Showings', query: 'What showings are scheduled today?' },
                      { label: 'Lease Expirations', query: 'Show upcoming lease expirations in the next 60 days' },
                      { label: 'Lead Pipeline', query: 'Show me client leads by source this month' },
                    ];
                  }
                  if (_pt.includes('bank') || _pt.includes('financial') || product.includes('bank') || product.includes('financial')) {
                    return [
                      { label: 'Balance Sheet', query: 'Show balance sheet summary' },
                      { label: 'Loan Portfolio', query: 'What is our current loan-to-deposit ratio?' },
                      { label: 'Risk Metrics', query: 'Show risk-weighted assets by category' },
                      { label: 'NIM Trend', query: 'What is our net interest margin trend?' },
                      { label: 'Deposit Stability', query: 'Show me deposit stability scores and decay rates' },
                    ];
                  }
                  if (_pt.includes('procurement') || _pt.includes('procureguard') || product.includes('procurement')) {
                    return [
                      { label: 'Active Contracts', query: 'Show all active contracts by department' },
                      { label: 'Pending Tenders', query: 'What tenders are currently open for bidding?' },
                      { label: 'Compliance', query: 'Show compliance check status for this month' },
                      { label: 'Vendor Ratings', query: 'Which vendors have the best delivery and quality ratings?' },
                      { label: 'Budget Status', query: 'What is our budget utilization by department this quarter?' },
                    ];
                  }
                  if (_pt.includes('gym') || _pt.includes('fitness') || product.includes('gym') || product.includes('fitness')) {
                    return [
                      { label: 'Memberships', query: 'Show active membership count and revenue by plan type' },
                      { label: 'Class Schedule', query: 'What classes are scheduled today and what is attendance?' },
                      { label: 'Renewals Due', query: 'Which memberships are expiring this month?' },
                      { label: 'Trainer Stats', query: 'Which personal trainers have the highest client retention?' },
                      { label: 'Revenue', query: 'What is our revenue breakdown by service type this month?' },
                    ];
                  }
                  if (_pt.includes('grocery') || _pt.includes('supermarket') || product.includes('grocery') || product.includes('supermarket')) {
                    return [
                      { label: 'Daily Sales', query: 'What are today\'s total sales across all departments?' },
                      { label: 'Low Stock', query: 'Which products are below reorder level?' },
                      { label: 'Top Products', query: 'Show top 10 selling products this week' },
                      { label: 'Waste Report', query: 'What is our spoilage rate by department this month?' },
                      { label: 'Supplier Orders', query: 'Show pending supplier orders and expected delivery dates' },
                    ];
                  }
                  if (_pt.includes('insurance') || product.includes('insurance') || product.includes('safehaven')) {
                    return [
                      { label: 'Premium Revenue', query: 'Total premium revenue across all offices this quarter?' },
                      { label: 'Claims Status', query: 'What is our loss ratio by office?' },
                      { label: 'Renewals', query: 'Which office has the highest policy renewal rate?' },
                      { label: 'Agent Performance', query: 'Compare agent performance across all offices' },
                      { label: 'Risk Assessment', query: 'Show underwriting risk scores for pending applications' },
                    ];
                  }
                  if (_pt.includes('hvac') || product.includes('hvac') || product.includes('coolbreeze')) {
                    return [
                      { label: 'Service Calls', query: 'What is our average service call revenue this month?' },
                      { label: 'Tech Ratings', query: 'Which technician has the highest customer rating?' },
                      { label: 'Parts Reorder', query: 'How many parts are below reorder point?' },
                      { label: 'Warranties', query: 'Show me warranty expirations in the next 30 days' },
                      { label: 'Contracts', query: 'How many active maintenance contracts do we have?' },
                    ];
                  }
                  if (_pt.includes('plumbing') || product.includes('plumbing') || product.includes('drainmaster')) {
                    return [
                      { label: 'Network Revenue', query: 'Total network revenue this month?' },
                      { label: 'Job Volume', query: 'Franchise vs retail subscriber job volume?' },
                      { label: 'Lead Distribution', query: 'Lead distribution across network?' },
                      { label: 'Customer Ratings', query: 'Location with best customer rating?' },
                      { label: 'Franchise Fees', query: 'Show franchise fee billing summary' },
                    ];
                  }
                  if (_pt.includes('medical') || _pt.includes('wellness') || product.includes('medical') || product.includes('wellness') || product.includes('med')) {
                    return [
                      { label: 'Patient Retention', query: 'What is our patient retention rate by service?' },
                      { label: 'Room Utilization', query: 'What is our treatment room utilization this week?' },
                      { label: 'Supply Reorder', query: 'Which medical supplies need reordering?' },
                      { label: 'Revenue', query: 'What is our revenue by treatment type this month?' },
                      { label: 'Claims', query: 'What is our insurance claim processing status?' },
                    ];
                  }
                  if (_pt.includes('logistics') || product.includes('logistics') || product.includes('freight')) {
                    return [
                      { label: 'Route Efficiency', query: 'What is our route fuel efficiency by corridor?' },
                      { label: 'Driver Performance', query: 'Show driver performance metrics and safety scores' },
                      { label: 'Cargo Capacity', query: 'What is our cargo capacity utilization by vehicle type?' },
                      { label: 'Delivery SLA', query: 'What is our delivery SLA performance this month?' },
                      { label: 'Contractors', query: 'Show independent contractor availability for peak demand' },
                    ];
                  }
                  if (_pt.includes('lawn') || product.includes('lawn') || product.includes('landscape') || product.includes('greenthumb')) {
                    return [
                      { label: 'Revenue', query: 'What is our revenue breakdown by service type?' },
                      { label: 'Crew Schedule', query: 'Which crew has the most jobs scheduled this week?' },
                      { label: 'Customer Base', query: 'How many active customers do we have this month?' },
                      { label: 'Equipment', query: 'Which equipment needs maintenance?' },
                      { label: 'Seasonal Forecast', query: 'What is our seasonal demand forecast for next quarter?' },
                    ];
                  }
                  if (_pt.includes('valet') || _pt.includes('auto') || product.includes('valet') || product.includes('auto') || product.includes('parking')) {
                    return [
                      { label: 'Shift Optimization', query: 'What is our valet shift optimization this week?' },
                      { label: 'EV Charging', query: 'What is our EV charging station utilization and revenue?' },
                      { label: 'Retrieval Time', query: 'What is our vehicle retrieval time performance?' },
                      { label: 'Fleet Status', query: 'What is our fleet vehicle maintenance status?' },
                      { label: 'Detail Services', query: 'Show express detail service report' },
                    ];
                  }
                  if (_pt.includes('it-consult') || product.includes('it consult') || product.includes('consulting')) {
                    return [
                      { label: 'Utilization', query: 'What is our current resource utilization rate by department?' },
                      { label: 'Active Projects', query: 'Show all active projects with status and budget utilization' },
                      { label: 'Bench Strength', query: 'How many consultants are currently on bench and for how long?' },
                      { label: 'Billing', query: 'What is our total billable revenue this month by client?' },
                      { label: 'Timesheets', query: 'Show timesheet submission compliance rate this week' },
                    ];
                  }
                  if (_pt.includes('china-us') || _pt.includes('strategic-intel') || _pt.includes('geopulse')) {
                    return [
                      { label: 'Daily Pulse', query: 'Show today\'s China-US geopolitical events with severity scores' },
                      { label: 'Corporate Exposure', query: 'Which companies have the highest exposure to today\'s events?' },
                      { label: 'Sector Scores', query: 'Show current sector impact scores across all 11 GICS sectors' },
                      { label: 'Forecasts', query: 'What are the current Bull/Base/Bear scenario probabilities?' },
                      { label: 'Event Timeline', query: 'Show the event timeline for the past 7 days with classifications' },
                    ];
                  }
                  if (_pt.includes('restaurant') || _pt.includes('hospitality') || _pt.includes('food') || product.includes('restaurant') || product.includes('hospitality')) {
                    return [
                      { label: 'Reservations', query: 'What are today\'s reservations and table availability?' },
                      { label: 'Today\'s Sales', query: 'What were today\'s total sales so far?' },
                      { label: 'Staff Schedule', query: 'Who is scheduled to work today?' },
                      { label: 'Inventory Check', query: 'What inventory items need attention today?' },
                      { label: 'Order Summary', query: 'Show me today\'s order summary by channel' },
                    ];
                  }
                  if (_pt.includes('manufacturing') || _pt.includes('material') || _pt.includes('construction') || _pt.includes('margin-intelligence') || product.includes('manufacturing') || product.includes('material') || product.includes('construction')) {
                    return [
                      { label: 'Margin Analysis', query: 'What is our gross margin percentage by product line this month?' },
                      { label: 'Material Costs', query: 'Which material categories have the highest cost increase this quarter?' },
                      { label: 'Supplier Performance', query: 'Show supplier delivery performance and cost trends' },
                      { label: 'Production Efficiency', query: 'What is our production yield rate by line this week?' },
                      { label: 'Inventory Turnover', query: 'What is our inventory turnover ratio by material category?' },
                    ];
                  }
                  if (_pt.includes('energy') || _pt.includes('utilities') || _pt.includes('power') || product.includes('energy') || product.includes('utilities') || product.includes('power')) {
                    return [
                      { label: 'Grid Status', query: 'What is our current grid load vs capacity?' },
                      { label: 'Outage Report', query: 'Show active outages and restoration progress' },
                      { label: 'Energy Cost', query: 'What is our cost per kWh trend this month?' },
                      { label: 'Peak Demand', query: 'What are peak demand patterns this week?' },
                      { label: 'Equipment Health', query: 'Which assets have pending maintenance alerts?' },
                    ];
                  }
                  if (_pt.includes('healthcare') || _pt.includes('clinical') || product.includes('hospital') || product.includes('clinical') || product.includes('healthcare')) {
                    return [
                      { label: 'Wait Times', query: 'What is our average patient wait time this week by provider?' },
                      { label: 'Same-Day Access', query: 'What percentage of appointments are same-day access this month?' },
                      { label: 'Claims', query: 'Show claim denial rate and top denial reasons this month' },
                      { label: 'Supply Risk', query: 'Which inventory items are below par level or expiring soon?' },
                      { label: 'Referrals', query: 'What is our average referral completion time by specialty?' },
                    ];
                  }
                  // Default: Generic menu items derived from product type context
                  return [
                    { label: 'Key Metrics', query: 'What are our key performance metrics today?' },
                    { label: 'Revenue', query: 'What is our total revenue this month vs last month?' },
                    { label: 'Operations', query: 'Show operational status summary' },
                    { label: 'Cost Analysis', query: 'What are our top cost drivers this month?' },
                    { label: 'Performance', query: 'Show performance trends for the current quarter' },
                  ];
                })();
                // Merge built-in + custom, deduplicate by label
                const seen = new Set();
                const allDaily = [...builtInDaily, ...customDailyItems].filter(d => {
                  const key = d.label.toLowerCase().trim();
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                // Exclude the current button item from the menu
                const menuItems = allDaily.filter(d => {
                  const key = d.label.toLowerCase().trim();
                  return key !== dailyBtn1Label.toLowerCase().trim() && key !== dailyBtn2Label.toLowerCase().trim() && key !== dailyBtn3Label.toLowerCase().trim();
                });
                return (
                  <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '0 4px 0 0', border: '1.5px solid #FFD54F', borderRadius: '20px', background: '#FFFDE7', position: 'relative' }}>
                    <legend style={{ fontSize: '10px', fontWeight: 700, color: '#F57F17', padding: '0 6px', marginLeft: '8px' }}>📅 Daily
                      <button onClick={(e) => { e.stopPropagation(); setShowDailyConfig(!showDailyConfig); setShowDailyMenu(false); }}
                        title="Configure visible button count (2 or 3)"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 2px', color: '#F57F17', verticalAlign: 'middle' }}
                      >⚙️</button>
                    </legend>
                    {showDailyConfig && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 110, background: '#fff', border: '1px solid #FFD54F', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 12px', marginTop: '4px', minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>Quick Buttons</div>
                        {[2, 3].map(n => (
                          <label key={n} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '3px 0', fontSize: '11px', color: '#555' }}>
                            <input type="radio" name="dailyBtnCount" checked={dailyBtnCount === n} onChange={() => {
                              setDailyBtnCount(n);
                              localStorage.setItem(`pnp_daily_btncount_${clientId}`, n);
                              setShowDailyConfig(false);
                            }} style={{ margin: 0 }} />
                            {n} buttons
                          </label>
                        ))}
                      </div>
                    )}
                    <button onClick={() => { setShowDailyMenu(!showDailyMenu); setShowDailyConfig(false); }}
                      style={{ padding: '4px 8px', border: 'none', fontSize: '11px', background: 'transparent', cursor: 'pointer', color: '#333' }}
                    >{showDailyMenu ? '▲' : '▼'} Menu</button>
                    {showDailyMenu && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #FFD54F', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '220px', marginTop: '4px' }}>
                        {menuItems.map((d, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                            <button onClick={() => { handleGenaiQuery(d.query); setShowDailyMenu(false); }}
                              style={{ flex: 1, padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', textAlign: 'left', color: '#333' }}
                            >{d.label}</button>
                            <button onClick={() => {
                              setDailyBtn3Label(dailyBtn2Label); setDailyBtn3Query(dailyBtn2Query);
                              localStorage.setItem(`pnp_daily_lbl3_${clientId}`, dailyBtn2Label);
                              localStorage.setItem(`pnp_daily_q3_${clientId}`, dailyBtn2Query);
                              setDailyBtn2Label(dailyBtn1Label); setDailyBtn2Query(dailyBtn1Query);
                              localStorage.setItem(`pnp_daily_lbl2_${clientId}`, dailyBtn1Label);
                              localStorage.setItem(`pnp_daily_q2_${clientId}`, dailyBtn1Query);
                              setDailyBtn1Label(d.label); setDailyBtn1Query(d.query);
                              localStorage.setItem(`pnp_daily_q_${clientId}`, d.query);
                              localStorage.setItem(`pnp_daily_lbl_${clientId}`, d.label);
                              localStorage.setItem(`pnp_daily_lbl_${clientId}`, d.label);
                              setShowDailyMenu(false);
                            }}
                              title={`Set "${d.label}" as your quick button`}
                              style={{ padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', color: '#F57F17', fontWeight: 700 }}
                            >⊕</button>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid #FFE082', padding: '6px 12px' }}>
                          <input
                            placeholder="✏️ Add your own daily query..."
                            onKeyDown={e => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const label = e.target.value.trim().substring(0, 40);
                                // Check for duplicates (case-insensitive)
                                const allLabels = new Set([
                                  'reservations', 'today\'s sales', 'staff schedule', 'inventory check', 'order summary',
                                  ...customDailyItems.map(d => d.label.toLowerCase().trim())
                                ]);
                                if (!allLabels.has(label.toLowerCase())) {
                                  const newItem = { label, query: e.target.value.trim() };
                                  const updated = [...customDailyItems, newItem];
                                  setCustomDailyItems(updated);
                                  localStorage.setItem(`pnp_daily_custom_${clientId}`, JSON.stringify(updated));
                                }
                                handleGenaiQuery(e.target.value.trim());
                                setShowDailyMenu(false);
                                e.target.value = '';
                              }
                            }}
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #FFE082', borderRadius: '6px', fontSize: '11px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    )}
                    <button onClick={() => handleGenaiQuery(dailyBtn1Query)}
                      style={{ padding: '3px 10px', background: '#F57F17', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                    >{dailyBtn1Label}</button>
                    <button onClick={() => handleGenaiQuery(dailyBtn2Query)}
                      style={{ padding: '3px 10px', background: '#FF8F00', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                    >{dailyBtn2Label}</button>
                    {dailyBtnCount >= 3 && (
                    <button onClick={() => handleGenaiQuery(dailyBtn3Query)}
                      style={{ padding: '3px 10px', background: '#FFA726', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                    >{dailyBtn3Label}</button>
                    )}
                  </fieldset>
                );
              })()}

              {/* ── GROUP 2: Analytics — feature-toggled: dynamic API or legacy hardcoded ── */}
              {USE_DYNAMIC_ANALYTICS ? (
                <AnalyticsFieldset
                  clientId={clientId}
                  productType={clientConfig?.product_type || ''}
                  onQuery={handleGenaiQuery}
                  customAnalyticsItems={customAnalyticsItems}
                  setCustomAnalyticsItems={setCustomAnalyticsItems}
                />
              ) : (() => {
                // Legacy hardcoded analytics buttons — no API call required
                const seen = new Set();
                const allAnalytics = [...operationsMenu, ...customAnalyticsItems].filter(op => {
                  const key = op.label.toLowerCase().trim();
                  if (seen.has(key)) return false;
                  seen.add(key);
                  return true;
                });
                const effectiveLabel = analyticsBtnLabel || operationsMenu[0]?.label || 'Revenue';
                const effectiveQuery = analyticsBtnQuery || operationsMenu[0]?.query;
                const effectiveLabel2 = analyticsBtn2Label || (operationsMenu[1]?.label !== effectiveLabel ? operationsMenu[1]?.label : operationsMenu[2]?.label) || '';
                const effectiveQuery2 = analyticsBtn2Query || (operationsMenu[1]?.label !== effectiveLabel ? operationsMenu[1]?.query : operationsMenu[2]?.query) || '';
                const usedLabels = new Set([effectiveLabel.toLowerCase(), effectiveLabel2.toLowerCase()]);
                const fallback3 = operationsMenu.find(op => !usedLabels.has(op.label.toLowerCase()));
                const effectiveLabel3 = analyticsBtn3Label || fallback3?.label || '';
                const effectiveQuery3 = analyticsBtn3Query || fallback3?.query || '';
                const btnLabels = new Set([effectiveLabel, effectiveLabel2, effectiveLabel3].map(l => l.toLowerCase().trim()).filter(Boolean));
                const menuItems = allAnalytics.filter(op => !btnLabels.has(op.label.toLowerCase().trim()));
                return (
                  <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '0 4px 0 0', border: '1.5px solid #90CAF9', borderRadius: '20px', background: '#E3F2FD', position: 'relative' }}>
                    <legend style={{ fontSize: '10px', fontWeight: 700, color: '#1565C0', padding: '0 6px', marginLeft: '8px' }}>
                      📊 Analytics
                      <button onClick={(e) => { e.stopPropagation(); setShowAnalyticsConfig(!showAnalyticsConfig); setShowAnalyticsMenu(false); }}
                        title="Configure visible button count (2 or 3)"
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '10px', padding: '0 2px', color: '#1565C0', verticalAlign: 'middle' }}
                      >⚙️</button>
                    </legend>
                    {showAnalyticsConfig && (
                      <div style={{ position: 'absolute', top: '100%', right: 0, zIndex: 110, background: '#fff', border: '1px solid #90CAF9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', padding: '8px 12px', marginTop: '4px', minWidth: '160px' }}>
                        <div style={{ fontSize: '11px', fontWeight: 600, marginBottom: '6px', color: '#333' }}>Quick Buttons</div>
                        {[2, 3].map(n => (
                          <label key={n} style={{ display: 'flex', alignItems: 'center', gap: '6px', cursor: 'pointer', padding: '3px 0', fontSize: '11px', color: '#555' }}>
                            <input type="radio" name="analyticsBtnCount" checked={analyticsBtnCount === n} onChange={() => { setAnalyticsBtnCount(n); localStorage.setItem(`pnp_analytics_btncount_${clientId}`, n); setShowAnalyticsConfig(false); }} style={{ margin: 0 }} />
                            {n} buttons
                          </label>
                        ))}
                      </div>
                    )}
                    <button onClick={() => { setShowAnalyticsMenu(!showAnalyticsMenu); setShowAnalyticsConfig(false); }}
                      style={{ padding: '4px 8px', border: 'none', fontSize: '11px', background: 'transparent', cursor: 'pointer', color: '#333' }}
                    >{showAnalyticsMenu ? '▲' : '▼'} Menu</button>
                    {showAnalyticsMenu && (
                      <div style={{ position: 'absolute', top: '100%', left: 0, zIndex: 100, background: '#fff', border: '1px solid #90CAF9', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.15)', minWidth: '220px', marginTop: '4px' }}>
                        {menuItems.map((op, i) => (
                          <div key={op.id || i} style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid #f0f0f0' }}>
                            <button onClick={() => { handleGenaiQuery(op.query); setShowAnalyticsMenu(false); }}
                              style={{ flex: 1, padding: '8px 12px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '12px', textAlign: 'left', color: '#333' }}
                            >{op.icon} {op.label}</button>
                            <button onClick={() => {
                              setAnalyticsBtn3Label(analyticsBtn2Label); setAnalyticsBtn3Query(analyticsBtn2Query);
                              localStorage.setItem(`pnp_analytics_lbl3_${clientId}`, analyticsBtn2Label);
                              localStorage.setItem(`pnp_analytics_q3_${clientId}`, analyticsBtn2Query);
                              setAnalyticsBtn2Label(analyticsBtnLabel); setAnalyticsBtn2Query(analyticsBtnQuery);
                              localStorage.setItem(`pnp_analytics_lbl2_${clientId}`, analyticsBtnLabel);
                              localStorage.setItem(`pnp_analytics_q2_${clientId}`, analyticsBtnQuery);
                              setAnalyticsBtnLabel(op.label); setAnalyticsBtnQuery(op.query);
                              localStorage.setItem(`pnp_analytics_lbl_${clientId}`, op.label);
                              localStorage.setItem(`pnp_analytics_q_${clientId}`, op.query);
                              setShowAnalyticsMenu(false);
                            }} title={`Set "${op.label}" as your quick button`}
                              style={{ padding: '8px 10px', border: 'none', background: 'transparent', cursor: 'pointer', fontSize: '14px', color: '#1565C0', fontWeight: 700 }}
                            >⊕</button>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid #90CAF9', padding: '6px 12px' }}>
                          <input placeholder="✏️ Add your own analytics query..."
                            onKeyDown={e => {
                              if (e.key === 'Enter' && e.target.value.trim()) {
                                const label = e.target.value.trim().substring(0, 40);
                                const allLabels = new Set(allAnalytics.map(op => op.label.toLowerCase().trim()));
                                if (!allLabels.has(label.toLowerCase())) {
                                  const newItem = { id: `custom_${Date.now()}`, label, query: e.target.value.trim(), icon: '📊' };
                                  const updated = [...customAnalyticsItems, newItem];
                                  setCustomAnalyticsItems(updated);
                                  localStorage.setItem(`pnp_analytics_custom_${clientId}`, JSON.stringify(updated));
                                }
                                handleGenaiQuery(e.target.value.trim());
                                setShowAnalyticsMenu(false);
                                e.target.value = '';
                              }
                            }}
                            style={{ width: '100%', padding: '6px 8px', border: '1px solid #90CAF9', borderRadius: '6px', fontSize: '11px', boxSizing: 'border-box' }}
                          />
                        </div>
                      </div>
                    )}
                    <button onClick={() => handleGenaiQuery(effectiveQuery)}
                      style={{ padding: '3px 10px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                    >{effectiveLabel}</button>
                    <button onClick={() => handleGenaiQuery(effectiveQuery2)}
                      style={{ padding: '3px 10px', background: '#1976D2', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                    >{effectiveLabel2}</button>
                    {analyticsBtnCount >= 3 && effectiveLabel3 && (
                      <button onClick={() => handleGenaiQuery(effectiveQuery3)}
                        style={{ padding: '3px 10px', background: '#1E88E5', color: '#fff', border: 'none', borderRadius: '12px', cursor: 'pointer', fontSize: '11px', fontWeight: 600, marginLeft: '2px', whiteSpace: 'nowrap' }}
                      >{effectiveLabel3}</button>
                    )}
                  </fieldset>
                );
              })()}

              {/* ══════════════════════════════════════════════════════════════
                   END — Traditional Analytics parent fieldset
                 ══════════════════════════════════════════════════════════════ */}
              </fieldset>

              {/* ── GROUP 2: Advanced Business Atomic Intelligence (peer) ── */}
              <fieldset style={{ display: 'inline-flex', alignItems: 'center', gap: '0', margin: 0, padding: '4px 2px 2px 0', border: '1.5px solid #CE93D8', borderRadius: '20px', background: '#F3E5F5', alignSelf: 'stretch' }}>
                <legend style={{ fontSize: '22px', fontWeight: 700, color: '#7B1FA2', padding: '0 6px', marginLeft: '8px', lineHeight: '0.5' }}>{(() => {
                  const ptIcon = (() => {
                    if (_pt.includes('china-us') || _pt.includes('strategic-intel')) return '🌏';
                    if (_pt.includes('procurement') || _pt.includes('procureguard')) return '🏛️';
                    if (_pt.includes('bank') || _pt.includes('financial')) return '🏦';
                    if (_pt.includes('restaurant') || _pt.includes('hospitality')) return '🍽️';
                    if (_pt.includes('gym') || _pt.includes('fitness')) return '💪';
                    if (_pt.includes('grocery') || _pt.includes('supermarket')) return '🛒';
                    if (_pt.includes('insurance')) return '🛡️';
                    if (_pt.includes('realestate') || _pt.includes('realty')) return '🏠';
                    if (_pt.includes('logistics')) return '🚛';
                    if (_pt.includes('medical') || _pt.includes('wellness')) return '🏥';
                    if (_pt.includes('hvac')) return '❄️';
                    if (_pt.includes('plumbing')) return '🔧';
                    if (_pt.includes('lawn')) return '🌿';
                    if (_pt.includes('valet') || _pt.includes('auto')) return '🚗';
                    return '🧠';
                  })();
                  return `${ptIcon} Smart Decisions Dashboard`;
                })()} <span style={{ fontSize: '11px', fontWeight: 400 }}>[Business Atomic Intelligence]</span></legend>
                <CustomIntelligencePanel
                  customQuestions={customQuestions} setCustomQuestions={setCustomQuestions}
                  sharedQuestions={sharedQuestions} clientId={clientId} companyName={companyName}
                  product={product} onRunQuestion={(q) => handleGenaiQuery(q)}
                  onGenaiQuery={handleGenaiQuery} operationsMenu={customAnalyticsItems}
                  getDemoResponse={getDemoResponse}
                  llmProviderId={llmPrefs.providerId}
                  onProviderChange={handleProviderChange}
                  llmProviders={llmPrefs.providers}
                />
              </fieldset>
              {/* ══════════════════════════════════════════════════════════════
                   END — All groups in single row
                 ══════════════════════════════════════════════════════════════ */}
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input value={genaiQuery} onChange={e => setGenaiQuery(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleGenaiQuery()}
                placeholder="Ask anything about your business operations..." style={{ ...inputStyle, flex: 1 }} aria-label="LLM query" />
              <button onClick={() => handleGenaiQuery()} disabled={dataEngine.loading} style={btnPrimary}>{dataEngine.loading ? '...' : 'Query'}</button>
            </div>

            {/* ── LLM-Driven Top 10 BI Questions ── */}
            <div style={{ marginTop: '12px', padding: '8px 12px', background: '#F3E5F5', borderRadius: '8px', border: '1.5px solid #CE93D8' }}>
              <LLMTop10Panel
                key={`${clientId}-${clientConfig?.product_type}-${llmPrefs.providerId}`}
                productType={clientConfig?.product_type}
                clientId={clientId}
                llmProviderId={llmPrefs.providerId}
                mainLLMProvider={llmPrefs.providerId}
                onRunQuestion={(q) => handleGenaiQuery(q)}
                onAddToCustom={(q) => {
                  // Add to custom questions via the existing custom intelligence flow
                  const newQ = {
                    question_id: `top10_${Date.now()}`,
                    question: q,
                    label: q.substring(0, 40),
                    created_at: new Date().toISOString(),
                    source: 'top10_llm',
                  };
                  setCustomQuestions([...customQuestions, newQ]);
                }}
                onShare={(q) => {
                  // Share notification — question shared from Top 10
                  // The share consent is already handled inside LLMTop10Panel
                }}
              />
            </div>
            {/* Background processing indicator */}
            {dataEngine.bgLoading && (
              <div style={{ padding: '6px 12px', background: '#E3F2FD', borderRadius: '6px', fontSize: '11px', color: '#1565C0', marginTop: '8px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <span style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }}>⟳</span> Revalidating data in background... You can continue using other tabs.
              </div>
            )}
            {dataEngine.cacheHit && !dataEngine.bgLoading && genaiResult && (
              <div style={{ padding: '3px 8px', background: '#f5f5f5', borderRadius: '4px', fontSize: '9px', color: '#999', marginTop: '4px', display: 'inline-block' }}>
                ⚡ cached
              </div>
            )}
            {genaiResult && (
              <div style={resultBox}>
                {/* Database Metadata Banner — shows DB details or hardcoded warning */}
                {(() => {
                  // Use db_config from clientConfig (resolved via product type map)
                  const dbInfo = resolvedDbConfig || null;
                  const isBackend = genaiResult._source === 'backend';
                  const hasRows = genaiResult.rows_scanned > 0;
                  const hasError = !!genaiResult.error;

                  // CRITICAL BUSINESS RULE: Determine if hardcoded should be shown
                  // NEVER show hardcoded if:
                  // 1. Client has own database (client_config)
                  // Demo fallback removed — ZDDV handles all cases.
                  // Never show "hardcoded" banner since all queries go to backend.
                  const isHardcoded = false;
                  
                  // Extract db_metadata from backend response if available
                  const dbMetadata = genaiResult.db_metadata || (dbInfo && isBackend ? {
                    database: dbInfo.db,
                    username: dbInfo.user || 'pnp_readonly',
                    host: dbInfo.host,
                    engine: dbInfo.engine,
                    query_time_ms: genaiResult.latency_ms || 0,
                    source: dbInfo.source || 'product_type_default',
                  } : null);

                  // Error case - show error banner
                  if (hasError) {
                    return (
                      <div style={{ padding: '10px 16px', background: '#ffebee', border: '2px solid #c62828', borderRadius: '8px', marginBottom: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                          <span style={{ fontSize: '24px' }}>🔴</span>
                          <div>
                            <div style={{ fontSize: '14px', fontWeight: 700, color: '#c62828', marginBottom: '4px' }}>
                              Database Error
                            </div>
                            <div style={{ fontSize: '12px', color: '#d32f2f' }}>
                              {dbInfo ? `${dbInfo.engine} • ${dbInfo.db} @ ${dbInfo.host}` : 'Connection failed'}
                            </div>
                            <div style={{ fontSize: '11px', color: '#e53935', marginTop: '6px' }}>
                              {genaiResult.error}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Use the new DatabaseMetadataBanner component
                  return (
                    <DatabaseMetadataBanner
                      dbMetadata={dbMetadata}
                      rowCount={genaiResult.rows_scanned || 0}
                      isHardcoded={isHardcoded}
                      position="top"
                    />
                  );
                })()}

                {/* Executive Insights Panel — renders FIRST (top) */}
                <InsightsPanel answer={genaiResult.answer} />

                {/* Raw answer text — only show if NO structured insights (else it'd be duplicate/shabby) */}
                {(() => {
                  const isJsonArray = typeof genaiResult.answer === 'string' && 
                    genaiResult.answer.trim().startsWith('[') && genaiResult.answer.trim().endsWith(']');
                  const hasInsights = hasStructuredInsights(genaiResult.answer);
                  
                  if (isJsonArray) return null; // IntelligenceChart will render the JSON
                  
                  if (hasInsights) {
                    // Structured content already shown above — put raw markdown in collapsible "Data Results" at bottom
                    return (
                      <details style={{
                        marginTop: '16px',
                        background: '#fafafa',
                        borderRadius: '8px',
                        border: '1px solid #e0e0e0',
                        padding: '10px 14px'
                      }}>
                        <summary style={{
                          cursor: 'pointer',
                          fontSize: '12px',
                          fontWeight: 600,
                          color: '#666',
                          userSelect: 'none'
                        }}>
                          📄 Data Results (raw response — click to expand)
                        </summary>
                        <div style={{
                          marginTop: '10px',
                          whiteSpace: 'pre-wrap',
                          fontSize: '12px',
                          color: '#555',
                          fontFamily: 'inherit',
                          lineHeight: 1.5,
                          maxHeight: '400px',
                          overflow: 'auto',
                          padding: '8px',
                          background: '#fff',
                          borderRadius: '6px',
                          border: '1px solid #eee'
                        }}>
                          {genaiResult.answer}
                        </div>
                      </details>
                    );
                  }
                  
                  // No structured insights — show raw text normally
                  return <div style={{ whiteSpace: 'pre-wrap' }}>{genaiResult.answer}</div>;
                })()}

                {/* Demo Data Warning (if no data available) */}
                {genaiResult.show_demo_warning && (
                  <DemoDataWarning queryLabel={genaiResult.demo_query_label} />
                )}

                {/* Simulation Warning for 0-row results (rubber stamp overlaid on simulated chart) */}
                {!genaiResult.show_demo_warning && genaiResult.rows_scanned === 0 && !genaiResult.error && genaiResult.answer && (
                  <div style={{
                    position: 'relative',
                    marginBottom: '8px'
                  }}>
                    {/* Rubber Stamp Overlay */}
                    <div style={{
                      position: 'absolute',
                      top: '40%',
                      left: '50%',
                      transform: 'translate(-50%, -50%) rotate(-12deg)',
                      zIndex: 10,
                      border: '5px solid #C62828',
                      borderRadius: '14px',
                      padding: '16px 28px',
                      background: 'rgba(255, 235, 238, 0.94)',
                      pointerEvents: 'none',
                      boxShadow: '0 6px 24px rgba(198, 40, 40, 0.35)',
                      maxWidth: '80%'
                    }}>
                      <div style={{
                        fontSize: '16px',
                        fontWeight: 900,
                        color: '#C62828',
                        textTransform: 'uppercase',
                        letterSpacing: '1.5px',
                        textAlign: 'center',
                        lineHeight: 1.4
                      }}>
                        ⚠️ There are NO DATA<br/>
                        <span style={{ fontSize: '13px', fontWeight: 700, letterSpacing: '1px' }}>
                          Simulated Data Visualizations
                        </span><br/>
                        <span style={{ fontSize: '11px', fontWeight: 600, letterSpacing: '0.5px', opacity: 0.85 }}>
                          Pls Disregard this Data Visualizations
                        </span>
                      </div>
                    </div>
                    {/* Simulated Chart underneath the stamp */}
                    <div style={{ opacity: 0.45, filter: 'grayscale(30%)' }}>
                      <IntelligenceChart
                        chartData={genaiResult.chart_data || genaiResult.demo_visualization?.chart_data || [
                          { name: 'Category A', value: 42000, pct: 35 },
                          { name: 'Category B', value: 28000, pct: 23 },
                          { name: 'Category C', value: 21000, pct: 18 },
                          { name: 'Category D', value: 15000, pct: 12 },
                          { name: 'Category E', value: 9000, pct: 8 },
                          { name: 'Other', value: 5000, pct: 4 }
                        ]}
                        chartType={genaiResult.chart_type || 'bar'}
                        chartTitle={genaiResult.chart_title || 'Simulated Data Visualization'}
                        isCurrency={genaiResult.is_currency || false}
                      />
                    </div>
                  </div>
                )}

                {/* Demo Visualization (if no real data — after 2+ attempts) */}
                {genaiResult.demo_visualization && genaiResult.rows_scanned !== 0 && (
                  <IntelligenceChart
                    chartData={genaiResult.demo_visualization.chart_data}
                    chartType={genaiResult.demo_visualization.chart_type}
                    chartTitle={genaiResult.demo_visualization.chart_title}
                    isCurrency={genaiResult.demo_visualization.is_currency}
                  />
                )}

                {/* Data Visualization — shown for successful queries with 1+ rows */}
                {!genaiResult.demo_visualization && genaiResult.rows_scanned > 0 && (
                  <IntelligenceChart
                    answer={genaiResult.answer}
                    chartData={genaiResult.chart_data}
                    chartType={genaiResult.chart_type}
                    chartTitle={genaiResult.chart_title}
                    isCurrency={genaiResult.is_currency}
                    visualization={genaiResult.visualization}
                  />
                )}

                {genaiResult.tables_queried && (
                  <div style={{ color: '#888', fontSize: '11px', marginTop: '8px', borderTop: '1px solid #e0e0e0', paddingTop: '8px' }}>
                    <span title={genaiResult.source === 'genai_fast_path' ? 'SQL Intelligence: Distilled-GenAI — Pre-built SQL pattern, no LLM call' : genaiResult.source === 'distilled_pattern' ? 'SQL Intelligence: Distilled Pattern' : 'SQL Intelligence: LLM-generated SQL'} style={{ cursor: 'help', marginRight: '4px' }}>💡</span>
                    {genaiResult.source === 'genai_fast_path' ? '⚡ Distilled-GenAI' : genaiResult.source === 'distilled_pattern' ? '⚡ Distilled Pattern' : genaiResult.source === 'user_hint' ? '📌 Hardcoded Hint' : `🤖 ${llmPrefs.provider.label}`}
                    {' • '}Tables: {genaiResult.tables_queried.join(', ')} • Rows: {genaiResult.rows_scanned?.toLocaleString()}
                    {genaiResult.latency_ms && <span> • {genaiResult.latency_ms}ms</span>}
                    {genaiResult.pattern && <span> • Pattern: {genaiResult.pattern}</span>}
                  </div>
                )}

                {/* SQL of Successful Result — editable/modifiable/confirm for future changes */}
                {genaiResult.rows_scanned > 0 && genaiResult.tables_queried && (() => {
                  const sql = genaiResult.executed_sql || genaiResult.sql || genaiResult.tables_queried.length === 1
                    ? `SELECT * FROM ${genaiResult.tables_queried[0]}\nLIMIT 100;`
                    : `SELECT *\nFROM ${genaiResult.tables_queried[0]}\n${genaiResult.tables_queried.slice(1).map(t => `  JOIN ${t} ON ${t}.${genaiResult.tables_queried[0].replace(/s$/, '')}_id = ${genaiResult.tables_queried[0]}.${genaiResult.tables_queried[0].replace(/s$/, '')}_id`).join('\n')}\nLIMIT 100;`;
                  const displaySql = genaiResult.executed_sql || genaiResult.sql || sql;
                  return (
                  <div style={{ marginTop: '10px', background: '#f5f7fa', borderRadius: '8px', border: '1px solid #e0e0e0', overflow: 'hidden' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#eef1f5', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ fontSize: '11px', fontWeight: 600, color: '#555' }}>📋 SQL — Refine Query / Edit / Confirm as Permanent</span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <button
                          onClick={() => {
                            const sqlToRun = displaySql.replace(/\n/g, ' ').trim();
                            setGenaiQuery(`[Instruction: SQL: ${sqlToRun}]`);
                            handleGenaiQuery(`[Instruction: SQL: ${sqlToRun}]`);
                          }}
                          style={{ padding: '3px 10px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          ▶ Run SQL
                        </button>
                        <button
                          onClick={() => { if (displaySql) navigator.clipboard.writeText(displaySql); }}
                          style={{ padding: '3px 10px', background: '#1565C0', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                        >
                          📋 Copy SQL
                        </button>
                      </div>
                    </div>
                    {/* Editable SQL textarea — user can modify and re-run */}
                    <textarea
                      defaultValue={displaySql}
                      id="cdg-sql-edit-area"
                      style={{ width: '100%', minHeight: '60px', maxHeight: '120px', padding: '8px 10px', fontSize: '11px', color: '#37474F', fontFamily: 'Consolas, monospace', border: 'none', borderBottom: '1px solid #e0e0e0', resize: 'vertical', background: '#fff', boxSizing: 'border-box' }}
                    />
                    <div style={{ display: 'flex', gap: '6px', padding: '6px 10px', background: '#f9fbe7', alignItems: 'center' }}>
                      <span style={{ fontSize: '10px', color: '#666', flex: 1 }}>✏️ Edit SQL above, then:</span>
                      <button
                        onClick={() => {
                          const el = document.getElementById('cdg-sql-edit-area');
                          const modifiedSql = el ? el.value.replace(/\n/g, ' ').trim() : displaySql.replace(/\n/g, ' ').trim();
                          setGenaiQuery(`[Instruction: SQL: ${modifiedSql}]`);
                          handleGenaiQuery(`[Instruction: SQL: ${modifiedSql}]`);
                        }}
                        style={{ padding: '3px 10px', background: '#FF6F00', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        ▶ Run Modified SQL
                      </button>
                      <button
                        onClick={async () => {
                          const el = document.getElementById('cdg-sql-edit-area');
                          const sqlToConfirm = el ? el.value.replace(/\n/g, ' ').trim() : displaySql.replace(/\n/g, ' ').trim();
                          try {
                            await fetch(`/v1/clients/${clientId}/cdg-sql/confirm`, {
                              method: 'POST', headers: { 'Content-Type': 'application/json' },
                              body: JSON.stringify({ session_id: 'direct', confirmed_sql: sqlToConfirm }),
                            });
                            setCdgSqlState({ status: 'success', source: 'permanent_pattern_L3', next_step: 'done', sql_used: sqlToConfirm });
                          } catch (e) { console.error(e); }
                        }}
                        title="Save this SQL as a Permanent Pattern (L3) — will be reused for identical queries"
                        style={{ padding: '3px 10px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 600, cursor: 'pointer' }}
                      >
                        ✓ Confirm as Permanent
                      </button>
                    </div>
                  </div>
                  );
                })()}

                {/* Recommended SQL — clickable Run buttons for each recommendation */}
                {genaiResult.recommended_sql && genaiResult.recommended_sql.length > 0 && (
                  <div style={{ marginTop: '12px', background: '#f0f7ff', borderRadius: '8px', border: '1px solid #bbdefb', padding: '12px' }}>
                    <div style={{ fontSize: '12px', fontWeight: 700, color: '#1565C0', marginBottom: '8px' }}>▶ Run Recommended SQL:</div>
                    {genaiResult.recommended_sql.map((rec, i) => (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px', padding: '6px 8px', background: '#fff', borderRadius: '6px', border: '1px solid #e3f2fd' }}>
                        <button
                          onClick={() => {
                            const sqlFlat = rec.sql.replace(/\n/g, ' ').trim();
                            handleGenaiQuery(`[Instruction: SQL: ${sqlFlat}]`);
                          }}
                          style={{ padding: '4px 10px', background: '#2E7D32', color: '#fff', border: 'none', borderRadius: '4px', fontSize: '10px', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >▶ Run</button>
                        <span style={{ fontSize: '11px', color: '#333', flex: 1 }}>{rec.label}</span>
                        <button
                          onClick={() => {
                            const sqlFlat = rec.sql.replace(/\n/g, ' ').trim();
                            const currentQ = genaiQuery.replace(/\[(?:Instruction|SQL)[\s\S]*$/i, '').trim();
                            setGenaiQuery(`${currentQ}[SQL: ${sqlFlat}]`);
                          }}
                          style={{ padding: '2px 8px', background: '#e3f2fd', color: '#1565C0', border: '1px solid #bbdefb', borderRadius: '4px', fontSize: '9px', fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
                        >📋 Paste</button>
                      </div>
                    ))}
                  </div>
                )}

                {/* Refinement prompt — show for zero results OR recommendations (NOT when backend unreachable) */}
                {(genaiResult.rows_scanned === 0 || genaiResult.recommended_sql || (genaiResult.answer && genaiResult.answer.includes('Connect your database'))) && genaiResult._source !== 'sql_pending' && genaiResult._source !== 'error' && (
                  <div style={{ marginTop: '12px', padding: '12px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #a5d6a7' }}>
                    <div style={{ fontSize: '12px', color: '#2E7D32', fontWeight: 600, marginBottom: '6px' }}>
                      {genaiResult.sql_used || genaiResult.sql || genaiResult.executed_sql
                        ? `📭 No Results from SQL: Query executed successfully but returned 0 rows.`
                        : `📋 No results found for this query.`}
                      {' '}You can refine it with specific instructions:
                    </div>
                    {(genaiResult.sql_used || genaiResult.sql || genaiResult.executed_sql) && (
                      <div style={{ padding: '6px 8px', background: '#f1f8e9', borderRadius: '4px', fontFamily: 'monospace', fontSize: '10px', color: '#33691E', marginBottom: '8px', whiteSpace: 'pre-wrap', maxHeight: '60px', overflow: 'auto' }}>
                        {genaiResult.sql_used || genaiResult.executed_sql || genaiResult.sql}
                      </div>
                    )}
                    <div style={{ fontSize: '11px', color: '#66BB6A', marginBottom: '8px' }}>
                      💡 Zero results may be valid — this query is saved to Custom Intelligence for monitoring. Re-run anytime to check for new data.
                    </div>
                    {!showRefinement ? (
                      <button onClick={() => setShowRefinement(true)} style={{
                        padding: '6px 14px', background: '#F57F17', color: '#fff', border: 'none',
                        borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer',
                      }}>🔧 Refine Query</button>
                    ) : (
                      <div>
                        <textarea
                          value={refinementInput}
                          onChange={e => setRefinementInput(e.target.value)}
                          placeholder={'Paste SQL + NLP instructions + Tables (E-N-T-SQL format):\n\n• SQL: SELECT d.department_name, c.contract_value FROM contracts c JOIN departments d ON ...\n• NLP: Show active contracts grouped by department with completion status\n• Tables: contracts, tenders, departments, vendors'}
                          rows={4}
                          style={{
                            width: '100%', padding: '8px 10px', border: '1px solid #ffe082',
                            borderRadius: '6px', fontSize: '12px', fontFamily: 'monospace',
                            boxSizing: 'border-box', resize: 'vertical', marginBottom: '6px',
                            background: '#fffde7',
                          }}
                          aria-label="Refinement instructions — SQL + NLP + Tables"
                        />
                        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                          <button onClick={handleRefinement} disabled={!refinementInput.trim()} style={{
                            padding: '6px 14px', background: refinementInput.trim() ? '#4CAF50' : '#ccc',
                            color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px',
                            fontWeight: 600, cursor: refinementInput.trim() ? 'pointer' : 'not-allowed',
                          }}>▶ Re-run with Instructions</button>
                          <button onClick={() => {
                            if (!refinementInput.trim()) return;
                            // Launch CDG-SQL L1→L2→L3 process with the user's SQL/NLP/Tables input
                            const input = refinementInput.trim();
                            // Detect if input contains SQL
                            const sqlMatch = input.match(/(?:SQL:\s*)(SELECT[\s\S]*?)((?:\n|$)(?:NLP:|Tables:|$))/i);
                            const extractedSql = sqlMatch ? sqlMatch[1].trim() : '';
                            // Set CDG-SQL state to L1 choice with the user's context
                            setCdgSqlState({
                              status: 'error',
                              next_step: 'l1_choice',
                              sql_used: extractedSql || '',
                              error: 'User-initiated CDG-SQL process from Refine Query',
                              source: 'refine_query_cdg',
                              session_id: `cdg-${Date.now().toString(36)}`,
                              available_tables: genaiResult?.tables_queried || [],
                              latency_ms: 0,
                              model: '',
                              original_query: input,
                            });
                            setShowRefinement(false);
                            setRefinementInput('');
                          }} disabled={!refinementInput.trim()}
                            title="Launch CDG-SQL (CEO-DDLS-GenAI SQL) Level 1→2→3 process: Uses your SQL/NLP/Tables input with COA-GPT to generate, execute, and confirm permanent SQL patterns"
                            style={{
                              padding: '6px 14px', background: refinementInput.trim() ? '#1565C0' : '#ccc',
                              color: '#fff', border: 'none', borderRadius: '6px', fontSize: '12px',
                              fontWeight: 600, cursor: refinementInput.trim() ? 'pointer' : 'not-allowed',
                            }}>🚀 Launch CDG-SQL (L1→L2→L3)</button>
                          <button onClick={() => { setShowRefinement(false); setRefinementInput(''); }} style={{
                            padding: '6px 14px', background: '#eee', color: '#666', border: 'none',
                            borderRadius: '6px', fontSize: '12px', cursor: 'pointer',
                          }}>Cancel</button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* CDG-SQL Intelligence Panel — renders below query results */}
            {cdgSqlState && (
              <div data-cdg-sql-panel>
              <CdgSqlPanel
                cdgState={cdgSqlState}
                clientId={clientId}
                productType={clientConfig?.product_type || ''}
                dbConfig={resolvedDbConfig}
                onSqlConfirmed={(data) => {
                  setCdgSqlState({ ...cdgSqlState, source: 'permanent_pattern_L3', status: 'success', next_step: 'done' });
                  // Pre-fill the query box with the confirmed SQL and auto-execute
                  if (data.confirmed_sql) {
                    const sqlQuery = `[Instruction: SQL: ${data.confirmed_sql}]`;
                    setGenaiQuery(sqlQuery);
                    handleGenaiQuery(sqlQuery);
                  }
                }}
                onRetry={(sql) => {
                  if (sql) {
                    // Pre-fill query box with the SQL and execute — user can see and modify
                    const sqlQuery = `[Instruction: SQL: ${sql}]`;
                    setGenaiQuery(sqlQuery);
                    handleGenaiQuery(sqlQuery);
                  } else {
                    setCdgSqlState(null);
                  }
                }}
              />
              </div>
            )}

            {/* System Error Bar — dismissible with refresh */}
            {systemError && (
              <div style={{
                marginTop: '12px', padding: '10px 14px', background: '#ffebee',
                borderRadius: '8px', border: '1px solid #ef9a9a',
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <div style={{ fontSize: '12px', color: '#c62828' }}>
                  ❌ System Error: {systemError}
                </div>
                <button onClick={() => setSystemError(null)} style={{
                  padding: '4px 12px', background: '#c62828', color: '#fff', border: 'none',
                  borderRadius: '6px', fontSize: '11px', fontWeight: 600, cursor: 'pointer',
                }}>🔄 Refresh</button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Comm-AI Tab */}
      {activeTab === 'agentic' && (
        <div>
          <div style={cardStyle}>
            <h3 style={{ marginTop: 0 }}>{UI.AGENTIC_ICON} {UI.AGENTIC_AI} — External Communications</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
              Public-facing agent for MCP tool invocations and A2A inter-agent messaging.
              All outgoing requests logged to public_audit_log.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', background: '#e8f5e9', borderRadius: '8px', border: '1px solid #c8e6c9' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>🔌 MCP (Model Context Protocol)</div>
                <div style={{ fontSize: '12px', color: '#666' }}>web_search, vendor_catalog, service_marketplace, signwell_send</div>
              </div>
              <div style={{ padding: '14px', background: '#e3f2fd', borderRadius: '8px', border: '1px solid #bbdefb' }}>
                <div style={{ fontWeight: 700, fontSize: '13px', marginBottom: '4px' }}>🤝 A2A (Agent-to-Agent)</div>
                <div style={{ fontSize: '12px', color: '#666' }}>identity.present, discovery.find_provider, permission.request</div>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <input value={agenticAction} onChange={e => setAgenticAction(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleAgenticAction()}
                placeholder="e.g. Find a plumber nearby, Send agreement to partner" style={{ ...inputStyle, flex: 1 }} aria-label="Comm-AI action" />
              <button onClick={handleAgenticAction} disabled={loading} style={{ ...btnPrimary, background: '#2E7D32' }}>{loading ? '...' : 'Dispatch'}</button>
            </div>
            {agenticResult && (
              <div style={resultBox}>
                <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                  <span style={{ background: agenticResult.protocol === 'MCP' ? '#4CAF50' : '#2196F3', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>{agenticResult.protocol}</span>
                  <span style={{ background: '#FF9800', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 700 }}>{agenticResult.status}</span>
                </div>
                <div>{agenticResult.message}</div>
                {agenticResult.audit_id && <div style={{ color: '#888', fontSize: '11px', marginTop: '8px' }}>Audit ID: {agenticResult.audit_id}</div>}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Permission Logs Tab */}
      {activeTab === 'permissions' && (
        <div>
          <div style={cardStyle}>
            <h3 style={{ margin: '0 0 12px' }}>🔐 Permission Logs</h3>
            <p style={{ color: '#666', fontSize: '13px', marginBottom: '16px' }}>
              View and manage cross-tenant access grants, A2A/MCP permission requests, and audit trail for {companyName}.
            </p>
            <div style={{ background: '#f5f7fa', borderRadius: '8px', padding: '16px' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0', textAlign: 'left' }}>
                    <th style={{ padding: '8px' }}>Timestamp</th>
                    <th style={{ padding: '8px' }}>Action</th>
                    <th style={{ padding: '8px' }}>Grantee</th>
                    <th style={{ padding: '8px' }}>Scope</th>
                    <th style={{ padding: '8px' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{new Date().toISOString().slice(0, 19)}</td>
                    <td style={{ padding: '8px' }}>MCP Read Grant</td>
                    <td style={{ padding: '8px' }}>pnp-agent-dist001</td>
                    <td style={{ padding: '8px' }}>inventory</td>
                    <td style={{ padding: '8px' }}><span style={{ background: '#4CAF50', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>ACTIVE</span></td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{new Date(Date.now() - 86400000).toISOString().slice(0, 19)}</td>
                    <td style={{ padding: '8px' }}>A2A Handshake</td>
                    <td style={{ padding: '8px' }}>pnp-agent-supplier</td>
                    <td style={{ padding: '8px' }}>pricing</td>
                    <td style={{ padding: '8px' }}><span style={{ background: '#FF9800', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>EXPIRED</span></td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #eee' }}>
                    <td style={{ padding: '8px', fontFamily: 'monospace', fontSize: '11px' }}>{new Date(Date.now() - 172800000).toISOString().slice(0, 19)}</td>
                    <td style={{ padding: '8px' }}>Schema Intelligence</td>
                    <td style={{ padding: '8px' }}>local-distiller</td>
                    <td style={{ padding: '8px' }}>schema_metadata</td>
                    <td style={{ padding: '8px' }}><span style={{ background: '#1565C0', color: '#fff', padding: '2px 8px', borderRadius: '4px', fontSize: '10px' }}>VERIFIED</span></td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ color: '#999', fontSize: '11px', marginTop: '12px' }}>
              Showing recent permission events. Full audit trail available via the API at <code>/v1/clients/{clientId}/permissions</code>.
            </p>
          </div>
        </div>
      )}

      {/* View Configuration Tab */}
      {activeTab === 'view-config' && (
        <ViewConfiguration clientConfig={clientConfig} />
      )}

      {/* View Client Details Tab — shows locked config + demo DB warning */}
      {activeTab === 'view-client-details' && (
        <div style={{ padding: '24px' }}>
          <h2 style={{ marginBottom: '16px', color: '#1a237e' }}>🔍 Client Details</h2>

          {/* Demo Mode Warning Banner */}
          {(!clientConfig?.db_config?.host || clientConfig?.db_config?.host === 'localhost') && (
            <div style={{ background: '#fff3e0', border: '1px solid #ff9800', borderRadius: '8px', padding: '16px', marginBottom: '20px' }}>
              <strong style={{ color: '#e65100' }}>⚠️ Demo Mode Active:</strong>
              <span style={{ color: '#bf360c', marginLeft: '8px' }}>
                You are connected to the Plug-N-Play-Infra demo database (restaurant-ops-v1).
                This is a read-only dataset for exploration.
                Go to <strong>Update Client Details</strong> to configure your private database.
              </span>
            </div>
          )}

          {/* Locked Configuration */}
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '20px', marginBottom: '16px' }}>
            <h3 style={{ marginBottom: '12px', color: '#333' }}>🔒 Locked Configuration</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Product Type', clientConfig?.product_type || 'Restaurant-Intelligence-Type-1'],
                  ['Platform Role', 'Subscriber'],
                  ['Client Type', clientConfig?.tenant_type || 'Standalone'],
                  ['Plan', `${clientConfig?.plan || 'Growth'} — $49/month`],
                  ['Company Name', clientConfig?.company_name || '—'],
                  ['Contact Email', clientConfig?.contact_email || '—'],
                  ['Client ID', clientConfig?.client_id || '—'],
                  ['Status', clientConfig?.status || 'trial'],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#555', width: '200px' }}>{label}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Database Configuration */}
          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '20px' }}>
            <h3 style={{ marginBottom: '12px', color: '#333' }}>🗄️ Database Configuration</h3>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <tbody>
                {[
                  ['Engine', 'PostgreSQL'],
                  ['Host', clientConfig?.db_config?.host || 'localhost'],
                  ['Port', clientConfig?.db_config?.port || '5432'],
                  ['Database', clientConfig?.db_config?.database || 'restaurant-ops-v1'],
                  ['Username', clientConfig?.db_config?.username || 'pnp_readonly'],
                  ['Password', '********'],
                  ['Mode', (!clientConfig?.db_config?.host || clientConfig?.db_config?.host === 'localhost') ? '🟡 Demo (PNP Sandbox)' : '🟢 Customer Database'],
                ].map(([label, value]) => (
                  <tr key={label} style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#555', width: '200px' }}>{label}</td>
                    <td style={{ padding: '10px 12px', fontFamily: 'monospace', fontSize: '13px' }}>{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Update Client Details Tab — allows DB config change */}
      {activeTab === 'update-client-details' && (
        <div style={{ padding: '24px' }}>
          <h2 style={{ marginBottom: '16px', color: '#1a237e' }}>✏️ Update Client Details</h2>
          <p style={{ color: '#666', marginBottom: '20px' }}>
            Connect your own Read-Only database to replace the PNP demo environment.
            Your product type and plan remain locked.
          </p>

          <div style={{ background: '#f5f5f5', borderRadius: '8px', padding: '20px', maxWidth: '600px' }}>
            <h3 style={{ marginBottom: '16px', color: '#333' }}>🗄️ Database Connection</h3>
            <form onSubmit={(e) => { e.preventDefault(); alert('Database update will be submitted to /v1/subscribers/upgrade. This feature is coming soon.'); }}>
              {[
                { id: 'engine', label: 'Engine', defaultVal: 'postgresql', disabled: true },
                { id: 'host', label: 'Host', defaultVal: '', placeholder: 'your-db-host.amazonaws.com' },
                { id: 'port', label: 'Port', defaultVal: '5432', placeholder: '5432' },
                { id: 'database', label: 'Database Name', defaultVal: '', placeholder: 'my_restaurant_db' },
                { id: 'username', label: 'Username (Read-Only)', defaultVal: '', placeholder: 'readonly_user' },
                { id: 'password', label: 'Password', defaultVal: '', placeholder: '••••••••', type: 'password' },
              ].map(field => (
                <div key={field.id} style={{ marginBottom: '14px' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: 600, color: '#333', marginBottom: '4px' }}>{field.label}</label>
                  <input
                    type={field.type || 'text'}
                    defaultValue={field.defaultVal}
                    placeholder={field.placeholder}
                    disabled={field.disabled}
                    style={{ width: '100%', padding: '10px 12px', border: '1.5px solid #ddd', borderRadius: '6px', fontSize: '14px', fontFamily: 'monospace', background: field.disabled ? '#eee' : '#fff' }}
                  />
                </div>
              ))}
              <button type="submit" style={{ padding: '12px 24px', background: '#1a237e', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: 600, cursor: 'pointer', marginTop: '8px' }}>
                🔄 Update Database Connection
              </button>
            </form>
          </div>
        </div>
      )}

      {/* ── TN-IPDS Procurement Intelligence Tabs ─────────────────────────── */}
      {/* Shown only when product_type === canonical TN Procurement string    */}
      {activeTab === 'tn-tenders' && (() => {
        const TenderUploadPanel = require('./TNProcurement/TenderUploadPanel').default;
        return <TenderUploadPanel clientId={clientId} />;
      })()}

      {activeTab === 'tn-risk-cases' && (() => {
        const RiskCaseReviewPanel = require('./TNProcurement/RiskCaseReviewPanel').default;
        return <RiskCaseReviewPanel clientId={clientId} />;
      })()}

      {activeTab === 'tn-policy-qa' && (() => {
        const PolicyQAPanel = require('./TNProcurement/PolicyQAPanel').default;
        return <PolicyQAPanel clientId={clientId} />;
      })()}

      {activeTab === 'tn-district-dashboard' && (() => {
        const DistrictDashboard = require('./TNProcurement/DistrictDashboard').default;
        return <DistrictDashboard clientId={clientId} />;
      })()}

      {activeTab === 'tn-cm-directives' && (
        <div style={{ padding: 24 }}>
          <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>📜 CM Directive KPI Targets</h2>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
            Active Chief Minister directive targets for Tamil Nadu procurement.
            These are KPI alignment targets — no political claims are made.
          </p>
          <iframe
            src={`/v1/tn-ipds/cm-directives?active=true`}
            title="CM Directives"
            style={{ display: 'none' }}
          />
          {/* Rendered by DistrictDashboard's CM overlay — navigate there for full view */}
          <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 10, padding: 20 }}>
            <p style={{ color: '#065f46', fontSize: 14 }}>
              CM Directive KPI targets are displayed as an overlay on the District Dashboard.
              Navigate to <strong>District Dashboard</strong> to see the full alignment view.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'tn-workflow-queue' && (
        <div style={{ padding: 24 }}>
          <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>🔄 Workflow Queue</h2>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
            Pending approval transactions requiring officer action.
          </p>
          <iframe
            src={`/v1/tn-ipds/audit-trail?entity_type=workflow_transaction&limit=50`}
            title="Workflow Queue"
            style={{ width: '100%', minHeight: 400, border: '1px solid #e5e7eb', borderRadius: 8 }}
          />
        </div>
      )}

      {activeTab === 'tn-vendors' && (
        <div style={{ padding: 24 }}>
          <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>🏢 Vendor Registry</h2>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
            Registered vendors — MSME, women-owned, startup flags, document expiry tracking.
            Use the AI Ops tab to query vendor data (e.g. "top vendors by award concentration").
          </p>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
            <p style={{ color: '#374151', fontSize: 14 }}>
              💡 <strong>Tip:</strong> Ask in the AI Ops tab: "Show vendor document expiry in 30 days"
              or "Top 10 vendors by award concentration" to get live vendor analytics.
            </p>
          </div>
        </div>
      )}

      {activeTab === 'tn-audit-trail' && (
        <div style={{ padding: 24 }}>
          <h2 style={{ color: '#1a1a2e', marginBottom: 8 }}>🔍 Procurement Audit Trail</h2>
          <p style={{ color: '#666', fontSize: 13, marginBottom: 20 }}>
            Complete audit log of all procurement actions — tenders, contracts, payments, AI decisions.
          </p>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: 20 }}>
            <p style={{ color: '#374151', fontSize: 14 }}>
              Audit trail is available via the API at <code>/v1/tn-ipds/audit-trail</code>.
              Filter by entity_type (tender, contract, payment, ai_case, workflow_transaction).
            </p>
          </div>
        </div>
      )}
      {/* ── End TN-IPDS Tabs ─────────────────────────────────────────────── */}

      {/* ── Product Analytics Tab (Materials Margin Intelligence) ────────── */}
      {activeTab === 'product-analytics' && (
        <AnalyticsPanel
          clientId={clientId}
          productType={clientConfig?.product_type}
          clientConfig={clientConfig}
        />
      )}

    </div>
  );
};

/**
 * PolicyGovernancePanel — Optional toggle on the Overview tab.
 *
 * Allows CEOs and business authorities to upload Federal, State,
 * CEO Directives, and Preferences PDFs that layer on top of the
 * existing product-type policies. Includes:
 *   - Consent acknowledgement before upload zone appears
 *   - Product-type relevance validation with warnings
 *   - Factory Reset to wipe all governance policies
 */

// ── Product-type relevance rules ─────────────────────────────────────────────
const PRODUCT_POLICY_RELEVANCE = {
  'financial': ['bank', 'finance', 'credit', 'loan', 'deposit', 'alm', 'nim', 'aml', 'bsa', 'fdic', 'occ', 'fed', 'federal', 'state', 'nydfs', 'ffiec', 'ceo', 'preference'],
  'bank': ['bank', 'finance', 'credit', 'loan', 'deposit', 'alm', 'nim', 'aml', 'bsa', 'fdic', 'occ', 'fed', 'federal', 'state', 'nydfs', 'ffiec', 'ceo', 'preference'],
  'restaurant': ['food', 'restaurant', 'hospitality', 'health', 'fda', 'usda', 'labor', 'wage', 'tip', 'alcohol', 'liquor', 'federal', 'state', 'ceo', 'preference'],
  'hospitality': ['food', 'restaurant', 'hospitality', 'health', 'fda', 'usda', 'labor', 'wage', 'federal', 'state', 'ceo', 'preference'],
  'gym': ['fitness', 'gym', 'health', 'liability', 'waiver', 'ada', 'labor', 'federal', 'state', 'ceo', 'preference'],
  'medical': ['hipaa', 'health', 'medical', 'patient', 'phi', 'cms', 'fda', 'federal', 'state', 'ceo', 'preference'],
  'insurance': ['insurance', 'liability', 'risk', 'claim', 'underwrite', 'federal', 'state', 'ceo', 'preference'],
  'realestate': ['real estate', 'property', 'lease', 'tenant', 'fair housing', 'federal', 'state', 'ceo', 'preference'],
  'grocery': ['retail', 'grocery', 'food', 'fda', 'usda', 'consumer', 'federal', 'state', 'ceo', 'preference'],
  'supermarket': ['retail', 'grocery', 'food', 'fda', 'usda', 'consumer', 'federal', 'state', 'ceo', 'preference'],
  'logistics': ['transport', 'logistics', 'dot', 'fmcsa', 'freight', 'federal', 'state', 'ceo', 'preference'],
  'default': ['federal', 'state', 'ceo', 'preference'],
};

function getProductDomain(productType) {
  if (!productType) return 'default';
  const pt = productType.toLowerCase();
  for (const key of Object.keys(PRODUCT_POLICY_RELEVANCE)) {
    if (pt.includes(key)) return key;
  }
  return 'default';
}

function validatePolicyRelevance(filename, productType) {
  const domain = getProductDomain(productType);
  const relevantTerms = PRODUCT_POLICY_RELEVANCE[domain] || PRODUCT_POLICY_RELEVANCE['default'];
  const nameLower = filename.toLowerCase();
  const otherDomains = Object.keys(PRODUCT_POLICY_RELEVANCE).filter(k => k !== domain && k !== 'default');
  const conflictingDomain = otherDomains.find(d => {
    const domainTerms = PRODUCT_POLICY_RELEVANCE[d].filter(t => !relevantTerms.includes(t));
    return domainTerms.some(t => nameLower.includes(t) && t.length > 4);
  });
  if (conflictingDomain) {
    return {
      warning: true,
      message: `"${filename}" appears to be a ${conflictingDomain} policy but your product type is ${productType}. Irrelevant policies may add unnecessary boundaries and slow AI performance.`,
    };
  }
  return { warning: false };
}

function PolicyGovernancePanel({ clientId, productType }) {
  const [expanded, setExpanded] = useState(false);
  const [consented, setConsented] = useState(() => {
    try { return localStorage.getItem(`policy_consent_${clientId}`) === 'yes'; } catch { return false; }
  });
  const [policies, setPolicies] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [files, setFiles] = useState({ federal: null, state: null, ceo_directives: null, preferences: null });
  const [fileWarnings, setFileWarnings] = useState({});
  const [resetting, setResetting] = useState(false);
  const [resetConfirm, setResetConfirm] = useState(false);
  const [resetStatus, setResetStatus] = useState(null);

  const CATEGORIES = [
    { id: 'federal', icon: '🏛️', label: 'Federal Policy', desc: 'Federal regulatory compliance (FDIC, OCC, Fed Reserve, FFIEC)' },
    { id: 'state', icon: '🗺️', label: 'State Policy', desc: 'State-level regulations (NC Chapter 53C, NYDFS, etc.)' },
    { id: 'ceo_directives', icon: '👔', label: 'CEO Directives', desc: 'Internal business guardrails and AI usage boundaries' },
    { id: 'preferences', icon: '⚙️', label: 'Preferences', desc: 'Location, language, timezone, LLM provider preferences' },
  ];

  const apiBase = window.location.hostname === 'localhost' ? 'http://localhost:8000' : '';

  // Always read token fresh at call time — empty string is fine (policy routes don't require auth)
  const getToken = () => localStorage.getItem('pnp_access_token') || localStorage.getItem('pnp_id_token') || '';

  // Try to refresh Cognito token silently — non-blocking, falls back gracefully
  const refreshAndGetToken = async () => {
    const current = getToken();
    const refreshToken = localStorage.getItem('pnp_refresh_token');
    if (!refreshToken) return current;
    try {
      const resp = await fetch(`${apiBase}/v1/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
      if (resp.ok) {
        const data = await resp.json();
        if (data.access_token) {
          localStorage.setItem('pnp_access_token', data.access_token);
          return data.access_token;
        }
      }
    } catch {}
    return current;
  };

  useEffect(() => {
    if (!expanded || !consented || !clientId) return;
    fetch(`${apiBase}/v1/clients/${clientId}/policies`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setPolicies(data?.policies || {}))
      .catch(() => setPolicies({}));
  }, [expanded, consented, clientId, apiBase]);

  const handleConsent = () => {
    try { localStorage.setItem(`policy_consent_${clientId}`, 'yes'); } catch {}
    setConsented(true);
  };

  const handleFileChange = (category, file) => {
    setFiles(prev => ({ ...prev, [category]: file }));
    if (file) {
      const validation = validatePolicyRelevance(file.name, productType);
      setFileWarnings(prev => ({ ...prev, [category]: validation.warning ? validation.message : null }));
    } else {
      setFileWarnings(prev => ({ ...prev, [category]: null }));
    }
  };

  // Factory reset — deactivates all active governance policies via single atomic endpoint
  const handleFactoryReset = async () => {
    if (!resetConfirm) { setResetConfirm(true); return; }
    setResetting(true);
    setResetStatus(null);
    setResetConfirm(false);
    try {
      const freshToken = await refreshAndGetToken();
      const resp = await fetch(`${apiBase}/v1/clients/${clientId}/policies/reset`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      const result = await resp.json();
      if (resp.ok && result.reset) {
        setResetStatus({ success: true, message: result.message });
        setPolicies({});
        try { localStorage.removeItem(`policy_consent_${clientId}`); } catch {}
        setConsented(false);
      } else {
        setResetStatus({ error: result.message || 'Reset failed — please try again' });
      }
    } catch (e) {
      setResetStatus({ error: 'Reset failed — please try again' });
    }
    setResetting(false);
  };

  // Download consolidated governance PDF
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [pdfProgress, setPdfProgress] = useState('');
  const handleDownloadPdf = async () => {
    setDownloadingPdf(true);
    setPdfProgress('');
    try {
      const freshToken = await refreshAndGetToken();
      const resp = await fetch(`${apiBase}/v1/clients/${clientId}/policies/consolidated/pdf`, {
        headers: { Authorization: `Bearer ${freshToken}` },
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${resp.status}`);
      }
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${clientId}-Consolidated-Policy-Governance.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e) {
      setPdfProgress(`⚠️ ${e.message}`);
      setTimeout(() => setPdfProgress(''), 8000);
    }
    setDownloadingPdf(false);
  };

  // Refresh active policies when a batch completes (fired by BatchStatusPanel)
  useEffect(() => {
    const onReassembled = () => {
      fetch(`${apiBase}/v1/clients/${clientId}/policies`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      }).then(r => r.ok ? r.json() : null).then(data => setPolicies(data?.policies || {})).catch(() => {});
    };
    window.addEventListener('policy-reassembled', onReassembled);
    return () => window.removeEventListener('policy-reassembled', onReassembled);
  }, [clientId, apiBase]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleUpload = async () => {
    const selected = Object.entries(files).filter(([, f]) => f !== null);
    if (selected.length === 0) return;
    setUploading(true);
    setUploadStatus({});
    try {
      const formData = new FormData();
      selected.forEach(([cat, file]) => formData.append(`files[${cat}]`, file));
      // Refresh token before upload to avoid 401 on expired sessions
      const freshToken = await refreshAndGetToken();
      const resp = await fetch(`${apiBase}/v1/clients/${clientId}/policies/batch/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${freshToken}` },
        body: formData,
      });
      if (resp.status === 401) {
        setUploadStatus({ expired: true });
        setUploading(false);
        return;
      }
      const result = await resp.json();
      if (resp.ok) {
        setUploadStatus({ success: true, batch_id: result.batch_id, file_count: result.file_count });
        setFiles({ federal: null, state: null, ceo_directives: null, preferences: null });
        setFileWarnings({});
        // Refresh active policies
        setTimeout(() => {
          fetch(`${apiBase}/v1/clients/${clientId}/policies`, {
            headers: { Authorization: `Bearer ${getToken()}` },
          }).then(r => r.ok ? r.json() : null).then(data => setPolicies(data?.policies || {})).catch(() => {});
        }, 1500);
      } else {
        setUploadStatus({ error: result.message || 'Upload failed' });
      }
    } catch (e) {
      setUploadStatus({ error: 'Network error — please try again' });
    }
    setUploading(false);
  };

  const activeCount = policies ? Object.keys(policies).filter(k => policies[k]).length : 0;
  const hasWarnings = Object.values(fileWarnings).some(Boolean);

  // Build collapsed-header tooltip showing all active + missing categories
  const buildHeaderTooltip = () => {
    if (!policies) return 'Loading policy status…';
    const active = CATEGORIES.filter(c => policies[c.id]);
    const missing = CATEGORIES.filter(c => !policies[c.id]);
    const lines = [];
    if (active.length > 0) {
      lines.push('── Active policies ──');
      active.forEach(c => {
        const p = policies[c.id];
        lines.push(`${c.icon} ${c.label} v${p?.version || '?'}${p?.uploaded_at ? ' · ' + new Date(p.uploaded_at).toLocaleDateString() : ''}`);
      });
    }
    if (missing.length > 0) {
      lines.push('── Not uploaded ──');
      missing.forEach(c => lines.push(`${c.icon} ${c.label}`));
    }
    if (lines.length === 0) return '0/4 policies active — AI using product-type defaults';
    return lines.join('\n');
  };

  // Fetch policy count on mount so the header badge is accurate even when collapsed
  useEffect(() => {
    if (!clientId || !consented) return;
    fetch(`${apiBase}/v1/clients/${clientId}/policies`, {
      headers: { Authorization: `Bearer ${getToken()}` },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => setPolicies(data?.policies || {}))
      .catch(() => setPolicies({}));
  }, [clientId, consented]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div style={{ border: `1px solid ${activeCount > 0 ? '#a5d6a7' : '#e3f2fd'}`, borderRadius: '12px', marginBottom: '16px', overflow: 'hidden' }}>
      {/* Toggle header */}
      <button
        onClick={() => setExpanded(e => !e)}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '14px 18px',
          background: expanded ? '#e3f2fd' : activeCount > 0 ? '#f1f8e9' : '#f8fbff',
          border: 'none', cursor: 'pointer', textAlign: 'left',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={{ fontSize: '20px' }}>📜</span>
          <div>
            <div style={{ fontWeight: 700, fontSize: '14px', color: '#1565c0', display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
              Policy Governance Layer
              {/* Always-visible counter badge with full tooltip */}
              {policies !== null ? (
                activeCount > 0 ? (
                  <span
                    title={buildHeaderTooltip()}
                    style={{ background: '#2e7d32', color: '#fff', borderRadius: '10px', padding: '1px 10px', fontSize: '11px', fontWeight: 700, cursor: 'help' }}
                  >
                    ✅ {activeCount} / 4 active
                  </span>
                ) : (
                  <span
                    title="No governance policies uploaded yet.\nAI is running on product-type defaults only.\nExpand to upload Federal, State, CEO Directives or Preferences."
                    style={{ background: '#e0e0e0', color: '#555', borderRadius: '10px', padding: '1px 10px', fontSize: '11px', fontWeight: 600, cursor: 'help' }}
                  >
                    0 / 4 — factory defaults
                  </span>
                )
              ) : (
                consented && (
                  <span style={{ background: '#e3f2fd', color: '#1565c0', borderRadius: '10px', padding: '1px 10px', fontSize: '11px' }}>
                    loading…
                  </span>
                )
              )}
            </div>
            <div style={{ fontSize: '12px', color: '#666', marginTop: '2px' }}>
              {activeCount > 0
                ? `${CATEGORIES.filter(c => policies?.[c.id]).map(c => c.label).join(', ')} — layered above product-type AI`
                : 'Optional — Upload Federal, State, CEO Directives & Preferences to layer on top of existing AI policies'
              }
            </div>
          </div>
        </div>
        <span style={{ fontSize: '18px', color: '#1565c0' }}>{expanded ? '▲' : '▼'}</span>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div style={{ padding: '18px', background: '#fff' }}>
          {!consented ? (
            <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '8px', padding: '16px' }}>
              <div style={{ fontWeight: 700, fontSize: '14px', marginBottom: '8px' }}>⚠️ Authority Acknowledgement Required</div>
              <p style={{ fontSize: '13px', color: '#555', margin: '0 0 12px' }}>
                By enabling Policy Governance, you confirm that you are an authorised business authority (CEO, Compliance Officer, or designated delegate)
                with the right to upload and apply <strong>Federal, State, CEO Directives, and Preferences</strong> policies for this tenant.
              </p>
              <p style={{ fontSize: '13px', color: '#555', margin: '0 0 16px' }}>
                These policies will be applied <strong>above</strong> the existing product-type AI configuration and will influence all AI responses,
                guardrails, language settings, and scheduling for this business.
                {productType && <> Policies irrelevant to <strong>{productType}</strong> may slow AI performance by adding unnecessary boundaries.</>}
              </p>
              <button
                onClick={handleConsent}
                style={{ background: '#1565c0', color: '#fff', border: 'none', borderRadius: '6px', padding: '10px 24px', cursor: 'pointer', fontWeight: 600, fontSize: '13px' }}
              >
                I Acknowledge — Enable Policy Governance
              </button>
            </div>
          ) : (
            <div>
              {/* ── Toolbar: always visible once consented ── */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px', flexWrap: 'wrap', gap: '8px' }}>
                {/* Left: active policy status bar with per-badge tooltips */}
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                  {policies && activeCount > 0 ? (
                    <>
                      <span style={{ fontWeight: 700, fontSize: '12px', color: '#2e7d32' }}>✅ {activeCount}/4 active:</span>
                      {CATEGORIES.filter(c => policies[c.id]).map(c => {
                        const p = policies[c.id];
                        const tip = [
                          `${c.label} — ACTIVE`,
                          `Version: v${p?.version || '?'}`,
                          p?.uploaded_by ? `Uploaded by: ${p.uploaded_by}` : null,
                          p?.uploaded_at ? `Date: ${new Date(p.uploaded_at).toLocaleString()}` : null,
                          p?.effective_date ? `Effective: ${new Date(p.effective_date).toLocaleDateString()}` : null,
                          p?.source === 'parent' ? 'Source: Inherited from parent' : 'Source: Own upload',
                          `\n${c.desc}`,
                        ].filter(Boolean).join('\n');
                        return (
                          <span
                            key={c.id}
                            title={tip}
                            style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', color: '#2e7d32', fontWeight: 600, cursor: 'help' }}
                          >
                            {c.icon} {c.label} <span style={{ opacity: 0.7 }}>v{p?.version || '?'}</span>
                          </span>
                        );
                      })}
                      {/* Dashed badges for categories NOT yet uploaded */}
                      {CATEGORIES.filter(c => !policies[c.id]).map(c => (
                        <span
                          key={c.id}
                          title={`${c.label} — NOT UPLOADED\n${c.desc}\n\nUpload a PDF below to activate this policy layer.`}
                          style={{ background: '#f5f5f5', border: '1px dashed #ccc', borderRadius: '6px', padding: '2px 10px', fontSize: '12px', color: '#aaa', cursor: 'help' }}
                        >
                          {c.icon} {c.label}
                        </span>
                      ))}
                    </>
                  ) : (
                    <span
                      title="No governance policies uploaded yet.\nAI is running on product-type defaults only.\nUpload Federal, State, CEO Directives or Preferences below to activate governance layers."
                      style={{ fontSize: '12px', color: '#888', fontStyle: 'italic', cursor: 'help' }}
                    >
                      0/4 policies active — AI running on product-type defaults only
                    </span>
                  )}
                </div>

                {/* Right: Download PDF + Factory Reset */}
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  {/* Download consolidated governance PDF */}
                  <button
                    onClick={handleDownloadPdf}
                    disabled={downloadingPdf}
                    style={{
                      background: downloadingPdf ? '#e3f2fd' : '#1565c0',
                      border: 'none',
                      color: downloadingPdf ? '#1565c0' : '#fff',
                      borderRadius: '6px',
                      padding: '6px 14px',
                      cursor: downloadingPdf ? 'not-allowed' : 'pointer',
                      fontSize: '12px',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                    }}
                    title="Download consolidated governance PDF with all active policies, conflicts, and resolved directives"
                  >
                    {downloadingPdf ? '⏳ Downloading…' : '📄 Download Policy PDF'}
                  </button>
                  {pdfProgress && (
                    <span style={{ fontSize: '11px', color: '#1565C0', marginLeft: '8px', fontStyle: 'italic' }}>
                      {pdfProgress}
                    </span>
                  )}

                  {/* Factory Reset — only meaningful when there are active policies */}
                  {policies && activeCount > 0 && (
                    !resetConfirm ? (
                      <button
                        onClick={handleFactoryReset}
                        disabled={resetting}
                        style={{ background: '#fff', border: '1px solid #ef9a9a', color: '#c62828', borderRadius: '6px', padding: '6px 14px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                        title="Deactivate all governance policies and return AI to product-type factory defaults"
                      >
                        🔄 Reset to Factory
                      </button>
                    ) : (
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                        <span style={{ fontSize: '12px', color: '#c62828', fontWeight: 600 }}>Confirm reset?</span>
                        <button onClick={handleFactoryReset} disabled={resetting}
                          style={{ background: '#c62828', color: '#fff', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}>
                          {resetting ? '⏳' : 'Yes, Reset'}
                        </button>
                        <button onClick={() => setResetConfirm(false)}
                          style={{ background: '#eee', color: '#333', border: 'none', borderRadius: '4px', padding: '4px 12px', cursor: 'pointer', fontSize: '12px' }}>
                          Cancel
                        </button>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Reset / status feedback */}
              {resetStatus?.success && <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#2e7d32', marginBottom: '12px' }}>✅ {resetStatus.message}</div>}
              {resetStatus?.partial && <div style={{ background: '#fff8e1', border: '1px solid #ffe082', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#e65100', marginBottom: '12px' }}>⚠️ {resetStatus.message}</div>}
              {resetStatus?.error && <div style={{ background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#c62828', marginBottom: '12px' }}>❌ {resetStatus.error}</div>}

              {/* Product-type context */}
              {productType && (
                <div style={{ background: '#f3e5f5', border: '1px solid #ce93d8', borderRadius: '6px', padding: '8px 12px', marginBottom: '14px', fontSize: '12px', color: '#6a1b9a' }}>
                  🏷️ Product type: <strong>{productType}</strong> — uploaded PDFs will be validated for relevance to this product domain.
                </div>
              )}

              {/* Global relevance warning */}
              {hasWarnings && (
                <div style={{ background: '#fff3e0', border: '1px solid #ffcc02', borderRadius: '6px', padding: '10px 12px', marginBottom: '12px', fontSize: '12px', color: '#e65100' }}>
                  <strong>⚠️ Relevance Warning</strong> — One or more selected files may not be relevant to your product type ({productType}).
                  Applying irrelevant policies can slow AI performance by adding unnecessary guardrails. You can still upload, but review the warnings below.
                </div>
              )}

              {/* Upload zone */}
              <div style={{ marginBottom: '16px' }}>
                <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '10px', color: '#333' }}>Upload Policy PDFs (select one or more categories):</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(240px, 1fr))', gap: '10px' }}>
                  {CATEGORIES.map(cat => {
                    const activePol = policies?.[cat.id];
                    const cardTip = activePol
                      ? [
                          `${cat.label} — ACTIVE (v${activePol.version || '?'})`,
                          activePol.uploaded_by ? `Uploaded by: ${activePol.uploaded_by}` : null,
                          activePol.uploaded_at ? `Date: ${new Date(activePol.uploaded_at).toLocaleString()}` : null,
                          activePol.effective_date ? `Effective: ${new Date(activePol.effective_date).toLocaleDateString()}` : null,
                          activePol.source === 'parent' ? 'Source: Inherited from parent' : 'Source: Own upload',
                          '\nUpload a new PDF to replace this version.',
                        ].filter(Boolean).join('\n')
                      : `${cat.desc}\n\nNo policy uploaded yet — click to select a PDF.`;
                    return (
                      <div key={cat.id}>
                        <label
                          title={cardTip}
                          style={{
                            display: 'block',
                            border: `2px ${activePol ? 'solid #a5d6a7' : `dashed ${fileWarnings[cat.id] ? '#ff9800' : files[cat.id] ? '#1565c0' : '#ccc'}`}`,
                            borderRadius: '8px', padding: '12px', cursor: 'pointer',
                            background: activePol ? '#f1f8e9' : fileWarnings[cat.id] ? '#fff8e1' : files[cat.id] ? '#e3f2fd' : '#fafafa',
                            position: 'relative',
                          }}>
                          {/* Active indicator badge on card */}
                          {activePol && (
                            <span style={{ position: 'absolute', top: '6px', right: '8px', background: '#2e7d32', color: '#fff', borderRadius: '8px', padding: '1px 7px', fontSize: '10px', fontWeight: 700 }}>
                              ✅ v{activePol.version || '?'}
                            </span>
                          )}
                          <input type="file" accept=".pdf" style={{ display: 'none' }}
                            onChange={e => handleFileChange(cat.id, e.target.files[0] || null)} />
                          <div style={{ fontSize: '20px', marginBottom: '4px' }}>{cat.icon}</div>
                          <div style={{ fontWeight: 600, fontSize: '13px' }}>{cat.label}</div>
                          <div style={{ fontSize: '11px', color: '#888', marginTop: '2px' }}>{cat.desc}</div>
                          {files[cat.id] ? (
                            <div style={{ fontSize: '11px', color: fileWarnings[cat.id] ? '#e65100' : '#1565c0', marginTop: '6px', fontWeight: 600 }}>
                              📎 {files[cat.id].name}
                            </div>
                          ) : activePol ? (
                            <div style={{ fontSize: '11px', color: '#2e7d32', marginTop: '6px' }}>
                              Active — click to replace
                            </div>
                          ) : (
                            <div style={{ fontSize: '11px', color: '#aaa', marginTop: '6px' }}>Click to select PDF</div>
                          )}
                        </label>
                        {fileWarnings[cat.id] && (
                          <div style={{ fontSize: '11px', color: '#e65100', marginTop: '4px', padding: '4px 8px', background: '#fff3e0', borderRadius: '4px', lineHeight: 1.4 }}>
                            ⚠️ {fileWarnings[cat.id]}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>

              <button
                onClick={handleUpload}
                disabled={uploading || Object.values(files).every(f => f === null)}
                style={{
                  background: uploading ? '#90caf9' : '#1565c0', color: '#fff', border: 'none',
                  borderRadius: '6px', padding: '10px 24px', cursor: uploading ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: '13px',
                }}
              >
                {uploading ? '⏳ Uploading...' : `📤 Upload ${Object.values(files).filter(Boolean).length || ''} Policy File${Object.values(files).filter(Boolean).length !== 1 ? 's' : ''}`}
              </button>

              {/* Persistent policy counter — always visible below upload button */}
              <div
                title={buildHeaderTooltip()}
                style={{ marginTop: '10px', padding: '8px 12px', background: activeCount > 0 ? '#e8f5e9' : '#f5f5f5', borderRadius: '6px', border: `1px solid ${activeCount > 0 ? '#a5d6a7' : '#e0e0e0'}`, fontSize: '12px', color: activeCount > 0 ? '#2e7d32' : '#888', display: 'flex', alignItems: 'center', gap: '8px', cursor: 'help' }}
              >
                <span style={{ fontWeight: 700 }}>{activeCount > 0 ? `✅ ${activeCount}/4 policies active` : '0/4 policies active'}</span>
                {activeCount > 0 && (
                  <span style={{ color: '#555' }}>— {CATEGORIES.filter(c => policies?.[c.id]).map(c => c.label).join(', ')}</span>
                )}
                {activeCount === 0 && (
                  <span>— AI using product-type defaults only</span>
                )}
                <span style={{ marginLeft: 'auto', fontSize: '11px', color: '#aaa' }}>hover for details</span>
              </div>

              {uploadStatus.success && (
                <div style={{ marginTop: '10px' }}>
                  <div style={{ background: '#e8f5e9', border: '1px solid #a5d6a7', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#2e7d32', marginBottom: '12px' }}>
                    ✅ {uploadStatus.file_count} file{uploadStatus.file_count !== 1 ? 's' : ''} submitted. Batch ID: <code style={{ fontSize: '11px' }}>{uploadStatus.batch_id}</code>
                  </div>
                  <BatchStatusPanel clientId={clientId} batchId={uploadStatus.batch_id} />
                </div>
              )}
              {uploadStatus.expired && (
                <div style={{ marginTop: '10px', background: '#fff3e0', border: '1px solid #ffcc02', borderRadius: '6px', padding: '12px', fontSize: '13px', color: '#e65100' }}>
                  🔄 <strong>Session expired.</strong> Your login session has timed out (Cognito tokens expire after 1 hour).
                  <br />
                  <button
                    onClick={() => { localStorage.setItem('pnp_post_login_path', window.location.pathname); window.location.href = '/login'; }}
                    style={{ marginTop: '8px', background: '#e65100', color: '#fff', border: 'none', borderRadius: '4px', padding: '6px 16px', cursor: 'pointer', fontSize: '12px', fontWeight: 600 }}
                  >
                    Re-login and return here
                  </button>
                </div>
              )}
              {uploadStatus.error && (
                <div style={{ marginTop: '10px', background: '#ffebee', border: '1px solid #ef9a9a', borderRadius: '6px', padding: '10px', fontSize: '13px', color: '#c62828' }}>
                  ❌ {uploadStatus.error}
                </div>
              )}
              <p style={{ fontSize: '11px', color: '#aaa', marginTop: '12px', margin: '12px 0 0' }}>
                Policies are processed asynchronously and applied above your product-type AI configuration. Max 25 MB per file. PDF only.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ClientApplication;
